import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import {
  Banner,
  Button,
  Card,
  Input,
  Layout,
  Modal,
  Nav,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  TextArea,
  Tooltip,
  Toast,
  Typography
} from '@douyinfe/semi-ui';
import {
  IconActivity,
  IconBookOpenStroked,
  IconCloud,
  IconDelete,
  IconEdit,
  IconEyeOpened,
  IconHistogram,
  IconKey,
  IconLayers,
  IconLock,
  IconPlus,
  IconServer,
  IconSetting
} from '@douyinfe/semi-icons';
import { api } from './api';
import type {
  ActiveGatewayRequest,
  AppBossBillingMode,
  BaseURL,
  BaseURLMonitoringItem,
  ComputeServer,
  EntranceProtocol,
  EntranceTestAction,
  EntranceTestResult,
  OffPeakPolicy,
  RealtimeCall,
  RealtimeCapabilities,
  RealtimeReadiness,
  StressTestEndpoint,
  StressTestRun,
  StressTestTargetType,
  Upstream,
  UpstreamCapabilities,
  UpstreamCluster,
  UpstreamClusterMember,
  UsageDetailBody,
  UsageEvent,
  UsageSummary,
  Workload,
  WorkloadImage,
  WorkloadRun
} from './types';
import { primaryPublicURL, publicURLsForBase } from './publicUrls';
import { SystemOverviewSection } from './SystemOverview';
import { mergeTranslationCapabilities } from './translationCapabilities';
import { EntranceHTTPDocs } from './EntranceHTTPDocs';
import { AppBossIntegrations } from './AppBossIntegrations';
import { AppBossDiagnostics } from './AppBossDiagnostics';
import { AppBossEntranceSettings, AppBossTokenDocsModal } from './AppBossEntranceSettings';
import { authenticationLabel, integrationError, tokenEntranceDraft, validateTokenEntrance } from './appBossIntegration';
import { AppBossEntranceLifecycle } from './AppBossEntranceLifecycle';
import { AppBossEnrollment } from './AppBossEnrollment';
import './entranceList.css';
import {
  VALUE_COMPLIANCE_DEFAULT,
  validateValueComplianceConfiguration,
  valueComplianceEnabledForProtocol,
  valueComplianceModelForProtocol,
  valueComplianceStatusText,
  valueComplianceUpstreamIdForProtocol,
  valueComplianceUpstreamStatus
} from './entranceValueCompliance';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const COPYRIGHT_TEXT = '版权所有 © 2026 PICOROCK';
const DOCSFARM_DOCS_ROUTE = '/docsfarm-docs';
const DOCSFARM_DOCS_STORAGE_PREFIX = 'aigate.docsfarmDocs.';
type DocsfarmDocsTabKey = 'docs' | 'models' | 'redoc' | 'openapi';
const DOCSFARM_DOCS_TABS: Array<{ key: DocsfarmDocsTabKey; label: string; path: string; includePublicHost: boolean }> = [
  { key: 'docs', label: '文档', path: '/docs', includePublicHost: true },
  { key: 'models', label: '模型及参数', path: '/docs/models', includePublicHost: true },
  { key: 'redoc', label: 'redoc', path: '/redoc', includePublicHost: false },
  { key: 'openapi', label: 'openapi.json', path: '/openapi.json', includePublicHost: false }
];

export function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

function Shell() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const onLoginPage = location.pathname === '/login';
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, enabled: !onLoginPage });
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.clear();
      navigate('/login');
    }
  });

  if (onLoginPage) return <Login />;
  if (me.isLoading) return <div className="centered">正在加载智能算力网关...</div>;
  if (me.isError) return <Navigate to="/login" replace />;
  if (location.pathname === DOCSFARM_DOCS_ROUTE) return <DocsfarmDocsWindow />;

  return (
    <Layout className={`app-shell${['/appboss-integrations', '/appboss-diagnostics', '/base-urls'].includes(location.pathname) ? ' integration-shell' : ''}`}>
      <Sider className="sidebar">
        <div className="brand">智能算力网关</div>
        <Nav
          selectedKeys={[location.pathname]}
          onSelect={(item) => navigate(String(item.itemKey))}
          items={[
            { itemKey: '/', text: '总览', icon: <IconActivity /> },
            { itemKey: '/base-urls', text: '入口管理', icon: <IconCloud /> },
            { itemKey: '/appboss-integrations', text: 'AppBoss 连接', icon: <IconKey /> },
            { itemKey: '/appboss-diagnostics', text: 'AppBoss 访问诊断', icon: <IconHistogram /> },
            { itemKey: '/upstreams', text: '上游服务', icon: <IconServer /> },
            { itemKey: '/stress-tests', text: '压力测试', icon: <IconHistogram /> },
            { itemKey: '/compute', text: '算力服务器监控', icon: <IconActivity /> },
            { itemKey: '/off-peak', text: '错峰编排', icon: <IconCloud /> },
            { itemKey: '/workload-images', text: '算力镜像', icon: <IconLayers /> },
            { itemKey: '/workloads', text: '算力容器', icon: <IconServer /> },
            { itemKey: '/workload-logs', text: '容器日志', icon: <IconHistogram /> },
            { itemKey: '/usage', text: '用量统计', icon: <IconHistogram /> },
            { itemKey: '/audit', text: '审计日志', icon: <IconLayers /> },
            { itemKey: '/settings', text: '设置', icon: <IconSetting /> }
          ]}
          footer={{ collapseButton: false }}
        />
      </Sider>
      <Layout>
        <Header className="topbar">
          <span id="integration-mobile-nav-label" hidden>页面导航</span><Select className="integration-mobile-nav" aria-labelledby="integration-mobile-nav-label" value={location.pathname} onChange={(value) => navigate(String(value))} optionList={[{ value: '/base-urls', label: '入口管理' }, { value: '/appboss-integrations', label: 'AppBoss 连接' }, { value: '/appboss-diagnostics', label: 'AppBoss 访问诊断' }, { value: '/', label: '总览' }]} />
          <Text type="secondary">当前用户：{me.data?.username}</Text>
          <Button theme="borderless" icon={<IconLock />} onClick={() => logout.mutate()}>
            退出登录
          </Button>
        </Header>
        <Content className="content">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/base-urls" element={<BaseURLs />} />
            <Route path="/appboss-integrations" element={<AppBossIntegrations key={me.data?.username} />} />
            <Route path="/appboss-diagnostics" element={<AppBossDiagnostics key={me.data?.username} />} />
            <Route path="/upstreams" element={<Upstreams />} />
            <Route path="/stress-tests" element={<StressTesting />} />
            <Route path="/compute" element={<ComputeMonitoring />} />
            <Route path="/off-peak" element={<OffPeakPolicies />} />
            <Route path="/workload-images" element={<WorkloadImages />} />
            <Route path="/workloads" element={<Workloads />} />
            <Route path="/workload-logs" element={<WorkloadLogs />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
          <CopyrightNotice />
        </Content>
      </Layout>
    </Layout>
  );
}

function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: () => api.login(username, password),
    onSuccess: (data) => {
      queryClient.setQueryData(['me'], { username: data.username });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  return (
    <div className="login-page">
      <Card className="login-card">
        <Title heading={3}>智能算力网关</Title>
        <Text type="secondary">AI算力管理后台</Text>
        <div className="form-grid">
          <Input value={username} onChange={setUsername} prefix="账号" />
          <Input mode="password" value={password} onChange={setPassword} prefix="密码" />
          <Button theme="solid" type="primary" loading={login.isPending} onClick={() => login.mutate()}>
            登录
          </Button>
        </div>
      </Card>
      <CopyrightNotice className="login-copyright" />
    </div>
  );
}

function CopyrightNotice({ className = '' }: { className?: string }) {
  return <div className={`copyright ${className}`.trim()}>{COPYRIGHT_TEXT}</div>;
}

type DocsfarmDocsPayload = {
  baseName: string;
  slug: string;
  upstreamName: string;
  publicHost: string;
  iframeUrl: string;
  docsUrls?: Partial<Record<DocsfarmDocsTabKey, string>>;
  createdAt: number;
};

function DocsfarmDocsWindow() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';
  const payload = readDocsfarmDocsPayload(token);
  const [activeTab, setActiveTab] = useState<DocsfarmDocsTabKey>('docs');
  if (!payload) {
    return (
      <div className="docsfarm-docs-window empty">
        <Card className="docsfarm-docs-empty-card">
          <Title heading={4}>文档窗口已失效</Title>
          <Text type="secondary">请回到入口管理页面，重新点击“接口文档”。</Text>
        </Card>
      </div>
    );
  }
  const activeIframeUrl = payload.docsUrls?.[activeTab] || payload.iframeUrl;
  return (
    <div className="docsfarm-docs-window">
      <div className="docsfarm-docs-head">
        <div className="docsfarm-docs-topbar">
          <div>
            <Title heading={5}>接口文档：{payload.baseName}</Title>
            <Text type="secondary">文档农场 · 公开访问主机 {payload.publicHost}</Text>
          </div>
          <Space>
            <Button onClick={() => window.location.reload()}>刷新</Button>
            <Button type="tertiary" onClick={() => window.close()}>关闭窗口</Button>
          </Space>
        </div>
        <div className="docsfarm-docs-nav" role="tablist" aria-label="文档农场文档入口">
          {DOCSFARM_DOCS_TABS.map((item) => (
            <Button
              aria-label={`切换到${item.label}`}
              aria-selected={activeTab === item.key}
              key={item.key}
              role="tab"
              size="small"
              theme={activeTab === item.key ? 'solid' : 'borderless'}
              type={activeTab === item.key ? 'primary' : 'tertiary'}
              onClick={() => setActiveTab(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>
      <iframe className="docsfarm-docs-frame" title={`${payload.baseName} 文档农场接口文档`} src={activeIframeUrl} />
    </div>
  );
}

function Overview() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: api.overview, refetchInterval: 10000 });
  const realtimeCalls = useQuery({ queryKey: ['realtimeCalls'], queryFn: api.realtimeCalls, refetchInterval: 5000 });
  const realtimeReadiness = useQuery({ queryKey: ['realtimeReadiness'], queryFn: api.realtimeReadiness, refetchInterval: 30000 });
  const summary: UsageSummary = overview.data?.summary || { requests: 0, errors: 0, totalTokens: 0 };
  const entranceItems = overview.data?.baseUrlMonitoring?.items || [];
  const activeRequests = overview.data?.activeGatewayRequests || [];
  const inFlightTotal = entranceItems.reduce((sum, item) => sum + (item.inFlightRequests || 0), 0);
  const activeWebSockets = activeRequests.filter((request) => isGatewayWebSocketProtocol(request.protocol)).length;
  const [selectedEntranceId, setSelectedEntranceId] = useState('');
  const selectedEntrance = entranceItems.find((item) => item.baseUrlId === selectedEntranceId) || entranceItems[0];
  const selectedActiveRequests = activeRequests.filter((request) => request.baseUrlId === selectedEntrance?.baseUrlId);
  useEffect(() => {
    if (!selectedEntranceId && entranceItems[0]) {
      setSelectedEntranceId(entranceItems[0].baseUrlId);
    }
  }, [entranceItems, selectedEntranceId]);
  const trafficChart = useMemo(() => buildOverviewTrafficChart(entranceItems), [entranceItems]);
  const tokenChart = useMemo(() => buildOverviewTokenChart(entranceItems), [entranceItems]);
  const selectedChart = useMemo(() => buildEntranceDetailChart(selectedEntrance), [selectedEntrance]);
  return (
    <section>
      <PageTitle title="总览" subtitle="查看主机系统健康状态，以及网关请求量和 Token 消耗。" />
      <SystemOverviewSection />
      <div className="overview-section-title gateway-overview-heading">
        <div>
          <Title heading={5}>网关流量</Title>
          <Text type="secondary">请求、错误、Token、进行中请求及入口地址状态。</Text>
        </div>
      </div>
      <div className="metric-grid">
        <Metric label="请求数" value={summary.requests || 0} />
        <Metric label="进行中请求" value={inFlightTotal} />
        <Metric label="WebSocket 活跃" value={activeWebSockets} />
        <Metric label="Token 消耗" value={summary.totalTokens || 0} />
        <Metric label="错误数" value={summary.errors || 0} />
        <Metric label="计量队列" value={overview.data?.metering?.usageQueueDepth || 0} />
      </div>
      <div className="overview-chart-grid">
        <Card className="panel">
          <div className="chart-title">请求与错误趋势</div>
          <ReactECharts option={trafficChart} style={{ height: 260 }} />
        </Card>
        <Card className="panel">
          <div className="chart-title">Token 消耗趋势</div>
          <ReactECharts option={tokenChart} style={{ height: 260 }} />
        </Card>
      </div>
      <RealtimeOverviewPanel
        calls={realtimeCalls.data || []}
        readiness={realtimeReadiness.data}
        bases={overview.data?.baseUrls || []}
        upstreams={overview.data?.upstreams || []}
      />
      <div className="overview-section-title">
        <div>
          <Title heading={5}>入口地址监控</Title>
          <Text type="secondary">按最近 24 小时展示每个入口的请求、错误、Token、进行中请求和计量队列状态，趋势按单位分开呈现。</Text>
        </div>
      </div>
      {overview.isLoading && <Card className="panel">正在加载入口监控...</Card>}
      {!overview.isLoading && entranceItems.length === 0 && (
        <Card className="panel empty-state">
          <Title heading={5}>暂无入口地址</Title>
          <Text type="secondary">创建入口后，这里会展示请求数、Token 消耗、错误数和历史曲线。</Text>
        </Card>
      )}
      <div className="entrance-grid">
        {entranceItems.map((item) => (
          <EntranceMonitorCard
            key={item.baseUrlId}
            item={item}
            selected={selectedEntrance?.baseUrlId === item.baseUrlId}
            onSelect={() => setSelectedEntranceId(item.baseUrlId)}
          />
        ))}
      </div>
      {selectedEntrance && (
        <Card className="panel">
          <div className="chart-toolbar">
            <Space>
              <Text strong>入口历史曲线</Text>
              <Select value={selectedEntrance.baseUrlId} onChange={(value) => setSelectedEntranceId(String(value))} style={{ width: 240 }}>
                {entranceItems.map((item) => (
                  <Select.Option value={item.baseUrlId} key={item.baseUrlId}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
              <Tag color="blue">进行中 {formatCompactNumber(selectedEntrance.inFlightRequests || 0)}</Tag>
              <Tag color="cyan">WS {formatCompactNumber(selectedEntrance.websocketConnections || 0)}</Tag>
            </Space>
            <Text type="secondary">请求/错误趋势与 Token 消耗分开展示</Text>
          </div>
          <ReactECharts option={selectedChart} style={{ height: 420 }} />
        </Card>
      )}
      {selectedEntrance && (
        <ActiveGatewayRequestPanel requests={selectedActiveRequests} />
      )}
    </section>
  );
}

function RealtimeOverviewPanel({ calls, readiness, bases, upstreams }: { calls: RealtimeCall[]; readiness?: RealtimeReadiness; bases: BaseURL[]; upstreams: Upstream[] }) {
  const show = calls.length > 0 || Boolean(readiness?.realtimeEntrances || readiness?.realtimeUpstreams);
  if (!show) return null;
  return (
    <Card className="panel">
      <div className="chart-toolbar">
        <Text strong>OpenAI Realtime 状态</Text>
        <Space>
          <Tag color={readiness?.httpsPublic ? 'green' : 'orange'}>HTTPS/WSS {readiness?.httpsPublic ? '就绪' : '需配置'}</Tag>
          <Tag color={readiness?.sip?.ready ? 'green' : readiness?.sip?.enabled ? 'orange' : 'grey'}>SIP/TLS {readiness?.sip?.ready ? '就绪' : readiness?.sip?.enabled ? '需检查' : '未启用'}</Tag>
          <Tag color="blue">活动 Call {calls.length}</Tag>
        </Space>
      </div>
      {(readiness?.warnings || []).map((warning) => <Banner key={warning} type="warning" closeIcon={null} description={warning} />)}
      {readiness?.sip?.enabled && (
        <div className="realtime-readiness-detail">
          <Text type="secondary">SIP {readiness.sip.listenAddr || '-'} → {readiness.sip.publicDomain || '-'}:{readiness.sip.publicPort || 5061}。{readiness.sip.mediaGuidance}</Text>
          <Space wrap>
            <Tag color={readiness.sip.listenerAddressValid ? 'green' : 'red'}>Listener 配置</Tag>
            <Tag color={readiness.sip.dnsResolved ? 'green' : 'red'}>公网 DNS</Tag>
            <Tag color={readiness.sip.certificateValidForDomain ? 'green' : 'red'}>证书域名/有效期</Tag>
            {readiness.sip.certificateExpiresAt && <Text type="secondary">证书到期：{formatDateTime(readiness.sip.certificateExpiresAt)}</Text>}
          </Space>
        </div>
      )}
      {readiness && (
        <Space wrap className="realtime-readiness-summary">
          <Tag>Realtime 入口 {readiness.realtimeEntrances}</Tag>
          <Tag>兼容上游 {readiness.realtimeUpstreams}</Tag>
          {(readiness.sipBillingEntrances || 0) > 0 && <Tag color="purple">SIP APPBOSS 入口 {readiness.sipBillingEntrances}</Tag>}
          <Tag color={readiness.webhookReadyUpstreams === readiness.sipUpstreams ? 'green' : 'orange'}>Webhook 本地配置 {readiness.webhookReadyUpstreams}/{readiness.sipUpstreams}</Tag>
          <Tag color="green">24h 回调成功 {readiness.callbackDelivery24h?.delivered || 0}</Tag>
          <Tag color={(readiness.callbackDelivery24h?.failed || 0) > 0 ? 'red' : 'grey'}>24h 回调失败 {readiness.callbackDelivery24h?.failed || 0}</Tag>
          <Tag color={(readiness.callbackDelivery24h?.pending || 0) > 0 ? 'orange' : 'grey'}>待处理 {readiness.callbackDelivery24h?.pending || 0}</Tag>
        </Space>
      )}
      {calls.length === 0 ? <div className="muted-line">当前没有活动 WebRTC 或 SIP call。</div> : (
        <Table
          rowKey="id"
          dataSource={calls}
          pagination={false}
          columns={[
            { title: '安全 Call 标识', render: (_, row) => row.callIdSuffix || '-' },
            { title: '入口', render: (_, row) => bases.find((base) => base.id === row.baseUrlId)?.name || shortUUID(row.baseUrlId) },
            { title: '传输', render: (_, row) => <Tag color="green">{String(row.transport || '').toUpperCase()}</Tag> },
            { title: '固定上游', render: (_, row) => upstreams.find((upstream) => upstream.id === row.upstreamId)?.name || shortUUID(row.upstreamId) },
            { title: '状态', render: (_, row) => <Tag color={row.state === 'active' ? 'green' : 'orange'}>{row.state}</Tag> },
            { title: '开始时间', render: (_, row) => formatDateTime(row.startedAt || row.createdAt) },
            { title: '持续时间', render: (_, row) => formatDuration(Math.max(0, Date.now() - new Date(row.startedAt || row.createdAt).getTime())) },
            { title: 'Sideband', render: (_, row) => row.sidebandOwned ? <Tag color="cyan">已占用</Tag> : <Tag color="grey">空闲</Tag> },
            { title: 'APPBOSS', render: (_, row) => row.appbossReserved ? <Tag color="purple">已预授权</Tag> : '-' }
          ]}
        />
      )}
    </Card>
  );
}

function ActiveGatewayRequestPanel({ requests }: { requests: ActiveGatewayRequest[] }) {
  return (
    <Card className="panel">
      <div className="chart-toolbar">
        <Text strong>进行中请求明细</Text>
        <Text type="secondary">{requests.length} 个</Text>
      </div>
      {requests.length === 0 && <Text type="secondary">当前没有进行中的入口请求。</Text>}
      {requests.length > 0 && (
        <div className="active-request-list">
          {requests.map((request) => (
            <div className="active-request-row" key={request.requestId}>
              <div>
                <Text strong>{request.endpoint || '-'}</Text>
                <div className="muted-line">
                  {request.method || '-'} · {request.upstream || '尚未选定上游'} · {shortUUID(request.requestId)}
                </div>
                <div className="muted-line">
                  {request.forwardedFor || request.remoteAddr || '未知来源'} · {request.keyPrefix || request.principalType || '未识别密钥'} · {request.userAgent || '未知客户端'}
                </div>
              </div>
              <Space>
                {request.protocol && <Tag color={usageProtocolColor(request.protocol)}>{usageProtocolLabel(request.protocol)}</Tag>}
                <Tag color="blue">{formatDuration(request.durationMs || 0)}</Tag>
              </Space>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function EntranceMonitorCard({ item, selected, onSelect }: { item: BaseURLMonitoringItem; selected: boolean; onSelect: () => void }) {
  return (
    <div className="entrance-card-click" onClick={onSelect}>
      <Card className={`entrance-card ${selected ? 'selected' : ''}`}>
        <div className="entrance-card-head">
          <div>
            <Text strong>{item.name}</Text>
            <div className="muted-line">{primaryPublicURL(item) || `/b/${item.slug}/v1`}</div>
          </div>
          <Space>
            {entranceProtocolTag(item)}
            <Tag color={item.status === 'enabled' ? 'green' : 'grey'}>{item.status === 'enabled' ? '启用' : '停用'}</Tag>
            <Tag color={item.authMode === 'allow_anonymous' ? 'orange' : 'blue'}>{item.authMode === 'allow_anonymous' ? '免密' : '密钥'}</Tag>
          </Space>
        </div>
        <div className="entrance-metrics">
          <MiniMetric label="请求数" value={formatCompactNumber(item.requests)} />
          <MiniMetric label="进行中" value={formatCompactNumber(item.inFlightRequests || 0)} />
          <MiniMetric label="WS 连接" value={formatCompactNumber(item.websocketConnections || 0)} />
          <MiniMetric label="Token" value={formatCompactNumber(item.totalTokens)} />
          <MiniMetric label="错误数" value={formatCompactNumber(item.errors)} />
        </div>
        <ReactECharts option={buildEntranceMiniChart(item)} style={{ height: 158 }} />
      </Card>
    </div>
  );
}

function BaseURLs() {
  const queryClient = useQueryClient();
  const bases = useQuery({ queryKey: ['baseURLs'], queryFn: api.baseURLs });
  const upstreams = useQuery({ queryKey: ['upstreams'], queryFn: api.upstreams });
  const clusters = useQuery({ queryKey: ['upstreamClusters'], queryFn: api.upstreamClusters });
  const [draft, setDraft] = useState<Partial<BaseURL>>(defaultBaseURLDraft());
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<BaseURL | null>(null);
  const [detailBaseId, setDetailBaseId] = useState('');
  const [docsBaseId, setDocsBaseId] = useState('');
  const [keyBase, setKeyBase] = useState<BaseURL | null>(null);
  const [testBase, setTestBase] = useState<BaseURL | null>(null);
  const [lifecycleBaseId, setLifecycleBaseId] = useState('');
  const [enrollmentBaseId, setEnrollmentBaseId] = useState('');
  const [integrationDenied, setIntegrationDenied] = useState(false);
  const closeEditor = () => {
    setCreateOpen(false);
    setEditing(null);
    setDraft(defaultBaseURLDraft());
  };
  const openCreate = () => {
    setEditing(null);
    setDraft(defaultBaseURLDraft());
    setCreateOpen(true);
  };
  const openEdit = (base: BaseURL) => {
    setEditing(base);
    setDraft({
      ...base,
      protocol: baseProtocol(base),
      appbossBillingMode: appBossBillingModeOf(base),
      appbossServiceId: base.appbossServiceId || '',
      appbossBalanceCheckUrl: '',
      appbossDeductUrl: '',
      defaultUpstreamTargetType: baseTargetType(base),
      fallbackTargetType: fallbackTargetType(base),
      websocketTargetType: websocketTargetType(base),
      websocketRoutePrefix: base.websocketRoutePrefix || '/ws',
      realtimeTargetType: realtimeTargetType(base),
      realtimeWebhookSigningSecret: ''
    });
    setCreateOpen(true);
  };
  const create = useMutation({
    mutationFn: () => {
      if (integrationDenied) throw new Error('请重新登录后操作');
      validateBaseURLDraftForSave(draft, editing);
      const body = normalizeBaseURLDraft(draft, editing);
      return editing ? api.updateBaseURL(editing.id, body) : api.createBaseURL(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baseURLs'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      closeEditor();
      Toast.success(editing ? '入口已更新' : '入口已创建');
    },
    onError: (error: Error) => {
      if (draft.authMode === 'appboss_token') {
        if ([401, 403].includes((error as { status?: number }).status || 0)) { setIntegrationDenied(true); closeEditor(); }
        Toast.error(integrationError(error));
      } else Toast.error(error.message);
    }
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteBaseURL(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baseURLs'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      Toast.success('入口已删除');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const entranceProtocol = baseProtocol(draft);
  const isHTTPDraft = entranceProtocol === 'openai_http';
  const isWebSocketDraft = entranceProtocol === 'websocket';
  const isRealtimeDraft = entranceProtocol === 'openai_realtime';
  const targetType = draft.defaultUpstreamTargetType || 'upstream';
  const standbyType = draft.fallbackTargetType || 'upstream';
  const wsTargetType = draft.websocketTargetType || 'upstream';
  const realtimeTarget = realtimeTargetType(draft);
  const httpUpstreams = (upstreams.data || []).filter((item) => upstreamProtocol(item) === 'openai_http');
  const eligibleComplianceUpstreams = httpUpstreams.filter((item) => item.status === 'enabled');
  const websocketUpstreams = (upstreams.data || []).filter(isWebSocketUpstream);
  const realtimeUpstreams = (upstreams.data || []).filter((item) => item.status === 'enabled' && isRealtimeUpstream(item) && supportsRealtimeCapabilities(item.realtimeCapabilities, draft.realtimeCapabilities));
  const httpClusters = (clusters.data || []).filter((cluster) => clusterHasProtocol(cluster, upstreams.data || [], 'openai_http'));
  const websocketClusters = (clusters.data || []).filter((cluster) => clusterHasProtocol(cluster, upstreams.data || [], 'websocket'));
  const realtimeClusters = (clusters.data || []).filter((cluster) => clusterHasCompatibleRealtimeTarget(cluster, upstreams.data || [], draft.realtimeCapabilities));
  const previewUrls = isWebSocketDraft ? [] : clientBaseURLs(draft);
  const defaultAliasUrl = isHTTPDraft ? defaultClientBaseURL(draft) : '';
  const websocketPreviewUrls = isWebSocketDraft ? clientWebSocketURLs(draft) : [];
  const realtimeWebSocketPreviewUrls = isRealtimeDraft ? clientRealtimeWebSocketURLs(draft) : [];
  const detailBase = (bases.data || []).find((item) => item.id === detailBaseId);
  const docsBase = (bases.data || []).find((item) => item.id === docsBaseId);
  const openDocs = (base: BaseURL) => {
    if (base.authMode !== 'appboss_token' && baseProtocol(base) === 'openai_http') {
      const docsfarmUpstream = docsfarmDocsUpstreamForBase(base, upstreams.data || [], clusters.data || []);
      if (docsfarmUpstream) {
        openDocsfarmDocsWindow(base, docsfarmUpstream);
        return;
      }
    }
    setDocsBaseId(base.id);
  };
  return (
    <section>
      <PageTitle title="入口管理" subtitle="管理对客户端暴露的 OpenAI HTTP、WebSocket 或完整 Realtime 入口。" />
      <div className="toolbar">
        <Button theme="solid" type="primary" icon={<IconPlus />} disabled={integrationDenied} onClick={openCreate}>
          新建入口
        </Button>
      </div>
      <Modal
        title={editing ? '编辑入口' : '新建入口'}
        visible={createOpen}
        onCancel={() => { if (!create.isPending) closeEditor(); }}
        maskClosable={!create.isPending}
        closeOnEsc={!create.isPending}
        className="integration-entrance-modal"
        width="min(760px, calc(100vw - 24px))"
        footer={
          <Space>
            <Button disabled={create.isPending} onClick={closeEditor}>取消</Button>
            <Button theme="solid" type="primary" loading={create.isPending} disabled={integrationDenied || !!validateTokenEntrance(draft, editing)} onClick={() => create.mutate()}>
              {editing ? '保存' : '创建'}
            </Button>
          </Space>
        }
      >
        <div className="modal-form">
          <label>
            <Text strong>名称</Text>
            <Input placeholder="例如：默认入口" value={draft.name || ''} onChange={(name) => setDraft({ ...draft, name })} />
          </label>
          <label>
            <Text strong>标识 Slug</Text>
            <Input placeholder="例如：default" value={draft.slug || ''} onChange={(slug) => setDraft({ ...draft, slug })} />
          </label>
          <label>
            <Text strong>入口类型</Text>
            <Select value={entranceProtocol} onChange={(protocol) => setDraft(draftWithEntranceProtocol(draft, protocol as EntranceProtocol))} className="select">
              <Select.Option value="openai_http">OpenAI HTTP</Select.Option>
              <Select.Option value="websocket">WebSocket</Select.Option>
              <Select.Option value="openai_realtime">OpenAI Realtime（WebSocket / WebRTC / SIP）</Select.Option>
            </Select>
          </label>
          {isHTTPDraft && (
            <div className="client-url-preview">
              <div>
                <Text strong>客户端 BaseURL</Text>
                {previewUrls.length > 0 ? <PublicURLList urls={previewUrls} /> : <Text type="secondary">填写 Slug 后自动生成</Text>}
              </div>
              {defaultAliasUrl && (
                <div>
                  <Text type="secondary">默认入口兼容地址</Text>
                  <div className="copy-line">
                    <code>{defaultAliasUrl}</code>
                    <Button size="small" onClick={() => copyText(defaultAliasUrl)}>
                      复制
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {isWebSocketDraft && (
            <div className="client-url-preview">
              <div>
                <Text strong>客户端 WebSocket URL</Text>
                {websocketPreviewUrls.length > 0 ? <PublicURLList urls={websocketPreviewUrls} /> : <Text type="secondary">填写 Slug 后自动生成</Text>}
              </div>
            </div>
          )}
          {isRealtimeDraft && (
            <div className="client-url-preview">
              <div>
                <Text strong>Realtime API BaseURL</Text>
                {previewUrls.length > 0 ? <PublicURLList urls={previewUrls} /> : <Text type="secondary">填写 Slug 后自动生成</Text>}
              </div>
              <div>
                <Text strong>Realtime WebSocket URL</Text>
                {realtimeWebSocketPreviewUrls.length > 0 ? <PublicURLList urls={realtimeWebSocketPreviewUrls} /> : <Text type="secondary">填写 Slug 后自动生成</Text>}
              </div>
            </div>
          )}
          <label>
            <Text strong id="entrance-auth-label">认证方式</Text>
            <Select aria-labelledby="entrance-auth-label" value={draft.authMode} disabled={create.isPending || editing?.authMode === 'appboss_token'} onChange={(authMode) => {
              if (editing?.authMode === 'appboss_token') return;
              if (editing && authMode === 'appboss_token') return;
              if (draft.authMode === 'appboss_token' && authMode !== 'appboss_token' && !window.confirm('确认更换认证方式？尚未保存的 AppBoss 授权设置将被清除。')) return;
              setDraft(authMode === 'appboss_token' ? tokenEntranceDraft(draft) : { ...draft, authMode: authMode as BaseURL['authMode'], appbossIntegrationId: '', appbossModels: [], appbossOperations: [] });
            }} className="select">
              <Select.Option value="require_key">要求 sk key</Select.Option>
              <Select.Option value="allow_anonymous">允许免密访问</Select.Option>
              {editing?.authMode === 'appboss_token' && <Select.Option value="appboss_token">{editing.appbossDualAuth ? 'API Key 或 AppBoss Token' : 'AppBoss 令牌'}</Select.Option>}
            </Select>
          </label>
          {draft.authMode === 'appboss_token' && createOpen && <AppBossEntranceSettings draft={draft} existing={editing} integrationLocked={editing?.authMode === 'appboss_token'} disabled={create.isPending || integrationDenied} onChange={setDraft} onDenied={() => { setIntegrationDenied(true); closeEditor(); }} />}
          {isHTTPDraft && (
            <>
              <label>
                <Text strong>上游类型</Text>
                <Select
                  value={targetType}
                  onChange={(value) =>
                    setDraft({
                      ...draft,
                      defaultUpstreamTargetType: value as BaseURL['defaultUpstreamTargetType'],
                      defaultUpstreamId: undefined,
                      defaultUpstreamClusterId: undefined
                    })
                  }
                  className="select"
                >
                  <Select.Option value="upstream">上游服务</Select.Option>
                  <Select.Option value="upstream_cluster">上游集群</Select.Option>
                </Select>
              </label>
              {targetType === 'upstream' ? (
                <label>
                  <Text strong>默认上游</Text>
                  <Select value={draft.defaultUpstreamId} onChange={(defaultUpstreamId) => setDraft({ ...draft, defaultUpstreamId: String(defaultUpstreamId) })} className="select" placeholder="选择上游服务">
                    {httpUpstreams.map((u) => (
                      <Select.Option value={u.id} key={u.id}>
                        {u.name}
                      </Select.Option>
                    ))}
                  </Select>
                </label>
              ) : (
                <label>
                  <Text strong>默认集群</Text>
                  <Select value={draft.defaultUpstreamClusterId} onChange={(defaultUpstreamClusterId) => setDraft({ ...draft, defaultUpstreamClusterId: String(defaultUpstreamClusterId) })} className="select" placeholder="选择上游集群">
                    {httpClusters.map((cluster) => (
                      <Select.Option value={cluster.id} key={cluster.id}>
                        {cluster.name}
                      </Select.Option>
                    ))}
                  </Select>
                </label>
              )}
              <div className="two-column-form">
                <label>
                  <Text strong>备用上游策略</Text>
                  <Select value={draft.fallbackBehavior || 'temporary_unavailable'} onChange={(fallbackBehavior) => setDraft({ ...draft, fallbackBehavior: fallbackBehavior as BaseURL['fallbackBehavior'] })} className="select">
                    <Select.Option value="temporary_unavailable">无备用，临时不可用</Select.Option>
                    <Select.Option value="use_fallback">使用备用上游</Select.Option>
                  </Select>
                </label>
                <label>
                  <Text strong>备用上游类型</Text>
                  <Select
                    value={standbyType}
                    disabled={draft.fallbackBehavior !== 'use_fallback'}
                    onChange={(value) =>
                      setDraft({
                        ...draft,
                        fallbackTargetType: value as BaseURL['fallbackTargetType'],
                        fallbackUpstreamId: undefined,
                        fallbackUpstreamClusterId: undefined
                      })
                    }
                    className="select"
                  >
                    <Select.Option value="upstream">上游服务</Select.Option>
                    <Select.Option value="upstream_cluster">上游集群</Select.Option>
                  </Select>
                </label>
              </div>
              {draft.fallbackBehavior === 'use_fallback' &&
                (standbyType === 'upstream' ? (
                  <label>
                    <Text strong>备用上游</Text>
                    <Select value={draft.fallbackUpstreamId} onChange={(fallbackUpstreamId) => setDraft({ ...draft, fallbackUpstreamId: String(fallbackUpstreamId) })} className="select" placeholder="选择备用上游服务">
                      {httpUpstreams.map((u) => (
                        <Select.Option value={u.id} key={u.id}>
                          {u.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </label>
                ) : (
                  <label>
                    <Text strong>备用集群</Text>
                    <Select value={draft.fallbackUpstreamClusterId} onChange={(fallbackUpstreamClusterId) => setDraft({ ...draft, fallbackUpstreamClusterId: String(fallbackUpstreamClusterId) })} className="select" placeholder="选择备用上游集群">
                      {httpClusters.map((cluster) => (
                        <Select.Option value={cluster.id} key={cluster.id}>
                          {cluster.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </label>
                ))}
              {draft.fallbackBehavior !== 'use_fallback' && <Banner type="warning" closeIcon={null} description="没有备用上游时，主算力节点被错峰占用或恢复中，请求会立即返回临时容量不足。" />}
            </>
          )}
          {isWebSocketDraft && (
            <>
              <label>
                <Text strong>上游类型</Text>
                <Select
                  value={wsTargetType}
                  onChange={(value) =>
                    setDraft({
                      ...draft,
                      websocketTargetType: value as BaseURL['websocketTargetType'],
                      websocketUpstreamId: undefined,
                      websocketUpstreamClusterId: undefined
                    })
                  }
                  className="select"
                >
                  <Select.Option value="upstream">上游服务</Select.Option>
                  <Select.Option value="upstream_cluster">上游集群</Select.Option>
                </Select>
              </label>
              {wsTargetType === 'upstream' ? (
                <label>
                  <Text strong>上游</Text>
                  <Select value={draft.websocketUpstreamId} onChange={(websocketUpstreamId) => setDraft({ ...draft, websocketUpstreamId: String(websocketUpstreamId) })} className="select" placeholder="选择 WebSocket 上游服务">
                    {websocketUpstreams.map((u) => (
                      <Select.Option value={u.id} key={u.id}>
                        {u.name}
                      </Select.Option>
                    ))}
                  </Select>
                </label>
              ) : (
                <label>
                  <Text strong>上游集群</Text>
                  <Select value={draft.websocketUpstreamClusterId} onChange={(websocketUpstreamClusterId) => setDraft({ ...draft, websocketUpstreamClusterId: String(websocketUpstreamClusterId) })} className="select" placeholder="选择 WebSocket 上游集群">
                    {websocketClusters.map((cluster) => (
                      <Select.Option value={cluster.id} key={cluster.id}>
                        {cluster.name}
                      </Select.Option>
                    ))}
                  </Select>
                </label>
              )}
            </>
          )}
          {isRealtimeDraft && (
            <>
              <div>
                <Text strong>对外开放能力</Text>
                <RealtimeCapabilityPicker
                  value={draft.realtimeCapabilities}
                  onChange={(realtimeCapabilities) => setDraft({ ...draft, realtimeCapabilities })}
                />
              </div>
              <label>
                <Text strong>Realtime 上游类型</Text>
                <Select
                  value={realtimeTarget}
                  onChange={(value) => setDraft({
                    ...draft,
                    realtimeTargetType: value as BaseURL['realtimeTargetType'],
                    realtimeUpstreamId: undefined,
                    realtimeUpstreamClusterId: undefined
                  })}
                  className="select"
                >
                  <Select.Option value="upstream">上游服务</Select.Option>
                  <Select.Option value="upstream_cluster">上游集群</Select.Option>
                </Select>
              </label>
              {realtimeTarget === 'upstream' ? (
                <label>
                  <Text strong>Realtime 上游</Text>
                  <Select value={draft.realtimeUpstreamId} onChange={(realtimeUpstreamId) => setDraft({ ...draft, realtimeUpstreamId: String(realtimeUpstreamId) })} className="select" placeholder="选择 Realtime 上游服务">
                    {realtimeUpstreams.map((upstream) => <Select.Option value={upstream.id} key={upstream.id}>{upstream.name}</Select.Option>)}
                  </Select>
                </label>
              ) : (
                <label>
                  <Text strong>Realtime 上游集群</Text>
                  <Select value={draft.realtimeUpstreamClusterId} onChange={(realtimeUpstreamClusterId) => setDraft({ ...draft, realtimeUpstreamClusterId: String(realtimeUpstreamClusterId) })} className="select" placeholder="选择 Realtime 上游集群">
                    {realtimeClusters.map((cluster) => <Select.Option value={cluster.id} key={cluster.id}>{cluster.name}</Select.Option>)}
                  </Select>
                </label>
              )}
              {draft.realtimeCapabilities?.sip && (
                <>
                  <label>
                    <Text strong>SIP 入口标识</Text>
                    <Input placeholder="例如：customer-support" value={draft.realtimeSipSlug || ''} onChange={(realtimeSipSlug) => setDraft({ ...draft, realtimeSipSlug })} />
                  </label>
                  <label>
                    <Text strong>来电 Webhook 回调 URL</Text>
                    <Input placeholder="业务系统的 HTTPS Realtime Webhook 地址" value={draft.realtimeWebhookCallbackUrl || ''} onChange={(realtimeWebhookCallbackUrl) => setDraft({ ...draft, realtimeWebhookCallbackUrl })} />
                  </label>
                  <label>
                    <Text strong>回调签名密钥</Text>
                    <Input mode="password" placeholder={editing?.realtimeWebhookSecretSet ? '留空表示不修改；也可在详情中轮换' : '用于 AIgate 签名转发的来电事件'} value={draft.realtimeWebhookSigningSecret || ''} onChange={(realtimeWebhookSigningSecret) => setDraft({ ...draft, realtimeWebhookSigningSecret })} />
                  </label>
                </>
              )}
            </>
          )}
          {draft.authMode === 'allow_anonymous' && (
            <Banner type="warning" closeIcon={null} description="当前入口将允许未携带 sk key 的请求通过，并按匿名访问统计用量。" />
          )}
          {isHTTPDraft && (
            <div className="form-section-divider value-compliance-setting">
              <div className="value-compliance-header">
                <div>
                  <Text strong>价值观合规</Text>
                  <div><Text type="secondary">向支持的普通 LLM 文本请求注入网关统一维护的安全系统提示。</Text></div>
                </div>
                <Switch
                  aria-label="价值观合规"
                  checked={Boolean(draft.valueComplianceEnabled)}
                  onChange={(checked) => setDraft({ ...draft, valueComplianceEnabled: Boolean(checked) })}
                />
              </div>
              {draft.valueComplianceEnabled && (
                <div className="value-compliance-dedicated">
                  <label>
                    <Text strong>合规专用上游</Text>
                    <Select
                      value={draft.valueComplianceUpstreamId}
                      onChange={(valueComplianceUpstreamId) => setDraft({ ...draft, valueComplianceUpstreamId: String(valueComplianceUpstreamId) })}
                      className="select"
                      placeholder="选择受保护请求唯一使用的上游服务"
                    >
                      {eligibleComplianceUpstreams.map((upstream) => (
                        <Select.Option value={upstream.id} key={upstream.id}>
                          {upstream.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </label>
                  <label>
                    <Text strong>合规专用模型</Text>
                    <Input
                      value={draft.valueComplianceModel || ''}
                      onChange={(valueComplianceModel) => setDraft({ ...draft, valueComplianceModel })}
                      placeholder="输入该专用上游中真实存在的模型，例如 qwen-plus"
                    />
                    <Text type="secondary">受保护请求会固定改写为此模型，不使用客户端传入的 model，也不执行入口模型路由。</Text>
                  </label>
                  <Banner
                    type="warning"
                    closeIcon={null}
                    description="普通 Chat Completions 与 Responses 会注入尽力型模型护栏（合规策略），将 model 固定改写为上方填写的合规专用模型，并只发送给该专用上游；模型路由、默认上游、集群及备用上游全部跳过。专用配置或上游不可用时直接返回 503，绝不回退。Uni Trans、DocsFarm 文件翻译、Embedding、图片音频和 WebSocket 仍走原路由。"
                  />
                  {eligibleComplianceUpstreams.length === 0 && <Banner type="danger" closeIcon={null} description="当前没有可选的已启用 OpenAI HTTP 上游服务，请先在上游服务中创建或启用一个。" />}
                </div>
              )}
            </div>
          )}
          {(draft.authMode !== 'appboss_token' || draft.appbossDualAuth) && <div className="form-section-divider">
            <Space>
              <Text strong>APPBOSS 点数计费</Text>
              <Switch
                checked={Boolean(draft.appbossBillingEnabled)}
                onChange={(checked) =>
                  setDraft({
                    ...draft,
                    appbossBillingEnabled: Boolean(checked),
                    appbossBillingMode: checked ? appBossBillingModeOf(draft) : 'gateway'
                  })
                }
              />
            </Space>
          </div>}
          {(draft.authMode !== 'appboss_token' || draft.appbossDualAuth) && draft.appbossBillingEnabled && (
            <>
              <div className="appboss-billing-mode">
                <Text strong>扣费方式</Text>
                <div className="appboss-billing-mode-options">
                  <label className="appboss-billing-mode-option">
                    <input
                      type="radio"
                      name="appboss-billing-mode"
                      checked={appBossBillingModeOf(draft) === 'gateway'}
                      onChange={() => setDraft({ ...draft, appbossBillingMode: 'gateway' })}
                    />
                    <span>由网关负责扣费</span>
                  </label>
                  <label className="appboss-billing-mode-option">
                    <input
                      type="radio"
                      name="appboss-billing-mode"
                      checked={appBossBillingModeOf(draft) === 'upstream'}
                      onChange={() => setDraft({ ...draft, appbossBillingMode: 'upstream' })}
                    />
                    <span>由上游服务负责扣费</span>
                  </label>
                </div>
              </div>
              {appBossBillingModeOf(draft) === 'upstream' && (
                <Banner
                  type="info"
                  closeIcon={null}
                  description="上游扣费模式下，AIgate 仍会先检查余额；余额不足会拦截请求。转发时会注入 X-APPBOSS-UID、X-APPBOSS-BALANCE-URL、X-APPBOSS-DEDUCT-URL，由上游按业务上报服务用量并扣费。"
                />
              )}
              {appBossBillingModeOf(draft) === 'gateway' && (
                <label>
                  <Text strong>AppBoss 服务类型</Text>
                  <Input
                    placeholder="例如 qwen35-live-trans"
                    value={draft.appbossServiceId || ''}
                    onChange={(appbossServiceId) => setDraft({ ...draft, appbossServiceId })}
                  />
                </label>
              )}
              {editing && (
                <div className="appboss-config-status">
                  <ConfiguredAppBossURL label="当前检查余额地址" configured={Boolean(editing.appbossBalanceCheckUrlSet)} value={editing.appbossBalanceCheckUrl} />
                  <ConfiguredAppBossURL label="当前扣费地址" configured={Boolean(editing.appbossDeductUrlSet)} value={editing.appbossDeductUrl} />
                </div>
              )}
              <div className="two-column-form">
                <label>
                  <Text strong>检查余额服务器地址</Text>
                  <Input
                    placeholder={editing?.appbossBalanceCheckUrlSet ? '输入新地址或 AppBoss 服务根地址以替换当前配置' : '输入检查余额地址或 AppBoss 服务根地址'}
                    value={draft.appbossBalanceCheckUrl || ''}
                    onChange={(appbossBalanceCheckUrl) => setDraft({ ...draft, appbossBalanceCheckUrl })}
                  />
                </label>
                <label>
                  <Text strong>扣费服务器地址</Text>
                  <Input
                    placeholder={editing?.appbossDeductUrlSet ? '输入新地址或 AppBoss 服务根地址以替换当前配置' : '输入扣费地址或 AppBoss 服务根地址'}
                    value={draft.appbossDeductUrl || ''}
                    onChange={(appbossDeductUrl) => setDraft({ ...draft, appbossDeductUrl })}
                  />
                </label>
              </div>
            </>
          )}
        </div>
      </Modal>
      <Table
        className="entrance-list-table"
        rowKey="id"
        dataSource={bases.data || []}
        loading={bases.isLoading}
        scroll={{ x: 1740 }}
        columns={[
          {
            title: '名称', dataIndex: 'name', width: 200,
            render: (_, row) => <div className="entrance-list-identity" data-entrance-id={row.id}>
              <Text strong>{row.name}</Text>
              {row.authMode === 'appboss_token'
                ? <Button className="entrance-list-primary" size="small" aria-label="AppBoss 设置" icon={<IconSetting />} disabled={integrationDenied || create.isPending} onClick={() => openEdit(row)}>AppBoss 设置</Button>
                : <Button className="entrance-list-primary" size="small" aria-label="接入 AppBoss" icon={<IconKey />} disabled={integrationDenied} onClick={() => setEnrollmentBaseId(row.id)}>接入 AppBoss</Button>}
            </div>
          },
          { title: 'Slug', dataIndex: 'slug', width: 120 },
          { title: '入口类型', width: 120, render: (_, row) => entranceProtocolTag(row) },
          { title: '入口地址', width: 300, render: (_, row) => <PublicURLSummary base={row} /> },
          {
            title: '上游', width: 160,
            render: (_, row) => renderEntranceTarget(row, upstreams.data || [], clusters.data || [])
          },
          {
            title: '备用上游', width: 150,
            render: (_, row) => (baseProtocol(row) === 'openai_http' ? renderFallbackTarget(row, upstreams.data || [], clusters.data || []) : <Text type="secondary">-</Text>)
          },
          { title: '认证', width: 120, render: (_, row) => <Tag color={row.authMode === 'allow_anonymous' ? 'orange' : 'blue'}>{row.appbossDualAuth ? 'API Key / AppBoss Token' : authenticationLabel(row.authMode)}</Tag> },
          { title: '价值观合规', width: 140, render: (_, row) => renderValueComplianceRoute(row, upstreams.data || []) },
          { title: 'APPBOSS', width: 170, render: (_, row) => (row.authMode === 'appboss_token' ? <Tag color="blue">按入口授权与资金策略</Tag> : row.appbossBillingEnabled ? appBossBillingModeTag(row) : <Tag color="grey">关闭</Tag>) },
          { title: '状态', width: 90, render: (_, row) => <Tag color={row.status === 'enabled' ? 'green' : 'grey'}>{row.status === 'enabled' ? '启用' : '停用'}</Tag> },
          {
            title: '操作', width: 170,
            dataIndex: 'id',
            render: (id, row) => {
              const actionBase = resolveTableRecord<BaseURL>(id, row, bases.data || []);
              if (!actionBase) return null;
              return (
                <ActionButtonGroup>
                  <ActionIconButton label="详情" icon={<IconEyeOpened />} onClick={() => setDetailBaseId(actionBase.id)} />
                  <ActionIconButton label="接口文档" icon={<IconBookOpenStroked />} onClick={() => openDocs(actionBase)} />
                  <ActionIconButton label="密钥" icon={<IconKey />} disabled={integrationDenied || (actionBase.authMode === 'appboss_token' && !actionBase.appbossDualAuth)} onClick={() => setKeyBase(actionBase)} />
                  <ActionIconButton label="编辑" icon={<IconEdit />} disabled={integrationDenied || create.isPending} onClick={() => openEdit(actionBase)} />
                  {actionBase.authMode === 'appboss_token' && <ActionIconButton label="启用与停用" icon={<IconSetting />} disabled={integrationDenied} onClick={() => setLifecycleBaseId(actionBase.id)} />}
                  <ActionIconButton label={actionBase.authMode === 'appboss_token' ? 'Token 付费测试未开放' : baseProtocol(actionBase) === 'openai_http' ? '测试' : '测试仅支持 OpenAI HTTP'} icon={<IconSetting />} disabled={integrationDenied || actionBase.authMode === 'appboss_token' || baseProtocol(actionBase) !== 'openai_http'} onClick={() => setTestBase(actionBase)} />
                  <ActionIconButton
                    label="删除"
                    type="danger"
                    icon={<IconDelete />}
                    loading={remove.isPending}
                    disabled={integrationDenied}
                    onClick={() => {
                      const message = actionBase.authMode === 'appboss_token'
                        ? `确认删除入口“${actionBase.name}”？删除后不再接受新请求，历史记录保留。`
                        : `确认删除入口“${actionBase.name}”？该入口下的密钥和模型路由也会删除。`;
                      if (window.confirm(message)) remove.mutate(actionBase.id);
                    }}
                  />
                </ActionButtonGroup>
              );
            }
          }
        ]}
      />
      <BaseURLDetailModal base={detailBase} upstreams={upstreams.data || []} clusters={clusters.data || []} visible={Boolean(detailBaseId)} onClose={() => setDetailBaseId('')} />
      <BaseURLDocsModal base={docsBase} visible={Boolean(docsBaseId)} onClose={() => setDocsBaseId('')} />
      {keyBase && <KeysModal base={keyBase} onClose={() => setKeyBase(null)} />}
      {testBase && <BaseURLTestModal base={testBase} onClose={() => setTestBase(null)} />}
      {enrollmentBaseId && <AppBossEnrollment key={enrollmentBaseId} sourceId={enrollmentBaseId} onClose={() => setEnrollmentBaseId('')} onChanged={() => { void queryClient.invalidateQueries({ queryKey: ['baseURLs'] }); void queryClient.invalidateQueries({ queryKey: ['overview'] }); }} onManage={(id) => { setEnrollmentBaseId(''); setLifecycleBaseId(id); }} onDenied={() => { setIntegrationDenied(true); closeEditor(); }} />}
      {lifecycleBaseId && <AppBossEntranceLifecycle key={lifecycleBaseId} entranceId={lifecycleBaseId} onClose={() => setLifecycleBaseId('')} onChanged={() => { void queryClient.invalidateQueries({ queryKey: ['baseURLs'] }); void queryClient.invalidateQueries({ queryKey: ['overview'] }); }} onDenied={() => { setIntegrationDenied(true); closeEditor(); }} />}
    </section>
  );
}

function baseTargetType(base: Partial<BaseURL>): BaseURL['defaultUpstreamTargetType'] {
  if (base.defaultUpstreamTargetType) return base.defaultUpstreamTargetType;
  return base.defaultUpstreamClusterId ? 'upstream_cluster' : 'upstream';
}

function fallbackTargetType(base: Partial<BaseURL>): BaseURL['fallbackTargetType'] {
  if (base.fallbackTargetType) return base.fallbackTargetType;
  return base.fallbackUpstreamClusterId ? 'upstream_cluster' : 'upstream';
}

function websocketTargetType(base: Partial<BaseURL>): BaseURL['websocketTargetType'] {
  if (base.websocketTargetType) return base.websocketTargetType;
  return base.websocketUpstreamClusterId ? 'upstream_cluster' : 'upstream';
}

function realtimeTargetType(base: Partial<BaseURL>): BaseURL['realtimeTargetType'] {
  if (base.realtimeTargetType) return base.realtimeTargetType;
  return base.realtimeUpstreamClusterId ? 'upstream_cluster' : 'upstream';
}

function baseProtocol(base: Partial<BaseURL>): EntranceProtocol {
  if (base.protocol === 'openai_realtime') return 'openai_realtime';
  return base.protocol === 'websocket' || base.websocketEnabled ? 'websocket' : 'openai_http';
}

function appBossBillingModeOf(item?: { appbossBillingMode?: AppBossBillingMode }): AppBossBillingMode {
  return item?.appbossBillingMode === 'upstream' ? 'upstream' : 'gateway';
}

function appBossBillingModeText(item?: { appbossBillingMode?: AppBossBillingMode }) {
  return appBossBillingModeOf(item) === 'upstream' ? '上游扣费' : '网关扣费';
}

function appBossBillingModeTag(item?: { appbossBillingMode?: AppBossBillingMode }) {
  const mode = appBossBillingModeOf(item);
  return <Tag color={mode === 'upstream' ? 'cyan' : 'purple'}>{appBossBillingModeText(item)}</Tag>;
}

function draftWithEntranceProtocol(draft: Partial<BaseURL>, protocol: EntranceProtocol): Partial<BaseURL> {
  if (protocol === 'websocket') {
    return {
      ...draft,
      protocol,
      websocketEnabled: true,
      websocketRoutePrefix: '/ws',
      websocketTargetType: draft.websocketTargetType || 'upstream',
      defaultUpstreamTargetType: 'upstream',
      defaultUpstreamId: undefined,
      defaultUpstreamClusterId: undefined,
      fallbackTargetType: 'upstream',
      fallbackBehavior: 'temporary_unavailable',
      fallbackUpstreamId: undefined,
      fallbackUpstreamClusterId: undefined,
      valueComplianceEnabled: false,
      valueComplianceUpstreamId: undefined,
      valueComplianceModel: undefined
    };
  }
  if (protocol === 'openai_realtime') {
    return {
      ...draft,
      protocol,
      websocketEnabled: false,
      websocketRoutePrefix: '',
      websocketTargetType: undefined,
      websocketUpstreamId: undefined,
      websocketUpstreamClusterId: undefined,
      defaultUpstreamTargetType: undefined,
      defaultUpstreamId: undefined,
      defaultUpstreamClusterId: undefined,
      fallbackTargetType: undefined,
      fallbackBehavior: 'temporary_unavailable',
      fallbackUpstreamId: undefined,
      fallbackUpstreamClusterId: undefined,
      realtimeTargetType: draft.realtimeTargetType || 'upstream',
      realtimeCapabilities: hasRealtimeCapabilities(draft.realtimeCapabilities)
        ? draft.realtimeCapabilities
        : defaultRealtimeCapabilities(),
      valueComplianceEnabled: false,
      valueComplianceUpstreamId: undefined,
      valueComplianceModel: undefined
    };
  }
  return {
    ...draft,
    protocol,
    websocketEnabled: false,
    websocketRoutePrefix: '',
    websocketTargetType: undefined,
    websocketUpstreamId: undefined,
    websocketUpstreamClusterId: undefined,
    realtimeTargetType: undefined,
    realtimeUpstreamId: undefined,
    realtimeUpstreamClusterId: undefined,
    realtimeCapabilities: {},
    realtimeSipSlug: '',
    realtimeWebhookCallbackUrl: '',
    realtimeWebhookSigningSecret: '',
    defaultUpstreamTargetType: draft.defaultUpstreamTargetType || 'upstream',
    fallbackTargetType: draft.fallbackTargetType || 'upstream',
    fallbackBehavior: draft.fallbackBehavior || 'temporary_unavailable'
  };
}

function defaultBaseURLDraft(): Partial<BaseURL> {
  return {
    protocol: 'openai_http',
    authMode: 'require_key',
    status: 'enabled',
    allowGeneric: true,
    defaultUpstreamTargetType: 'upstream',
    fallbackTargetType: 'upstream',
    fallbackBehavior: 'temporary_unavailable',
    websocketEnabled: false,
    websocketRoutePrefix: '/ws',
    websocketTargetType: 'upstream',
    valueComplianceEnabled: VALUE_COMPLIANCE_DEFAULT,
    appbossBillingMode: 'gateway'
  };
}

function defaultRealtimeCapabilities(): RealtimeCapabilities {
  return { websocket: true, webrtc: true, clientSecrets: true, calls: true, sideband: true, sip: false, translations: false, transcription: false };
}

function ActionButtonGroup({ children }: { children: ReactNode }) {
  return <div className="action-icon-row">{children}</div>;
}

function resolveTableRecord<T extends { id: string }>(idOrRecord: unknown, maybeRecord: unknown, records: T[]): T | undefined {
  if (maybeRecord && typeof maybeRecord === 'object' && 'id' in maybeRecord) return maybeRecord as T;
  if (idOrRecord && typeof idOrRecord === 'object' && 'id' in idOrRecord) return idOrRecord as T;
  const id = String(idOrRecord || '');
  return records.find((item) => item.id === id);
}

function resolveClusterMember(idOrRecord: unknown, maybeRecord: unknown, members: UpstreamClusterMember[]): UpstreamClusterMember | undefined {
  if (maybeRecord && typeof maybeRecord === 'object' && 'upstreamId' in maybeRecord) return maybeRecord as UpstreamClusterMember;
  if (idOrRecord && typeof idOrRecord === 'object' && 'upstreamId' in idOrRecord) return idOrRecord as UpstreamClusterMember;
  const upstreamId = String(idOrRecord || '');
  return members.find((item) => item.upstreamId === upstreamId);
}

function ActionIconButton({
  label,
  icon,
  onClick,
  type,
  loading = false,
  disabled = false
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  type?: 'primary' | 'secondary' | 'tertiary' | 'warning' | 'danger';
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <Tooltip content={label} position="top">
      <span className="icon-action-wrap">
        <Button
          aria-label={label}
          className="icon-action"
          size="small"
          theme="borderless"
          type={type}
          icon={icon}
          loading={loading}
          disabled={disabled}
          onClick={onClick}
        />
      </span>
    </Tooltip>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="detail-item">
      <Text type="secondary">{label}</Text>
      <div>{children}</div>
    </div>
  );
}

function PublicURLSummary({ base }: { base: Partial<BaseURL> }) {
  const urls = publicURLsForBase(base);
  if (urls.length === 0) return <Text type="secondary">-</Text>;
  return (
    <div className="public-url-summary">
      <code>{urls[0]}</code>
      {urls.length > 1 && <Tag color="blue">+{urls.length - 1}</Tag>}
    </div>
  );
}

function PublicURLList({ urls }: { urls: string[] }) {
  if (urls.length === 0) return <Text type="secondary">未配置</Text>;
  return (
    <div className="public-url-list">
      {urls.map((url, index) => (
        <div className="public-url-list-row" key={`${url}-${index}`}>
          {index === 0 && <Tag color="green">主地址</Tag>}
          {index > 0 && <Tag color="grey">备用</Tag>}
          <CopyableURL value={url} />
        </div>
      ))}
    </div>
  );
}

function ConfiguredAppBossURL({ label, configured, value }: { label: string; configured: boolean; value?: string }) {
  return (
    <div className="appboss-config-item">
      <Text type="secondary">{label}</Text>
      <div className="appboss-config-value">
        <Tag color={configured ? 'green' : 'grey'}>{configured ? '已配置' : '未配置'}</Tag>
        {configured && value && <code>{value}</code>}
      </div>
    </div>
  );
}

function BaseURLDetailModal({ base, upstreams, clusters, visible, onClose }: { base?: BaseURL; upstreams: Upstream[]; clusters: UpstreamCluster[]; visible: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const rotateWebhookSecret = useMutation({
    mutationFn: () => api.rotateRealtimeWebhookSecret(base!.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['baseURLs'] });
      window.prompt('新的回调签名密钥只显示这一次，请立即保存：', result.secret);
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  if (!base) {
    return (
      <Modal title="入口详情" visible={visible} onCancel={onClose} footer={null} width={760}>
        <Text type="secondary">没有找到这条入口配置。</Text>
      </Modal>
    );
  }
  const protocol = baseProtocol(base);
  const isWebSocketEntrance = protocol === 'websocket';
  const isRealtimeEntrance = protocol === 'openai_realtime';
  const isHTTPEntrance = protocol === 'openai_http';
  const clientUrl = isWebSocketEntrance ? '' : clientBaseURL(base);
  const publicUrls = publicURLsForBase(base);
  const defaultAliasUrl = isHTTPEntrance ? defaultClientBaseURL(base) : '';
  const wsUrl = isWebSocketEntrance ? clientWebSocketURL(base) : '';
  const realtimeWsUrl = isRealtimeEntrance ? (base.realtimeWebSocketUrl || clientRealtimeWebSocketURLs(base)[0] || '') : '';
  return (
    <Modal title={`入口详情：${base.name}`} visible={visible} onCancel={onClose} footer={null} width={760}>
      <div className="detail-grid">
        <DetailItem label="名称">{base.name}</DetailItem>
        <DetailItem label="Slug">{base.slug}</DetailItem>
        <DetailItem label="入口类型">{entranceProtocolTag(base)}</DetailItem>
        {isHTTPEntrance && (
          <DetailItem label="客户端 BaseURL">
            <CopyableURL value={clientUrl} />
          </DetailItem>
        )}
        {isWebSocketEntrance && (
          <DetailItem label="客户端 WebSocket URL">
            <CopyableURL value={wsUrl} />
          </DetailItem>
        )}
        {isRealtimeEntrance && (
          <>
            <DetailItem label="Realtime API BaseURL"><CopyableURL value={clientUrl} /></DetailItem>
            <DetailItem label="Realtime WebSocket URL"><CopyableURL value={realtimeWsUrl} /></DetailItem>
            <DetailItem label="Realtime 能力">{renderRealtimeCapabilities(base.realtimeCapabilities)}</DetailItem>
            <DetailItem label="SIP URI">{base.realtimeSipUri ? <CopyableURL value={base.realtimeSipUri} /> : <Text type="secondary">未启用 SIP</Text>}</DetailItem>
            <DetailItem label="来电事件回调">{base.realtimeWebhookCallbackUrl ? <CopyableURL value={base.realtimeWebhookCallbackUrl} /> : <Text type="secondary">未配置</Text>}</DetailItem>
            <DetailItem label="回调签名密钥">
              <Space>
                <Tag color={base.realtimeWebhookSecretSet ? 'green' : 'grey'}>{base.realtimeWebhookSecretSet ? '已设置' : '未设置'}</Tag>
                {base.realtimeWebhookCallbackUrl && <Button size="small" loading={rotateWebhookSecret.isPending} onClick={() => rotateWebhookSecret.mutate()}>轮换</Button>}
              </Space>
            </DetailItem>
          </>
        )}
        <DetailItem label="全部入口地址">
          <PublicURLList urls={publicUrls} />
        </DetailItem>
        {isHTTPEntrance && (
          <DetailItem label="默认入口兼容地址">
            {defaultAliasUrl ? <CopyableURL value={defaultAliasUrl} /> : <Text type="secondary">仅 default slug 提供</Text>}
          </DetailItem>
        )}
        <DetailItem label="上游">{renderEntranceTarget(base, upstreams, clusters)}</DetailItem>
        {isHTTPEntrance && <DetailItem label="备用上游">{renderFallbackTarget(base, upstreams, clusters)}</DetailItem>}
        {isHTTPEntrance && (
          <DetailItem label="价值观合规">
            {renderValueComplianceRoute(base, upstreams)}
          </DetailItem>
        )}
        <DetailItem label="APPBOSS 计费">
          <Tag color={base.appbossBillingEnabled ? 'purple' : 'grey'}>{base.authMode === 'appboss_token' ? '按入口授权与资金策略' : base.appbossBillingEnabled ? '启用' : '关闭'}</Tag>
        </DetailItem>
        {base.authMode !== 'appboss_token' && base.appbossBillingEnabled && (
          <>
            <DetailItem label="扣费方式">{appBossBillingModeTag(base)}</DetailItem>
            {appBossBillingModeOf(base) === 'gateway' && <DetailItem label="AppBoss 服务类型">{base.appbossServiceId || '未配置'}</DetailItem>}
            <DetailItem label="检查余额地址">{base.appbossBalanceCheckUrlSet ? base.appbossBalanceCheckUrl || '已配置' : '未配置'}</DetailItem>
            <DetailItem label="扣费地址">{base.appbossDeductUrlSet ? base.appbossDeductUrl || '已配置' : '未配置'}</DetailItem>
          </>
        )}
        <DetailItem label="认证">
          <Tag color={base.authMode === 'allow_anonymous' ? 'orange' : 'blue'}>{base.appbossDualAuth ? 'API Key / AppBoss Token' : authenticationLabel(base.authMode)}</Tag>
        </DetailItem>
        <DetailItem label="状态">
          <Tag color={base.status === 'enabled' ? 'green' : 'grey'}>{base.status === 'enabled' ? '启用' : '停用'}</Tag>
        </DetailItem>
      </div>
    </Modal>
  );
}

function BaseURLDocsModal({ base, visible, onClose }: { base?: BaseURL; visible: boolean; onClose: () => void }) {
  if (base?.authMode === 'appboss_token') return <AppBossTokenDocsModal base={base} visible={visible} onClose={onClose} />;
  if (!base) {
    return (
      <Modal title="接口文档" visible={visible} onCancel={onClose} footer={null} width={920}>
        <Text type="secondary">没有找到这条入口配置。</Text>
      </Modal>
    );
  }
  const protocol = baseProtocol(base);
  const isWebSocketEntrance = protocol === 'websocket';
  const isRealtimeEntrance = protocol === 'openai_realtime';
  const isHTTPEntrance = protocol === 'openai_http';
  const baseUrl = isWebSocketEntrance ? '' : clientBaseURL(base);
  const publicUrls = publicURLsForBase(base);
  const wsUrl = isWebSocketEntrance ? clientWebSocketURL(base) : '';
  const realtimeWsUrl = isRealtimeEntrance ? (base.realtimeWebSocketUrl || clientRealtimeWebSocketURLs(base)[0] || '') : '';
  const requiresKey = base.authMode !== 'allow_anonymous';
  const upstreamOwnedAppBossBilling = Boolean(base.appbossBillingEnabled) && appBossBillingModeOf(base) === 'upstream';
  const chatPath = baseUrl ? `${baseUrl}/chat/completions` : '';
  const modelsPath = baseUrl ? `${baseUrl}/models` : '';
  const authHeader = !isWebSocketEntrance && requiresKey ? '  -H "Authorization: Bearer <client-api-key>" \\\n' : '';
  const appBossHeader = !isWebSocketEntrance && base.appbossBillingEnabled ? '  -H "X-APPBOSS-UID: <appboss-uid>" \\\n' : '';
  const curlExample = isHTTPEntrance
    ? `curl -X POST "${chatPath}" \\
${authHeader}${appBossHeader}  -H "Content-Type: application/json" \\
  -d '{
    "model": "<model>",
    "messages": [
      {
        "role": "user",
        "content": "请用一句话介绍你自己。"
      }
    ],
    "stream": false
  }'`
    : '';
  const pythonExample = isHTTPEntrance
    ? `from openai import OpenAI

client = OpenAI(
    api_key="${requiresKey ? '<client-api-key>' : 'unused-by-this-entrance'}",
    base_url="${baseUrl}",
${base.appbossBillingEnabled ? '    default_headers={"X-APPBOSS-UID": "<appboss-uid>"},\n' : ''})

response = client.chat.completions.create(
    model="<model>",
    messages=[
        {"role": "user", "content": "请用一句话介绍你自己。"}
    ],
)

print(response.choices[0].message.content)`
    : '';
  const websocketNativeHeaders = [requiresKey ? 'Authorization: Bearer <client-api-key>' : '', base.appbossBillingEnabled ? 'X-APPBOSS-UID: <appboss-uid>' : ''].filter(Boolean).join('\n');
  const websocketNativeExample = isWebSocketEntrance
    ? `// Node.js、Android、iOS 等可设置 WebSocket header 的客户端
${websocketNativeHeaders || '无需客户端鉴权 header'}
const ws = new WebSocket("${wsUrl}", []);`
    : '';
  const websocketBrowserQuery = [requiresKey ? 'api_key=<client-api-key>' : '', base.appbossBillingEnabled ? 'appboss_uid=<appboss-uid>' : ''].filter(Boolean).join('&');
  const websocketBrowserURL = websocketBrowserQuery ? `${wsUrl}?${websocketBrowserQuery}` : wsUrl;
  const websocketBrowserExample = isWebSocketEntrance
    ? `const ws = new WebSocket("${websocketBrowserURL}");
ws.onmessage = (event) => console.log(event.data);`
    : '';

  return (
    <Modal title={`接口文档：${base.name}`} visible={visible} onCancel={onClose} footer={null} width={920}>
      <div className="api-doc-modal">
        <div className="api-doc-summary">
          <DetailItem label="入口名称">{base.name}</DetailItem>
          <DetailItem label="Slug">{base.slug}</DetailItem>
          <DetailItem label="入口类型">{entranceProtocolTag(base)}</DetailItem>
          {!isWebSocketEntrance && (
            <DetailItem label="客户端 BaseURL">
              <CopyableURL value={baseUrl} />
            </DetailItem>
          )}
          {isWebSocketEntrance && (
            <DetailItem label="客户端 WebSocket URL">
              <CopyableURL value={wsUrl} />
            </DetailItem>
          )}
          {isRealtimeEntrance && (
            <DetailItem label="Realtime WebSocket URL">
              <CopyableURL value={realtimeWsUrl} />
            </DetailItem>
          )}
          <DetailItem label="全部入口地址">
            <PublicURLList urls={publicUrls} />
          </DetailItem>
          <DetailItem label={isHTTPEntrance ? '业务接口鉴权' : '鉴权方式'}>
            <Tag color={requiresKey ? 'blue' : 'orange'}>{requiresKey ? `${isHTTPEntrance ? '业务调用' : ''}需要客户端 key` : '匿名访问，不需要客户端 key'}</Tag>
          </DetailItem>
          <DetailItem label={isWebSocketEntrance ? 'APPBOSS 计费' : '业务接口 APPBOSS 计费'}>
            {base.appbossBillingEnabled ? (
              <Space>
                <Tag color="purple">需要 X-APPBOSS-UID</Tag>
                {appBossBillingModeTag(base)}
              </Space>
            ) : (
              <Tag color="grey">关闭</Tag>
            )}
          </DetailItem>
        </div>

        <Banner
          type={requiresKey ? 'info' : 'warning'}
          closeIcon={null}
          description={
            isRealtimeEntrance && requiresKey
              ? '原生 Realtime WebSocket 使用 Authorization header；浏览器 WebSocket 使用受支持的子协议携带入口 key。WebRTC 推荐先签发短期 client_secret，长期 key 不应暴露给浏览器。'
              : isRealtimeEntrance
                ? '当前 Realtime 入口允许匿名访问；生产环境仍建议启用客户端 key，并让浏览器换取短期 client_secret。'
                : isWebSocketEntrance && requiresKey
              ? 'Node、Android、iOS 等可设置 WebSocket header 的客户端使用 Authorization header；浏览器原生 WebSocket 使用 api_key 查询参数。'
              : isWebSocketEntrance
                ? '当前入口允许匿名 WebSocket 连接；浏览器和原生客户端都不需要客户端 key。'
                : requiresKey
              ? 'Chat Completions 等业务调用需要在 Authorization 头中传入该入口下创建的 sk-style key。文档只展示占位符，不会显示已保存的明文 key；翻译模型发现的公开规则见下方路径说明。'
              : '当前入口允许匿名调用，HTTP 示例不会包含 Authorization 头；如果 SDK 强制要求 api_key 参数，可使用普通占位值。'
          }
        />

        {isHTTPEntrance && base.valueComplianceEnabled && (
          <Banner
            type="info"
            closeIcon={null}
            description={`本入口已启用价值观合规（策略 ${base.valueCompliancePolicyVersion || '当前版本'}）。AIgate 会改写支持的普通文本生成请求，并固定使用合规专用模型 ${base.valueComplianceModel || '未配置'}；客户端传入的 model 不会用于合规上游。`}
          />
        )}

        {base.appbossBillingEnabled && (
          <Banner
            type="info"
            closeIcon={null}
            description={
              upstreamOwnedAppBossBilling
                ? '上游扣费模式下，AIgate 仍会先检查余额；余额不足会拦截请求。转发给上游时会注入 X-APPBOSS-UID、X-APPBOSS-BALANCE-URL、X-APPBOSS-DEDUCT-URL，由上游决定扣点并调用扣费地址。'
                : '网关扣费模式下，AIgate 会先检查余额，并在请求完成后按网关计量结果扣费；APPBOSS 回调地址不会转发给上游。'
            }
          />
        )}

        {isHTTPEntrance && (
          <EntranceHTTPDocs
            base={base}
            chatPath={chatPath}
            modelsPath={modelsPath}
            requiresKey={requiresKey}
            chatCurlExample={curlExample}
            chatPythonExample={pythonExample}
            renderCodeSnippet={(title, value) => <CodeSnippet title={title} value={value} />}
            renderCopyableURL={(value) => <CopyableURL value={value} />}
          />
        )}
        {isWebSocketEntrance && (
          <>
            {base.appbossBillingEnabled && (
              <Banner
                type="info"
                closeIcon={null}
                description={
                  upstreamOwnedAppBossBilling
                    ? 'WebSocket 上游扣费模式下，客户端仍只传 APPBOSS UID；AIgate 检查余额后会向上游注入可信 APPBOSS 透传头。'
                    : 'WebSocket APPBOSS 身份由 AIgate 用于计费，不会转发给上游；原生客户端使用 X-APPBOSS-UID header，浏览器客户端使用 appboss_uid 查询参数。'
                }
              />
            )}
            <CodeSnippet title="WebSocket 浏览器示例" value={websocketBrowserExample} />
            {(requiresKey || base.appbossBillingEnabled) && <CodeSnippet title="WebSocket 原生客户端 Header 示例" value={websocketNativeExample} />}
          </>
        )}
        {isRealtimeEntrance && (
          <RealtimeEntranceDocs base={base} baseUrl={baseUrl} websocketUrl={realtimeWsUrl} requiresKey={requiresKey} />
        )}
      </div>
    </Modal>
  );
}

function RealtimeEntranceDocs({ base, baseUrl, websocketUrl, requiresKey }: { base: BaseURL; baseUrl: string; websocketUrl: string; requiresKey: boolean }) {
  const translationWebsocketUrl = websocketUrl.replace(/\/realtime$/, '/realtime/translations');
  const nativeWS = `// Node.js / 服务端（ws 包）
import WebSocket from "ws";

const ws = new WebSocket("${websocketUrl}?model=gpt-realtime", {
  headers: { ${requiresKey ? 'Authorization: "Bearer <client-api-key>"' : '/* 该入口允许匿名 */'} },
});
ws.on("message", (event) => console.log(JSON.parse(event.toString())));`;
  const browserWS = requiresKey
    ? `const ws = new WebSocket(\n  "${websocketUrl}?model=gpt-realtime",\n  ["realtime", "openai-insecure-api-key.<client-api-key>"]\n);`
    : `const ws = new WebSocket("${websocketUrl}?model=gpt-realtime", ["realtime"]);`;
  const secretHeaders = requiresKey ? '  -H "Authorization: Bearer <client-api-key>" \\\n' : '';
  const clientSecret = `curl -X POST "${baseUrl}/realtime/client_secrets" \\\n${secretHeaders}  -H "Content-Type: application/json" \\\n  -d '{"session":{"type":"realtime","model":"gpt-realtime"}}'`;
  const webrtc = `// <session-token> 应由可信后端提供：优先使用短期 client_secret，切勿把永久上游 key 放进浏览器。
const pc = new RTCPeerConnection();
const audio = document.createElement("audio");
audio.autoplay = true;
pc.ontrack = (event) => { audio.srcObject = event.streams[0]; };

const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
microphone.getTracks().forEach((track) => pc.addTrack(track, microphone));

const events = pc.createDataChannel("oai-events");
events.addEventListener("message", (event) => console.log(JSON.parse(event.data)));

const offer = await pc.createOffer();
await pc.setLocalDescription(offer);

const form = new FormData();
form.set("sdp", offer.sdp || "");
form.set("session", JSON.stringify({ type: "realtime", model: "gpt-realtime" }));

const response = await fetch("${baseUrl}/realtime/calls", {
  method: "POST",
  headers: { Authorization: "Bearer <session-token>" },
  body: form,
});
if (!response.ok) throw new Error(await response.text());
const callLocation = response.headers.get("Location"); // AIgate 已改写为当前入口
await pc.setRemoteDescription({ type: "answer", sdp: await response.text() });`;
  const ephemeralWS = `const ws = new WebSocket(
  "${websocketUrl}?model=gpt-realtime",
  ["realtime", "openai-ephemeral-user-api-key.<client-secret>"]
);`;
  const translation = `# 先在可信后端签发翻译专用短期凭证
curl -X POST "${baseUrl}/realtime/translations/client_secrets" \\
${secretHeaders}  -H "Content-Type: application/json" \\
  -d '{"session":{"model":"gpt-realtime-translate","audio":{"output":{"language":"zh"}}}}'

# 客户端随后连接专用翻译 WebSocket
const translation = new WebSocket(
  "${translationWebsocketUrl}",
  ["realtime", "openai-ephemeral-user-api-key.<client-secret>"]
);`;
  const transcription = `# 兼容 Realtime transcription session 创建接口
curl -X POST "${baseUrl}/realtime/transcription_sessions" \\
${secretHeaders}  -H "Content-Type: application/json" \\
  -d '{"input_audio_format":"pcm16","input_audio_transcription":{"model":"gpt-4o-transcribe"}}'

# 使用响应中的 client_secret 连接主 Realtime WebSocket，并保留转写 intent
${websocketUrl}?intent=transcription`;
  const controls = `curl -X POST "${baseUrl}/realtime/calls/<call_id>/accept" -H "Authorization: Bearer <client-api-key>" -H "Content-Type: application/json" -d '{"type":"realtime","model":"gpt-realtime"}'

curl -X POST "${baseUrl}/realtime/calls/<call_id>/reject" -H "Authorization: Bearer <client-api-key>" -H "Content-Type: application/json" -d '{"status_code":486}'

curl -X POST "${baseUrl}/realtime/calls/<call_id>/refer" -H "Authorization: Bearer <client-api-key>" -H "Content-Type: application/json" -d '{"target_uri":"tel:+15551234567"}'

curl -X POST "${baseUrl}/realtime/calls/<call_id>/hangup" -H "Authorization: Bearer <client-api-key>"`;
  const sideband = `const control = new WebSocket("${websocketUrl}?call_id=<call_id>", ["realtime", "openai-insecure-api-key.<client-api-key>"]);`;
  const webhookVerification = `// 必须使用未经 JSON 解析的 rawBody
const signed = webhookId + "." + webhookTimestamp + "." + rawBody;
const expected = base64(hmacSha256(base64Decode(signingSecret.replace("whsec_", "")), signed));
// 校验 webhook-signature 中的 v1 值，并拒绝超出 5 分钟时间窗或重复 webhook-id。`;
  return (
    <>
      <Banner type="info" closeIcon={null} description="Realtime 媒体流在浏览器/电话与上游之间直传；AIgate 负责鉴权、信令、会话归属、Calls 控制、Webhook 与审计，不转码音频。" />
      {base.realtimeCapabilities?.websocket && <><CodeSnippet title="Realtime WebSocket（原生客户端）" value={nativeWS} /><CodeSnippet title="Realtime WebSocket（浏览器子协议）" value={browserWS} /></>}
      {base.realtimeCapabilities?.translations && <CodeSnippet title="Realtime 实时语音翻译" value={translation} />}
      {base.realtimeCapabilities?.transcription && <CodeSnippet title="Realtime 实时语音转写会话" value={transcription} />}
      {base.realtimeCapabilities?.clientSecrets && <><CodeSnippet title="签发浏览器短期 client_secret" value={clientSecret} /><CodeSnippet title="浏览器用短期凭证连接 Realtime WebSocket" value={ephemeralWS} /></>}
      {base.realtimeCapabilities?.webrtc && <CodeSnippet title="浏览器 WebRTC（音频轨道 + oai-events + 统一 Calls 接口）" value={webrtc} />}
      {base.realtimeCapabilities?.calls && <CodeSnippet title="Calls 控制接口" value={controls} />}
      {base.realtimeCapabilities?.sideband && <CodeSnippet title="Sideband 控制连接" value={sideband} />}
      {base.realtimeCapabilities?.sip && (
        <div className="api-doc-summary">
          <DetailItem label="SIP Trunk URI">{base.realtimeSipUri ? <CopyableURL value={base.realtimeSipUri} /> : <Text type="danger">网关尚未配置公开 SIP 域名或端口</Text>}</DetailItem>
          <DetailItem label="来电事件回调">{base.realtimeWebhookCallbackUrl ? <CopyableURL value={base.realtimeWebhookCallbackUrl} /> : <Text type="secondary">未配置</Text>}</DetailItem>
          <DetailItem label="Carrier / Trunk 责任"><Text type="secondary">使用 TLS 连接入口 SIP URI，并允许 SDP 指向的 provider RTP/SRTP/ICE 地址双向可达。</Text></DetailItem>
          {base.appbossBillingEnabled && <DetailItem label="SIP APPBOSS"><Text type="secondary">仅信任 AIGATE_REALTIME_SIP_APPBOSS_TRUSTED_CIDRS 中来源的初始 INVITE；carrier 或前级 AIgate 必须提供 X-APPBOSS-UID。</Text></DetailItem>}
        </div>
      )}
      {base.realtimeWebhookCallbackUrl && <CodeSnippet title="来电 Webhook 验签要点" value={webhookVerification} />}
    </>
  );
}

function CodeSnippet({ title, value }: { title: string; value: string }) {
  return (
    <div className="api-doc-code-card">
      <div className="api-doc-code-head">
        <Text strong>{title}</Text>
        <Button size="small" onClick={() => copyText(value)}>
          复制
        </Button>
      </div>
      <pre className="api-doc-code-block">
        <code>{value}</code>
      </pre>
    </div>
  );
}

function normalizeBaseURLDraft(draft: Partial<BaseURL>, editing?: BaseURL | null): Partial<BaseURL> {
  const protocol = baseProtocol(draft);
  const next: Partial<BaseURL> = { ...draft, protocol };
  if (protocol === 'websocket') {
    next.websocketEnabled = true;
    next.websocketRoutePrefix = '/ws';
    next.websocketTargetType = websocketTargetType(draft);
    next.defaultUpstreamTargetType = undefined;
    next.defaultUpstreamId = undefined;
    next.defaultUpstreamClusterId = undefined;
    next.fallbackTargetType = undefined;
    next.fallbackBehavior = 'temporary_unavailable';
    next.fallbackUpstreamId = undefined;
    next.fallbackUpstreamClusterId = undefined;
    next.valueComplianceEnabled = false;
    next.valueComplianceUpstreamId = undefined;
    next.valueComplianceModel = undefined;
    next.realtimeTargetType = undefined;
    next.realtimeUpstreamId = undefined;
    next.realtimeUpstreamClusterId = undefined;
    next.realtimeCapabilities = {};
    next.realtimeSipSlug = '';
    next.realtimeWebhookCallbackUrl = '';
    next.realtimeWebhookSigningSecret = '';
    if (next.websocketTargetType === 'upstream_cluster') {
      next.websocketUpstreamId = undefined;
    } else {
      next.websocketUpstreamClusterId = undefined;
    }
  } else if (protocol === 'openai_realtime') {
    next.defaultUpstreamTargetType = undefined;
    next.defaultUpstreamId = undefined;
    next.defaultUpstreamClusterId = undefined;
    next.fallbackTargetType = undefined;
    next.fallbackBehavior = 'temporary_unavailable';
    next.fallbackUpstreamId = undefined;
    next.fallbackUpstreamClusterId = undefined;
    next.websocketEnabled = false;
    next.websocketRoutePrefix = '';
    next.websocketTargetType = undefined;
    next.websocketUpstreamId = undefined;
    next.websocketUpstreamClusterId = undefined;
    next.valueComplianceEnabled = false;
    next.valueComplianceUpstreamId = undefined;
    next.valueComplianceModel = undefined;
    next.realtimeTargetType = realtimeTargetType(draft);
    next.realtimeCapabilities = hasRealtimeCapabilities(next.realtimeCapabilities) ? next.realtimeCapabilities : defaultRealtimeCapabilities();
    if (next.realtimeTargetType === 'upstream_cluster') next.realtimeUpstreamId = undefined;
    else next.realtimeUpstreamClusterId = undefined;
    if (!next.realtimeCapabilities.sip) {
      next.realtimeSipSlug = '';
      next.realtimeWebhookCallbackUrl = '';
      next.realtimeWebhookSigningSecret = '';
    } else {
      next.realtimeSipSlug = String(next.realtimeSipSlug || '').trim();
      next.realtimeWebhookCallbackUrl = String(next.realtimeWebhookCallbackUrl || '').trim();
      next.realtimeWebhookSigningSecret = String(next.realtimeWebhookSigningSecret || '').trim();
    }
  } else {
    next.valueComplianceEnabled = valueComplianceEnabledForProtocol(protocol, next.valueComplianceEnabled);
    next.valueComplianceUpstreamId = valueComplianceUpstreamIdForProtocol(protocol, next.valueComplianceUpstreamId);
    next.valueComplianceModel = valueComplianceModelForProtocol(protocol, next.valueComplianceModel);
    next.defaultUpstreamTargetType = baseTargetType(draft);
    next.fallbackTargetType = fallbackTargetType(draft);
    if (next.defaultUpstreamTargetType === 'upstream_cluster') {
      next.defaultUpstreamId = undefined;
    } else {
      next.defaultUpstreamClusterId = undefined;
    }
    if (next.fallbackBehavior !== 'use_fallback') {
      next.fallbackBehavior = 'temporary_unavailable';
      next.fallbackUpstreamId = undefined;
      next.fallbackUpstreamClusterId = undefined;
    } else if (next.fallbackTargetType === 'upstream_cluster') {
      next.fallbackUpstreamId = undefined;
    } else {
      next.fallbackUpstreamClusterId = undefined;
    }
    next.websocketEnabled = false;
    next.websocketRoutePrefix = '';
    next.websocketTargetType = undefined;
    next.websocketUpstreamId = undefined;
    next.websocketUpstreamClusterId = undefined;
    next.realtimeTargetType = undefined;
    next.realtimeUpstreamId = undefined;
    next.realtimeUpstreamClusterId = undefined;
    next.realtimeCapabilities = {};
    next.realtimeSipSlug = '';
    next.realtimeWebhookCallbackUrl = '';
    next.realtimeWebhookSigningSecret = '';
  }
  next.valueCompliancePolicyVersion = undefined;
  if (!next.appbossBillingEnabled) {
    next.appbossBillingEnabled = false;
    next.appbossBillingMode = 'gateway';
    next.appbossServiceId = '';
    next.appbossBalanceCheckUrl = '';
    next.appbossDeductUrl = '';
  } else {
    next.appbossBillingMode = appBossBillingModeOf(next);
    next.appbossServiceId = String(next.appbossServiceId || '').trim();
    next.appbossBalanceCheckUrl = String(next.appbossBalanceCheckUrl || '').trim();
    next.appbossDeductUrl = String(next.appbossDeductUrl || '').trim();
  }
  next.publicUrls = undefined;
  return next.authMode === 'appboss_token' ? tokenEntranceDraft(next, editing) : next;
}

function validateBaseURLDraftForSave(draft: Partial<BaseURL>, editing?: BaseURL | null) {
  const tokenProblem = validateTokenEntrance(draft, editing);
  if (tokenProblem) throw new Error(tokenProblem);
  const protocol = baseProtocol(draft);
  if (protocol === 'websocket') {
    const targetType = websocketTargetType(draft);
    if (targetType === 'upstream' && !draft.websocketUpstreamId) {
      throw new Error('请选择上游服务');
    }
    if (targetType === 'upstream_cluster' && !draft.websocketUpstreamClusterId) {
      throw new Error('请选择上游集群');
    }
  } else if (protocol === 'openai_realtime') {
    const targetType = realtimeTargetType(draft);
    if (targetType === 'upstream' && !draft.realtimeUpstreamId) throw new Error('请选择 Realtime 上游服务');
    if (targetType === 'upstream_cluster' && !draft.realtimeUpstreamClusterId) throw new Error('请选择 Realtime 上游集群');
    validateRealtimeCapabilities(draft.realtimeCapabilities);
    if (draft.realtimeCapabilities?.sip) {
      if (!String(draft.realtimeSipSlug || '').trim()) throw new Error('启用 SIP 时必须填写 SIP 入口标识');
      const callbackURL = String(draft.realtimeWebhookCallbackUrl || '').trim();
      if (callbackURL) validateHTTPSURL(callbackURL, '来电 Webhook 回调 URL');
    }
  } else {
    const targetType = baseTargetType(draft);
    if (targetType === 'upstream' && !draft.defaultUpstreamId) {
      throw new Error('请选择默认上游服务');
    }
    if (targetType === 'upstream_cluster' && !draft.defaultUpstreamClusterId) {
      throw new Error('请选择默认上游集群');
    }
    if (draft.fallbackBehavior === 'use_fallback') {
      const standbyType = fallbackTargetType(draft);
      if (standbyType === 'upstream' && !draft.fallbackUpstreamId) {
        throw new Error('请选择备用上游服务');
      }
      if (standbyType === 'upstream_cluster' && !draft.fallbackUpstreamClusterId) {
        throw new Error('请选择备用上游集群');
      }
    }
    const complianceError = validateValueComplianceConfiguration(baseProtocol(draft), draft.valueComplianceEnabled, draft.valueComplianceUpstreamId, draft.valueComplianceModel);
    if (complianceError) throw new Error(complianceError);
  }
  if (draft.appbossBillingEnabled) {
    const serviceId = String(draft.appbossServiceId || '').trim();
    const balanceURL = String(draft.appbossBalanceCheckUrl || '').trim();
    const deductURL = String(draft.appbossDeductUrl || '').trim();
    if (appBossBillingModeOf(draft) === 'gateway' && !serviceId) {
      throw new Error('请填写 AppBoss 服务类型');
    }
    if (!balanceURL && !editing?.appbossBalanceCheckUrlSet) {
      throw new Error('请填写 APPBOSS 检查余额服务器地址');
    }
    if (!deductURL && !editing?.appbossDeductUrlSet) {
      throw new Error('请填写 APPBOSS 扣费服务器地址');
    }
    if (balanceURL) validateHttpURL(balanceURL, 'APPBOSS 检查余额服务器地址');
    if (deductURL) validateHttpURL(deductURL, 'APPBOSS 扣费服务器地址');
  }
}

function validateHTTPSURL(value: string, label: string) {
  try {
    if (new URL(value).protocol !== 'https:') throw new Error('bad protocol');
  } catch {
    throw new Error(`${label} 必须是 https 地址`);
  }
}

function validateHttpURL(value: string, label: string) {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error('bad protocol');
  } catch {
    throw new Error(`${label} 必须是 http 或 https 地址`);
  }
}

function clientBaseURL(base: Partial<BaseURL>) {
  const urls = clientBaseURLs(base);
  if (urls.length > 0) return urls[0];
  return '';
}

function clientBaseURLs(base: Partial<BaseURL>) {
  const urls = publicURLsForBase(base);
  if (urls.length > 0) return urls;
  const slug = String(base.slug || '').trim();
  if (!slug) return [];
  return [`${window.location.origin}/b/${slug}/v1`];
}

function defaultClientBaseURL(base: Partial<BaseURL>) {
  return String(base.slug || '').trim() === 'default' ? `${window.location.origin}/v1` : '';
}

function clientWebSocketURL(base: Partial<BaseURL>) {
  const urls = clientWebSocketURLs(base);
  if (urls.length > 0) return urls[0];
  return '';
}

function clientWebSocketURLs(base: Partial<BaseURL>) {
  const urls = publicURLsForBase(base);
  if (urls.length > 0) {
    return urls.map((url) => toWebSocketClientURL(url, base.websocketRoutePrefix || '/ws'));
  }
  const slug = String(base.slug || '').trim();
  if (!slug) return [];
  const prefix = normalizeWebSocketRoutePrefix(base.websocketRoutePrefix || '/ws');
  const httpBase = `${window.location.origin}/b/${slug}${prefix}`;
  return [httpBase.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:')];
}

function clientRealtimeWebSocketURLs(base: Partial<BaseURL>) {
  if (base.realtimeWebSocketUrl) return [base.realtimeWebSocketUrl];
  return clientBaseURLs(base).map((raw) => {
    try {
      const url = new URL(raw);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.pathname = `${url.pathname.replace(/\/+$/, '')}/realtime`;
      return url.toString();
    } catch {
      return `${raw.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:').replace(/\/+$/, '')}/realtime`;
    }
  });
}

function toWebSocketClientURL(raw: string, routePrefix: string) {
  const prefix = normalizeWebSocketRoutePrefix(routePrefix || '/ws');
  try {
    const url = new URL(raw);
    if (url.protocol === 'http:') url.protocol = 'ws:';
    if (url.protocol === 'https:') url.protocol = 'wss:';
    if (url.pathname === '/v1' || url.pathname.endsWith('/v1')) {
      url.pathname = `${url.pathname.slice(0, -3)}${prefix}`;
    }
    return url.toString();
  } catch {
    return raw.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:').replace(/\/v1$/, prefix);
  }
}

function normalizeWebSocketRoutePrefix(prefix: string) {
  const clean = String(prefix || '/ws').trim().replace(/\/+$/, '') || '/ws';
  return clean.startsWith('/') ? clean : `/${clean}`;
}

function copyText(value: string) {
  if (!value) return;
  navigator.clipboard
    ?.writeText(value)
    .then(() => Toast.success('已复制'))
    .catch(() => Toast.error('复制失败'));
}

function CopyableURL({ value }: { value: string }) {
  if (!value) return <span>-</span>;
  return (
    <div className="copy-line compact">
      <code>{value}</code>
      <Button size="small" onClick={() => copyText(value)}>
        复制
      </Button>
    </div>
  );
}

function docsfarmDocsUpstreamForBase(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  return entranceTestTargetUpstreams(base, upstreams, clusters).find((upstream) => Boolean(upstream.capabilities?.docsfarm));
}

function openDocsfarmDocsWindow(base: BaseURL, upstream: Upstream) {
  const publicHost = gatewayPublicHost(base);
  const docsUrls = buildDocsfarmDocsURLs(upstream.baseUrl, publicHost);
  if (!docsUrls.docs) {
    Toast.error('无法生成文档农场地址');
    return;
  }
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const payload: DocsfarmDocsPayload = {
    baseName: base.name,
    slug: base.slug,
    upstreamName: upstream.name,
    publicHost,
    iframeUrl: docsUrls.docs,
    docsUrls,
    createdAt: Date.now()
  };
  try {
    localStorage.setItem(`${DOCSFARM_DOCS_STORAGE_PREFIX}${token}`, JSON.stringify(payload));
  } catch {
    Toast.error('无法打开文档窗口，请检查浏览器存储权限');
    return;
  }
  const opened = window.open(`${DOCSFARM_DOCS_ROUTE}?token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
  if (!opened) Toast.error('浏览器阻止了文档窗口，请允许弹窗后重试。');
}

function readDocsfarmDocsPayload(token: string): DocsfarmDocsPayload | null {
  if (!token) return null;
  try {
    const raw = localStorage.getItem(`${DOCSFARM_DOCS_STORAGE_PREFIX}${token}`);
    if (!raw) return null;
    const payload = JSON.parse(raw) as Partial<DocsfarmDocsPayload>;
    if ((!payload.iframeUrl && !payload.docsUrls?.docs) || !payload.baseName || !payload.publicHost) return null;
    return payload as DocsfarmDocsPayload;
  } catch {
    return null;
  }
}

function gatewayPublicHost(base: BaseURL) {
  try {
    return new URL(clientBaseURL(base)).origin;
  } catch {
    return window.location.origin;
  }
}

function buildDocsfarmDocsURLs(upstreamBaseURL: string, publicHost: string): Record<DocsfarmDocsTabKey, string> {
  return DOCSFARM_DOCS_TABS.reduce(
    (out, item) => ({
      ...out,
      [item.key]: buildDocsfarmDocsURL(upstreamBaseURL, publicHost, item.path, item.includePublicHost)
    }),
    {} as Record<DocsfarmDocsTabKey, string>
  );
}

function buildDocsfarmDocsURL(upstreamBaseURL: string, publicHost: string, targetPath = '/docs', includePublicHost = true) {
  const trimmed = String(upstreamBaseURL || '').trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    const upstreamPath = url.pathname.replace(/\/+$/, '');
    const docsRoot = upstreamPath.endsWith('/v1') ? upstreamPath.slice(0, -3) : upstreamPath;
    url.pathname = joinURLPath(docsRoot, targetPath);
    url.search = '';
    url.hash = '';
    if (includePublicHost) url.searchParams.set('publiic_host', publicHost);
    return url.toString();
  } catch {
    const docsRoot = trimmed.replace(/\/+$/, '').replace(/\/v1$/, '');
    const suffix = includePublicHost ? `?publiic_host=${encodeURIComponent(publicHost)}` : '';
    return `${docsRoot}${targetPath}${suffix}`;
  }
}

function joinURLPath(rootPath: string, targetPath: string) {
  const root = rootPath.replace(/\/+$/, '');
  const target = targetPath.startsWith('/') ? targetPath : `/${targetPath}`;
  return `${root}${target}` || '/';
}

function renderBaseTarget(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (baseTargetType(base) === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.defaultUpstreamClusterId);
    return (
      <Space>
        <Tag color="violet">集群</Tag>
        <span>{cluster?.name || '-'}</span>
      </Space>
    );
  }
  const upstream = upstreams.find((item) => item.id === base.defaultUpstreamId);
  return (
    <Space>
      <Tag color="blue">服务</Tag>
      <span>{upstream?.name || '-'}</span>
    </Space>
  );
}

function renderEntranceTarget(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (baseProtocol(base) === 'websocket') return renderWebSocketTarget(base, upstreams, clusters);
  if (baseProtocol(base) === 'openai_realtime') return renderRealtimeTarget(base, upstreams, clusters);
  return renderBaseTarget(base, upstreams, clusters);
}

function renderValueComplianceRoute(base: BaseURL, upstreams: Upstream[]) {
  if (baseProtocol(base) === 'websocket' || !base.valueComplianceEnabled) {
    return <Tag color="grey">关闭</Tag>;
  }
  const target = valueComplianceUpstreamStatus(base.valueComplianceEnabled, base.valueComplianceUpstreamId, upstreams, base.valueComplianceModel);
  return (
    <div className="value-compliance-route-status">
      <Tag color="green">{valueComplianceStatusText(true, base.valueCompliancePolicyVersion)}</Tag>
      <Text type={target.state === 'ready' ? 'success' : 'danger'}>{target.text}</Text>
    </div>
  );
}

function renderWebSocketTarget(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (!base.websocketEnabled) return <Tag color="grey">未启用</Tag>;
  if (websocketTargetType(base) === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.websocketUpstreamClusterId);
    return (
      <Space>
        <Tag color="violet">集群</Tag>
        <span>{cluster?.name || '-'}</span>
      </Space>
    );
  }
  const upstream = upstreams.find((item) => item.id === base.websocketUpstreamId);
  return (
    <Space>
      <Tag color="cyan">服务</Tag>
      <span>{upstream?.name || '-'}</span>
    </Space>
  );
}

function renderRealtimeTarget(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (realtimeTargetType(base) === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.realtimeUpstreamClusterId);
    return <Space><Tag color="violet">Realtime 集群</Tag><span>{cluster?.name || '-'}</span></Space>;
  }
  const upstream = upstreams.find((item) => item.id === base.realtimeUpstreamId);
  return <Space><Tag color="green">Realtime 服务</Tag><span>{upstream?.name || '-'}</span></Space>;
}

function upstreamProtocol(upstream: Partial<Upstream>) {
  return upstream.protocol || 'openai_http';
}

function isWebSocketUpstream(upstream: Partial<Upstream>) {
  return upstreamProtocol(upstream) === 'websocket';
}

function isRealtimeUpstream(upstream: Partial<Upstream>) {
  return upstreamProtocol(upstream) === 'openai_realtime';
}

function upstreamProtocolTag(upstream: Partial<Upstream>) {
  if (isWebSocketUpstream(upstream)) return <Tag color="cyan">WebSocket</Tag>;
  if (isRealtimeUpstream(upstream)) return <Tag color="green">OpenAI Realtime</Tag>;
  return <Tag color="blue">OpenAI HTTP</Tag>;
}

function entranceProtocolTag(base: Partial<BaseURL> | Partial<BaseURLMonitoringItem>) {
  if (baseProtocol(base) === 'websocket') return <Tag color="cyan">WebSocket</Tag>;
  if (baseProtocol(base) === 'openai_realtime') return <Tag color="green">OpenAI Realtime</Tag>;
  return <Tag color="blue">OpenAI HTTP</Tag>;
}

function usageProtocolLabel(protocol?: string) {
  switch (protocol) {
    case 'websocket': return 'WebSocket';
    case 'openai_realtime_websocket': return 'Realtime WS';
    case 'openai_realtime_sideband': return 'Realtime Sideband';
    case 'openai_realtime_webrtc': return 'Realtime WebRTC';
    case 'openai_realtime_sip': return 'Realtime SIP';
    case 'openai_realtime_control': return 'Realtime Control';
    default: return 'HTTP';
  }
}

function usageProtocolColor(protocol?: string) {
  if (protocol === 'websocket') return 'cyan';
  if (protocol?.startsWith('openai_realtime_')) return 'green';
  return 'blue';
}

function isGatewayWebSocketProtocol(protocol?: string) {
  return protocol === 'websocket' || protocol === 'openai_realtime_websocket' || protocol === 'openai_realtime_sideband';
}

function isRealtimeSessionProtocol(protocol?: string) {
  return isGatewayWebSocketProtocol(protocol) || protocol === 'openai_realtime_webrtc' || protocol === 'openai_realtime_sip';
}

function clusterHasProtocol(cluster: UpstreamCluster, upstreams: Upstream[], protocol: EntranceProtocol) {
  const byID = new Map(upstreams.map((item) => [item.id, item]));
  return normalizeClusterMembers(cluster.members || []).some((member) => {
    const upstream = byID.get(member.upstreamId);
    if (!upstream || upstream.status !== 'enabled' || member.status !== 'enabled') return false;
    return upstreamProtocol(upstream) === protocol;
  });
}

function supportsRealtimeCapabilities(available?: RealtimeCapabilities, required?: RealtimeCapabilities) {
  return REALTIME_CAPABILITY_OPTIONS.every((option) => !required?.[option.key] || Boolean(available?.[option.key]));
}

function clusterHasCompatibleRealtimeTarget(cluster: UpstreamCluster, upstreams: Upstream[], required?: RealtimeCapabilities) {
  if (cluster.status !== 'enabled') return false;
  const byID = new Map(upstreams.map((item) => [item.id, item]));
  return normalizeClusterMembers(cluster.members || []).some((member) => {
    const upstream = byID.get(member.upstreamId);
    return Boolean(member.status === 'enabled' && upstream?.status === 'enabled' && isRealtimeUpstream(upstream) && supportsRealtimeCapabilities(upstream.realtimeCapabilities, required));
  });
}

function renderFallbackTarget(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (base.fallbackBehavior !== 'use_fallback') {
    return <Tag color="orange">临时不可用</Tag>;
  }
  if (fallbackTargetType(base) === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.fallbackUpstreamClusterId);
    return (
      <Space>
        <Tag color="violet">集群</Tag>
        <span>{cluster?.name || '-'}</span>
      </Space>
    );
  }
  const upstream = upstreams.find((item) => item.id === base.fallbackUpstreamId);
  return (
    <Space>
      <Tag color="blue">服务</Tag>
      <span>{upstream?.name || '-'}</span>
    </Space>
  );
}

function BaseURLTestModal({ base, onClose }: { base: BaseURL; onClose: () => void }) {
  const upstreams = useQuery({ queryKey: ['upstreams'], queryFn: api.upstreams });
  const clusters = useQuery({ queryKey: ['upstreamClusters'], queryFn: api.upstreamClusters });
  const [model, setModel] = useState('');
  const [question, setQuestion] = useState('请用一句话介绍你自己。');
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('en');
  const targetUpstreams = useMemo(() => entranceTestTargetUpstreams(base, upstreams.data || [], clusters.data || []), [base, upstreams.data, clusters.data]);
  const supportedActions = useMemo(() => entranceTestActionsForTargets(targetUpstreams), [targetUpstreams]);
  const targetMissingCapabilities = targetUpstreams.length > 0 && targetUpstreams.some((upstream) => !hasConfiguredCapabilities(upstream.capabilities));
  const targetSupportsDocsfarm = targetUpstreams.length > 0 && targetUpstreams.every((upstream) => Boolean(upstream.capabilities?.docsfarm));
  const translationTargetKey = targetUpstreams.map((upstream) => upstream.id).sort().join(',');
  const translationCapabilities = useQuery({
    queryKey: ['translationCapabilities', translationTargetKey],
    queryFn: () => Promise.all(targetUpstreams.map((upstream) => api.translationCapabilities(upstream.id))),
    enabled: supportedActions.includes('translation') && targetUpstreams.length > 0,
    retry: false
  });
  const mergedTranslationCapabilities = useMemo(
    () => mergeTranslationCapabilities(translationCapabilities.data || []),
    [translationCapabilities.data]
  );
  const translationCapabilitiesReady = Boolean(
    !translationCapabilities.isError
    && mergedTranslationCapabilities
    && translationCapabilities.data?.length === targetUpstreams.length
  );
  useEffect(() => {
    if (!mergedTranslationCapabilities) return;
    const sourceCodes = mergedTranslationCapabilities.sourceLanguages.map((language) => language.code);
    const targetCodes = mergedTranslationCapabilities.languages.map((language) => language.code);
    if (!sourceCodes.includes(sourceLang)) setSourceLang(sourceCodes.includes('auto') ? 'auto' : sourceCodes[0] || '');
    if (!targetCodes.includes(targetLang)) setTargetLang(targetCodes.includes('en') ? 'en' : targetCodes[0] || '');
  }, [mergedTranslationCapabilities, sourceLang, targetLang]);
  const test = useMutation({
    mutationFn: (action: EntranceTestAction) => api.testBaseURL(base.id, { model, question, action, sourceLang, targetLang }),
    onError: (error: Error) => Toast.error(error.message)
  });
  return (
    <Modal title={`测试入口：${base.name}`} visible onCancel={onClose} footer={null} width={860}>
      <div className="modal-form">
        <label>
          <Text strong>测试模型</Text>
          <Input placeholder="留空使用默认上游的第一个模型" value={model} onChange={setModel} />
          {supportedActions.includes('translation') && <Text type="tertiary">仅用于普通测试动作；翻译测试固定使用 Uni Trans 公共模型 auto。</Text>}
        </label>
        <label>
          <Text strong>测试文本 / 问题</Text>
          <Input placeholder="请输入测试内容" value={question} onChange={setQuestion} />
        </label>
        {supportedActions.includes('translation') && (
          <>
            <Banner
              type={translationCapabilities.isError || (translationCapabilities.data && !mergedTranslationCapabilities) ? 'danger' : 'info'}
              closeIcon={null}
              description={translationCapabilities.isLoading
                ? '正在从 Uni Trans 的 /v1/models/auto/capabilities 读取语言能力…'
                : translationCapabilities.isError
                  ? `无法读取翻译能力，翻译测试已禁用：${(translationCapabilities.error as Error).message}`
                  : translationCapabilities.data && !mergedTranslationCapabilities
                    ? '候选上游没有共同的 auto 翻译语言，翻译测试已禁用。'
                  : '翻译测试固定使用公共模型 auto；实际 provider 由 Uni Trans 根据翻译方向选择。'}
            />
            {mergedTranslationCapabilities && (
              <div className="translation-language-grid">
                <label>
                  <Text strong>源语言</Text>
                  <Select value={sourceLang} onChange={(value) => setSourceLang(String(value))} className="select">
                    {mergedTranslationCapabilities.sourceLanguages.map((language) => (
                      <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>
                    ))}
                  </Select>
                </label>
                <label>
                  <Text strong>目标语言</Text>
                  <Select value={targetLang} onChange={(value) => setTargetLang(String(value))} className="select">
                    {mergedTranslationCapabilities.languages.map((language) => (
                      <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>
                    ))}
                  </Select>
                </label>
                {mergedTranslationCapabilities.directionRules.length > 0 && (
                  <Text type="tertiary" className="translation-direction-rule">
                    方向规则：{mergedTranslationCapabilities.directionRules.join('；')}
                  </Text>
                )}
              </div>
            )}
          </>
        )}
        {targetUpstreams.length === 0 && <Banner type="warning" closeIcon={null} description="入口没有可用于测试的上游服务。" />}
        {targetMissingCapabilities && <Banner type="warning" closeIcon={null} description="上游服务未配置能力标签，请先在上游服务中配置问答、流式、多模态、思考模式、翻译服务或文档农场能力。" />}
        <div className="test-action-row">
          {ENTRANCE_TEST_ACTIONS.filter((item) => supportedActions.includes(item.action)).map((item) => (
            <Button
              key={item.action}
              theme="solid"
              type="primary"
              loading={test.isPending}
              disabled={item.action === 'translation' && !translationCapabilitiesReady}
              onClick={() => test.mutate(item.action)}
            >
              {item.label}
            </Button>
          ))}
          {targetUpstreams.length > 0 && !targetSupportsDocsfarm && <Text type="secondary">文档农场测试需要在目标上游启用文档农场能力。</Text>}
          {targetUpstreams.length > 0 && supportedActions.length === 0 && <Text type="secondary">当前上游能力没有可执行的入口测试按钮。</Text>}
        </div>
        {test.data && (
          <div className="test-results">
            {(test.data.results || []).map((item: EntranceTestResult) => (
              <EntranceTestResultCard key={`${item.action || item.name}-${item.selectedUpstreamId || ''}`} item={item} />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function EntranceTestResultCard({ item }: { item: EntranceTestResult }) {
  const subtests = item.subtests || [];
  return (
    <Card className="test-result">
      <EntranceTestResultHeader item={item} />
      {subtests.length > 0 ? (
        <div className="docsfarm-subtests">
          {subtests.map((subtest, index) => (
            <div className="docsfarm-subtest" key={`${subtest.kind || 'subtest'}-${subtest.model || subtest.name}-${index}`}>
              <EntranceTestResultHeader item={subtest} compact />
              <TestMessageGrid item={subtest} />
            </div>
          ))}
        </div>
      ) : (
        <TestMessageGrid item={item} />
      )}
    </Card>
  );
}

function EntranceTestResultHeader({ item, compact = false }: { item: EntranceTestResult; compact?: boolean }) {
  return (
    <div className="test-result-header">
      <Tag color={item.ok ? 'green' : 'red'}>{item.ok ? '通过' : '失败'}</Tag>
      <Text strong={!compact}>{item.name}</Text>
      {item.unsupported && <Tag color="orange">能力不支持</Tag>}
      {item.kind && <Tag color="grey">{item.kind}</Tag>}
      {item.model && <Tag color="teal">{item.model}</Tag>}
      <Text type="secondary">{item.status ? `HTTP ${item.status}` : ''}</Text>
      {typeof item.latencyMs === 'number' && <Text type="secondary">{item.latencyMs} ms</Text>}
      {item.selectedUpstreamName && <Tag color="blue">{item.selectedUpstreamName}</Tag>}
      {item.proxyUrlSet && <Tag color="orange">{item.proxyUrl || '代理已配置'}</Tag>}
    </div>
  );
}

function TestMessageGrid({ item }: { item: EntranceTestResult }) {
  return (
    <div className="test-message-section">
      {item.diagnosis && <Banner className="test-diagnosis" type="warning" closeIcon={null} description={item.diagnosis} />}
      <div className="test-message-grid">
        <div className="test-message-block">
          <Text strong>请求消息</Text>
          <pre>{formatTestMessage(item.request ?? '能力校验未通过，未调用上游。')}</pre>
        </div>
        <div className="test-message-block">
          <Text strong>回应消息</Text>
          <pre>{formatTestMessage(testResponseMessage(item))}</pre>
        </div>
      </div>
    </div>
  );
}

const ENTRANCE_TEST_ACTIONS: Array<{ action: EntranceTestAction; label: string }> = [
  { action: 'chat', label: '测试问答' },
  { action: 'non_stream', label: '测试非流式' },
  { action: 'stream', label: '测试流式' },
  { action: 'vision', label: '测试多模态' },
  { action: 'translation', label: '测试翻译' },
  { action: 'docsfarm', label: '测试文档农场' }
];

function testResponseMessage(item: EntranceTestResult) {
  return item.error || item.response || '无返回内容';
}

function formatTestMessage(value: unknown) {
  if (value === null || value === undefined) {
    return '无内容';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  return JSON.stringify(value, null, 2);
}

function entranceTestTargetUpstreams(base: BaseURL, upstreams: Upstream[], clusters: UpstreamCluster[]) {
  if (baseProtocol(base) !== 'openai_http') return [];
  const targetType = base.defaultUpstreamTargetType || 'upstream';
  if (targetType === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.defaultUpstreamClusterId);
    const members = normalizeClusterMembers(cluster?.members || []).filter((member) => member.status === 'enabled');
    return members
      .map((member) => upstreams.find((upstream) => upstream.id === member.upstreamId))
      .filter((upstream): upstream is Upstream => Boolean(upstream && upstream.status === 'enabled' && upstreamProtocol(upstream) === 'openai_http'));
  }
  const upstream = upstreams.find((item) => item.id === base.defaultUpstreamId);
  return upstream && upstream.status === 'enabled' && upstreamProtocol(upstream) === 'openai_http' ? [upstream] : [];
}

function entranceTestActionsForTargets(upstreams: Upstream[]): EntranceTestAction[] {
  if (upstreams.length === 0) return [];
  const actionSets = upstreams.map((upstream) => new Set(entranceTestActionsForUpstream(upstream)));
  return ENTRANCE_TEST_ACTIONS.map((item) => item.action).filter((action) => actionSets.every((set) => set.has(action)));
}

function entranceTestActionsForUpstream(upstream: Upstream): EntranceTestAction[] {
  const caps = upstream.capabilities || {};
  if (!hasConfiguredCapabilities(caps)) return [];
  const actions: EntranceTestAction[] = [];
  if (caps.chat) actions.push('chat', 'non_stream');
  if (caps.chat && caps.stream) actions.push('stream');
  if (caps.vision) actions.push('vision');
  if (caps.translation) actions.push('translation');
  if (caps.docsfarm) actions.push('docsfarm');
  return actions;
}

function hasConfiguredCapabilities(caps?: UpstreamCapabilities) {
  return Boolean(caps && (caps.chat || caps.stream || caps.vision || caps.thinking || caps.translation || caps.docsfarm));
}

const UPSTREAM_CAPABILITY_OPTIONS = [
  { key: 'chat', label: '问答', color: 'blue' },
  { key: 'stream', label: '流式', color: 'cyan' },
  { key: 'vision', label: '多模态', color: 'violet' },
  { key: 'thinking', label: '思考模式', color: 'orange' },
  { key: 'translation', label: '翻译服务', color: 'green' },
  { key: 'docsfarm', label: '文档农场', color: 'teal' }
] as const;

const REALTIME_CAPABILITY_OPTIONS = [
  { key: 'websocket', label: 'Realtime WebSocket' },
  { key: 'translations', label: '实时语音翻译' },
  { key: 'transcription', label: '实时语音转写' },
  { key: 'webrtc', label: '浏览器 WebRTC' },
  { key: 'clientSecrets', label: '临时凭证' },
  { key: 'calls', label: 'Calls 控制' },
  { key: 'sideband', label: 'Sideband 控制' },
  { key: 'sip', label: '电话 SIP' }
] as const;

function hasRealtimeCapabilities(caps?: RealtimeCapabilities) {
  return Boolean(caps && REALTIME_CAPABILITY_OPTIONS.some((option) => Boolean(caps[option.key])));
}

function validateRealtimeCapabilities(caps?: RealtimeCapabilities) {
  if (!hasRealtimeCapabilities(caps)) throw new Error('请至少选择一个 Realtime 能力');
  if (caps?.translations && !caps.websocket) throw new Error('实时语音翻译能力依赖 Realtime WebSocket');
  if (caps?.transcription && !caps.websocket) throw new Error('实时语音转写能力依赖 Realtime WebSocket');
  if ((caps?.webrtc || caps?.sip || caps?.sideband) && !caps.calls) throw new Error('WebRTC、SIP 和 Sideband 能力依赖 Calls 控制');
}

function RealtimeCapabilityPicker({ value, onChange }: { value?: RealtimeCapabilities; onChange: (value: RealtimeCapabilities) => void }) {
  const current = value || {};
  return (
    <div className="capability-picker">
      {REALTIME_CAPABILITY_OPTIONS.map((option) => (
        <label className="capability-option" key={option.key}>
          <input
            type="checkbox"
            checked={Boolean(current[option.key])}
            onChange={(event) => onChange({ ...current, [option.key]: event.currentTarget.checked })}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function renderRealtimeCapabilities(caps?: RealtimeCapabilities) {
  if (!hasRealtimeCapabilities(caps)) return <Tag color="red">未配置</Tag>;
  return <div className="tag-list">{REALTIME_CAPABILITY_OPTIONS.filter((option) => caps?.[option.key]).map((option) => <Tag color="green" key={option.key}>{option.label}</Tag>)}</div>;
}

function UpstreamCapabilityPicker({ value, onChange }: { value?: UpstreamCapabilities; onChange: (value: UpstreamCapabilities) => void }) {
  const current = value || {};
  return (
    <div className="capability-picker">
      {UPSTREAM_CAPABILITY_OPTIONS.map((option) => (
        <label className="capability-option" key={option.key}>
          <input
            type="checkbox"
            checked={Boolean(current[option.key])}
            onChange={(event) => onChange({ ...current, [option.key]: event.currentTarget.checked })}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function renderUpstreamCapabilityTags(upstream: Partial<Upstream>) {
  if (isWebSocketUpstream(upstream)) return <Tag color="grey">不适用</Tag>;
  if (isRealtimeUpstream(upstream)) {
    const caps = upstream.realtimeCapabilities;
    if (!hasRealtimeCapabilities(caps)) return <Tag color="red">未配置</Tag>;
    return <div className="tag-list">{REALTIME_CAPABILITY_OPTIONS.filter((option) => caps?.[option.key]).map((option) => <Tag color="green" key={option.key}>{option.label}</Tag>)}</div>;
  }
  const caps = upstream.capabilities;
  if (!hasConfiguredCapabilities(caps)) return <Tag color="red">未配置</Tag>;
  return (
    <div className="tag-list">
      {UPSTREAM_CAPABILITY_OPTIONS.filter((option) => caps?.[option.key]).map((option) => (
        <Tag color={option.color} key={option.key}>
          {option.label}
        </Tag>
      ))}
    </div>
  );
}

function renderUpstreamProxyStatus(upstream: Upstream) {
  if (!upstream.proxyUrlSet) return <Tag color="grey">直连</Tag>;
  return (
    <Space>
      <Tag color="orange">代理</Tag>
      {upstream.proxyUrl && <Text type="secondary">{upstream.proxyUrl}</Text>}
    </Space>
  );
}

function formatProxyUsage(value: any) {
  if (!value?.proxyUrlSet) return '直连';
  return value.proxyUrl ? `代理 ${value.proxyUrl}` : '代理已配置';
}

function formatHealthResult(value: any) {
  const status = value?.protocol === 'websocket'
    ? (value?.ok ? 'WebSocket 配置正常' : 'WebSocket 配置异常')
    : value?.protocol === 'openai_realtime'
      ? (value?.ok ? `Realtime HTTP/WSS 正常${value?.sipConfigured ? '，SIP 配置完整' : '，SIP 配置不完整'}` : 'Realtime 配置异常')
      : value?.status ? `HTTP ${value.status}` : value?.ok ? '正常' : '异常';
  const latency = typeof value?.latencyMs === 'number' ? `${value.latencyMs} ms` : '-';
  const error = value?.error ? ` · ${value.error}` : '';
  return `${status} · ${formatProxyUsage(value)} · ${latency}${error}`;
}

function UpstreamDetailModal({ upstream, computeServers, onClose }: { upstream: Upstream; computeServers: ComputeServer[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const computeServer = computeServers.find((server) => server.uuid === upstream.computeServerUuid);
  const rotateReceiver = useMutation({
    mutationFn: () => api.rotateRealtimeWebhookReceiver(upstream.id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['upstreams'] });
      window.prompt('新的上游 Webhook Receiver URL 已生效，旧地址立即失效：', result.realtimeWebhookReceiverUrl || '');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  return (
    <Modal title={`上游详情：${upstream.name}`} visible onCancel={onClose} footer={null} width={760}>
      <div className="detail-grid">
        <DetailItem label="名称">{upstream.name}</DetailItem>
        <DetailItem label="转发协议">{upstreamProtocolTag(upstream)}</DetailItem>
        <DetailItem label="上游地址">
          <CopyableURL value={upstream.baseUrl} />
        </DetailItem>
        <DetailItem label="上游密钥">
          <Tag color={upstream.apiKeySet ? 'green' : 'red'}>{upstream.apiKeySet ? '已设置' : '未设置'}</Tag>
        </DetailItem>
        <DetailItem label="代理服务器">{renderUpstreamProxyStatus(upstream)}</DetailItem>
        <DetailItem label="能力">{renderUpstreamCapabilityTags(upstream)}</DetailItem>
        {isRealtimeUpstream(upstream) && (
          <>
            <DetailItem label="Realtime WebSocket"><CopyableURL value={upstream.realtimeWebSocketUrl || ''} /></DetailItem>
            <DetailItem label="上游 SIP">{upstream.realtimeCapabilities?.sip ? <code>sips:{upstream.realtimeSipUser}@{upstream.realtimeSipDomain}:{upstream.realtimeSipPort || 5061};transport=tls</code> : <Text type="secondary">未启用</Text>}</DetailItem>
            <DetailItem label="SIP TLS SNI">{upstream.realtimeSipSni || '-'}</DetailItem>
            <DetailItem label="上游 Webhook 验签"><Tag color={upstream.realtimeWebhookSecretSet ? 'green' : 'grey'}>{upstream.realtimeWebhookSecretSet ? '已设置' : '未设置'}</Tag></DetailItem>
            <DetailItem label="AIgate Webhook Receiver">
              <div>
                <CopyableURL value={upstream.realtimeWebhookReceiverUrl || ''} />
                <Button size="small" loading={rotateReceiver.isPending} onClick={() => rotateReceiver.mutate()}>轮换 Receiver URL</Button>
              </div>
            </DetailItem>
          </>
        )}
        <DetailItem label="模型列表">
          {(upstream.models || []).length > 0 ? (
            <div className="tag-list">
              {(upstream.models || []).map((model) => (
                <Tag color="blue" key={model}>
                  {model}
                </Tag>
              ))}
            </div>
          ) : (
            <Text type="secondary">未配置</Text>
          )}
        </DetailItem>
        <DetailItem label="算力节点">{computeServer?.displayName || upstream.computeServerUuid || '-'}</DetailItem>
        <DetailItem label="超时时间">{upstream.timeoutSeconds} 秒</DetailItem>
        <DetailItem label="状态">
          <Tag color={upstream.status === 'enabled' ? 'green' : 'grey'}>{upstream.status === 'enabled' ? '启用' : '停用'}</Tag>
        </DetailItem>
      </div>
    </Modal>
  );
}

function KeysModal({ base, onClose }: { base: BaseURL; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('default');
  const [secret, setSecret] = useState('');
  const keys = useQuery({ queryKey: ['keys', base.id], queryFn: () => api.keys(base.id) });
  const create = useMutation({
    mutationFn: () => api.createKey(base.id, name),
    onSuccess: (data) => {
      setSecret(data.secret);
      queryClient.invalidateQueries({ queryKey: ['keys', base.id] });
    }
  });
  const revoke = useMutation({
    mutationFn: (keyId: string) => api.revokeKey(base.id, keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keys', base.id] })
  });
  const rotate = useMutation({
    mutationFn: (keyId: string) => api.rotateKey(base.id, keyId),
    onSuccess: (data) => {
      setSecret(data.secret);
      queryClient.invalidateQueries({ queryKey: ['keys', base.id] });
    }
  });
  return (
    <Modal title={`${base.name} 的客户端密钥`} visible onCancel={onClose} footer={null} width={760}>
      <div className="inline-form">
        <Input value={name} onChange={setName} placeholder="密钥名称" />
        <Button theme="solid" type="primary" onClick={() => create.mutate()}>
          创建密钥
        </Button>
      </div>
      {secret && <Banner type="success" closeIcon={null} description={`密钥只显示一次：${secret}`} />}
      <Table
        rowKey="id"
        dataSource={keys.data || []}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '前缀', dataIndex: 'prefix' },
          { title: '状态', dataIndex: 'status' },
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                <Button size="small" onClick={() => rotate.mutate(row.id)}>
                  轮换
                </Button>
                <Button size="small" type="danger" onClick={() => revoke.mutate(row.id)}>
                  吊销
                </Button>
              </Space>
            )
          }
        ]}
      />
    </Modal>
  );
}

function Upstreams() {
  const queryClient = useQueryClient();
  const upstreams = useQuery({ queryKey: ['upstreams'], queryFn: api.upstreams });
  const clusters = useQuery({ queryKey: ['upstreamClusters'], queryFn: api.upstreamClusters });
  const computeServers = useQuery({ queryKey: ['computeServers'], queryFn: api.computeServers });
  const [draft, setDraft] = useState<Partial<Upstream>>({ status: 'enabled', timeoutSeconds: 120, protocol: 'openai_http' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Upstream | null>(null);
  const [detailUpstream, setDetailUpstream] = useState<Upstream | null>(null);
  const [clusterDraft, setClusterDraft] = useState<Partial<UpstreamCluster>>({ status: 'enabled', policy: 'random', members: [] });
  const [clusterOpen, setClusterOpen] = useState(false);
  const [editingCluster, setEditingCluster] = useState<UpstreamCluster | null>(null);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [clusterHealthResult, setClusterHealthResult] = useState<any>(null);
  const closeEditor = () => {
    setCreateOpen(false);
    setEditing(null);
    setDraft({ status: 'enabled', timeoutSeconds: 120, protocol: 'openai_http' });
  };
  const closeClusterEditor = () => {
    setClusterOpen(false);
    setEditingCluster(null);
    setMemberToAdd('');
    setClusterDraft({ status: 'enabled', policy: 'random', members: [] });
  };
  const openCreate = () => {
    setEditing(null);
    setDraft({ status: 'enabled', timeoutSeconds: 120, protocol: 'openai_http' });
    setCreateOpen(true);
  };
  const openCreateCluster = () => {
    setEditingCluster(null);
    setMemberToAdd('');
    setClusterDraft({ status: 'enabled', policy: 'random', members: [] });
    setClusterOpen(true);
  };
  const openEdit = (upstream: Upstream) => {
    setEditing(upstream);
    setDraft({ ...upstream, protocol: upstreamProtocol(upstream), apiKey: '', proxyUrlClear: false });
    setCreateOpen(true);
  };
  const openEditCluster = (cluster: UpstreamCluster) => {
    setEditingCluster(cluster);
    setMemberToAdd('');
    setClusterDraft({ ...cluster, members: normalizeClusterMembers(cluster.members || []) });
    setClusterOpen(true);
  };
  const create = useMutation({
    mutationFn: () => {
      validateUpstreamDraftForSave(draft);
      if (upstreamProtocol(draft) === 'openai_http' && !hasConfiguredCapabilities(draft.capabilities)) throw new Error('请至少选择一个上游能力标签');
      const body = normalizeUpstreamDraftForSave(draft, editing);
      return editing ? api.updateUpstream(editing.id, body) : api.createUpstream(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstreams'] });
      closeEditor();
      Toast.success(editing ? '上游服务已更新' : '上游服务已创建');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const saveCluster = useMutation({
    mutationFn: () => {
      const body = normalizeClusterDraft(clusterDraft);
      return editingCluster ? api.updateUpstreamCluster(editingCluster.id, body) : api.createUpstreamCluster(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstreamClusters'] });
      queryClient.invalidateQueries({ queryKey: ['baseURLs'] });
      closeClusterEditor();
      Toast.success(editingCluster ? '上游集群已更新' : '上游集群已创建');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const remove = useMutation({
    mutationFn: api.deleteUpstream,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstreams'] });
      Toast.success('上游服务已删除');
    }
  });
  const removeCluster = useMutation({
    mutationFn: api.deleteUpstreamCluster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstreamClusters'] });
      queryClient.invalidateQueries({ queryKey: ['baseURLs'] });
      Toast.success('上游集群已删除');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const health = useMutation({ mutationFn: api.healthUpstream, onSuccess: (data) => Toast.info(formatHealthResult(data)) });
  const clusterHealth = useMutation({
    mutationFn: api.healthUpstreamCluster,
    onSuccess: (data) => setClusterHealthResult(data),
    onError: (error: Error) => Toast.error(error.message)
  });
  const draftMembers = normalizeClusterMembers(clusterDraft.members || []);
  const availableMembers = (upstreams.data || []).filter((item) => !draftMembers.some((member) => member.upstreamId === item.id));
  const realtimeDraft = isRealtimeUpstream(draft);
  return (
    <section>
      <PageTitle title="上游服务" subtitle="配置 OpenAI HTTP、WebSocket 或完整 Realtime 上游服务供应方。" />
      <div className="toolbar">
        <Button theme="solid" type="primary" icon={<IconPlus />} onClick={openCreate}>
          新建上游
        </Button>
        <Button icon={<IconPlus />} onClick={openCreateCluster}>
          新建集群
        </Button>
      </div>
      <Modal
        title={editing ? '编辑上游服务' : '新建上游服务'}
        visible={createOpen}
        onCancel={closeEditor}
        width={720}
        footer={
          <Space>
            <Button onClick={closeEditor}>取消</Button>
            <Button theme="solid" type="primary" loading={create.isPending} onClick={() => create.mutate()}>
              {editing ? '保存' : '创建'}
            </Button>
          </Space>
        }
      >
        <div className="modal-form">
          <label>
            <Text strong>名称</Text>
            <Input placeholder="例如：OpenAI 主账号" value={draft.name || ''} onChange={(name) => setDraft({ ...draft, name })} />
          </label>
          <label>
            <Text strong>转发协议</Text>
            <Select
              value={upstreamProtocol(draft)}
              onChange={(protocol) => setDraft(draftWithUpstreamProtocol(draft, protocol as Upstream['protocol']))}
              className="select"
            >
              <Select.Option value="openai_http">OpenAI HTTP</Select.Option>
              <Select.Option value="websocket">WebSocket</Select.Option>
              <Select.Option value="openai_realtime">OpenAI Realtime（WebSocket / WebRTC / SIP）</Select.Option>
            </Select>
          </label>
          <label>
            <Text strong>Base URL</Text>
            <Input placeholder={realtimeDraft ? '必须是 provider 的 HTTPS API 根地址（通常以 /v1 结尾）' : '支持 HTTP、HTTPS、WS、WSS 协议'} value={draft.baseUrl || ''} onChange={(baseUrl) => setDraft({ ...draft, baseUrl })} />
          </label>
          <label>
            <Text strong>API Key</Text>
            <Input mode="password" placeholder={editing ? '留空表示不修改原密钥' : '上游服务密钥'} value={draft.apiKey || ''} onChange={(apiKey) => setDraft({ ...draft, apiKey })} />
          </label>
          <label>
            <Text strong>代理服务器</Text>
            <Input
              placeholder={editing ? '留空表示不修改；例如：http://127.0.0.1:7890' : '可选，例如：http://127.0.0.1:7890'}
              value={draft.proxyUrl || ''}
              onChange={(proxyUrl) =>
                setDraft({
                  ...draft,
                  proxyUrl,
                  proxyUrlClear: Boolean(editing?.proxyUrlSet && proxyUrl.trim() === '')
                })
              }
            />
          </label>
          <label>
            <Text strong>模型列表</Text>
            <Input placeholder="多个模型用英文逗号分隔" value={(draft.models || []).join(',')} onChange={(models) => setDraft({ ...draft, models: models.split(',').map((m) => m.trim()).filter(Boolean) })} />
          </label>
          {upstreamProtocol(draft) === 'openai_http' && (
            <div>
              <Text strong>能力标签</Text>
              <UpstreamCapabilityPicker value={draft.capabilities} onChange={(capabilities) => setDraft({ ...draft, capabilities })} />
            </div>
          )}
          {realtimeDraft && (
            <>
              <div>
                <Text strong>Realtime 能力</Text>
                <RealtimeCapabilityPicker value={draft.realtimeCapabilities} onChange={(realtimeCapabilities) => setDraft({ ...draft, realtimeCapabilities })} />
              </div>
              {draft.realtimeCapabilities?.sip && (
                <>
                  <div className="two-column-form">
                    <label>
                      <Text strong>上游 SIP 域名</Text>
                      <Input placeholder="sip.api.openai.com" value={draft.realtimeSipDomain || ''} onChange={(realtimeSipDomain) => setDraft({ ...draft, realtimeSipDomain })} />
                    </label>
                    <label>
                      <Text strong>上游 SIP TLS 端口</Text>
                      <Input placeholder="5061" value={String(draft.realtimeSipPort || 5061)} onChange={(value) => setDraft({ ...draft, realtimeSipPort: Number(value) || 5061, realtimeSipTransport: 'tls' })} />
                    </label>
                  </div>
                  <div className="two-column-form">
                    <label>
                      <Text strong>SIP Project / User</Text>
                      <Input placeholder="proj_xxx" value={draft.realtimeSipUser || ''} onChange={(realtimeSipUser) => setDraft({ ...draft, realtimeSipUser })} />
                    </label>
                    <label>
                      <Text strong>TLS SNI</Text>
                      <Input placeholder="默认使用 SIP 域名" value={draft.realtimeSipSni || ''} onChange={(realtimeSipSni) => setDraft({ ...draft, realtimeSipSni })} />
                    </label>
                  </div>
                  <label>
                    <Text strong>上游 Webhook 验签密钥</Text>
                    <Input mode="password" placeholder={editing?.realtimeWebhookSecretSet ? '留空表示不修改原密钥' : '用于验证上游 realtime.call.incoming 事件'} value={draft.realtimeWebhookSigningSecret || ''} onChange={(realtimeWebhookSigningSecret) => setDraft({ ...draft, realtimeWebhookSigningSecret })} />
                  </label>
                  <Banner type="info" closeIcon={null} description="创建后，详情页会显示应配置到上游平台的 AIgate Webhook Receiver URL；接收令牌可独立轮换。" />
                </>
              )}
            </>
          )}
          <label>
            <Text strong>关联算力节点</Text>
            <Select value={draft.computeServerUuid || ''} onChange={(computeServerUuid) => setDraft({ ...draft, computeServerUuid: String(computeServerUuid) || undefined })} className="select" placeholder="可选，用于错峰编排">
              <Select.Option value="">不关联</Select.Option>
              {(computeServers.data || []).map((server) => (
                <Select.Option value={server.uuid} key={server.uuid}>
                  {server.displayName}
                </Select.Option>
              ))}
            </Select>
          </label>
          <label>
            <Text strong>超时时间（秒）</Text>
            <Input placeholder="120" value={String(draft.timeoutSeconds || 120)} onChange={(value) => setDraft({ ...draft, timeoutSeconds: Number(value) || 120 })} />
          </label>
          <label>
            <Text strong>状态</Text>
            <Select value={draft.status} onChange={(status) => setDraft({ ...draft, status: String(status) })} className="select">
              <Select.Option value="enabled">启用</Select.Option>
              <Select.Option value="disabled">停用</Select.Option>
            </Select>
          </label>
        </div>
      </Modal>
      <Modal
        title={editingCluster ? '编辑上游集群' : '新建上游集群'}
        visible={clusterOpen}
        onCancel={closeClusterEditor}
        width={820}
        footer={
          <Space>
            <Button onClick={closeClusterEditor}>取消</Button>
            <Button theme="solid" type="primary" loading={saveCluster.isPending} onClick={() => saveCluster.mutate()}>
              {editingCluster ? '保存' : '创建'}
            </Button>
          </Space>
        }
      >
        <div className="modal-form">
          <label>
            <Text strong>集群名称</Text>
            <Input placeholder="例如：生产上游池" value={clusterDraft.name || ''} onChange={(name) => setClusterDraft({ ...clusterDraft, name })} />
          </label>
          <label>
            <Text strong>描述</Text>
            <Input placeholder="可选" value={clusterDraft.description || ''} onChange={(description) => setClusterDraft({ ...clusterDraft, description })} />
          </label>
          <div className="two-column-form">
            <label>
              <Text strong>选择策略</Text>
              <Select value={clusterDraft.policy || 'random'} onChange={(policy) => setClusterDraft({ ...clusterDraft, policy: policy as UpstreamCluster['policy'] })} className="select">
                <Select.Option value="random">随机选择</Select.Option>
                <Select.Option value="round_robin">轮流选择</Select.Option>
                <Select.Option value="least_connections">最少连接数</Select.Option>
              </Select>
            </label>
            <label>
              <Text strong>状态</Text>
              <Select value={clusterDraft.status || 'enabled'} onChange={(status) => setClusterDraft({ ...clusterDraft, status: String(status) })} className="select">
                <Select.Option value="enabled">启用</Select.Option>
                <Select.Option value="disabled">停用</Select.Option>
              </Select>
            </label>
          </div>
          <div className="cluster-member-editor">
            <div className="inline-form">
              <Select value={memberToAdd} onChange={(value) => setMemberToAdd(String(value))} placeholder="选择上游服务" className="select">
                {availableMembers.map((u) => (
                  <Select.Option value={u.id} key={u.id}>
                    {u.name}
                  </Select.Option>
                ))}
              </Select>
              <Button
                icon={<IconPlus />}
                disabled={!memberToAdd}
                onClick={() => {
                  if (!memberToAdd) return;
                  setClusterDraft({
                    ...clusterDraft,
                    members: normalizeClusterMembers([...draftMembers, { upstreamId: memberToAdd, status: 'enabled', position: draftMembers.length }])
                  });
                  setMemberToAdd('');
                }}
              >
                添加成员
              </Button>
            </div>
            <Table
              rowKey="upstreamId"
              dataSource={draftMembers}
              pagination={false}
              columns={[
                {
                  title: '上游服务',
                  render: (_, member) => upstreams.data?.find((u) => u.id === member.upstreamId)?.name || member.upstreamId
                },
                {
                  title: '状态',
                  render: (_, member) => (
                    <Select
                      value={member.status}
                      onChange={(status) =>
                        setClusterDraft({
                          ...clusterDraft,
                          members: draftMembers.map((item) => (item.upstreamId === member.upstreamId ? { ...item, status: String(status) } : item))
                        })
                      }
                      style={{ width: 120 }}
                    >
                      <Select.Option value="enabled">启用</Select.Option>
                      <Select.Option value="disabled">停用</Select.Option>
                    </Select>
                  )
                },
                {
                  title: '顺序',
                  render: (_, member, index) => (
                    <Space>
                      <Button size="small" disabled={index === 0} onClick={() => setClusterDraft({ ...clusterDraft, members: moveClusterMember(draftMembers, index, index - 1) })}>
                        上移
                      </Button>
                      <Button size="small" disabled={index === draftMembers.length - 1} onClick={() => setClusterDraft({ ...clusterDraft, members: moveClusterMember(draftMembers, index, index + 1) })}>
                        下移
                      </Button>
                    </Space>
                  )
                },
                {
                  title: '操作',
                  dataIndex: 'upstreamId',
                  render: (upstreamId, member) => {
                    const actionMember = resolveClusterMember(upstreamId, member, draftMembers);
                    if (!actionMember) return null;
                    return (
                      <ActionButtonGroup>
                        <ActionIconButton
                          label="移除"
                          type="danger"
                          icon={<IconDelete />}
                          onClick={() => setClusterDraft({ ...clusterDraft, members: normalizeClusterMembers(draftMembers.filter((item) => item.upstreamId !== actionMember.upstreamId)) })}
                        />
                      </ActionButtonGroup>
                    );
                  }
                }
              ]}
            />
          </div>
        </div>
      </Modal>
      <Table
        rowKey="id"
        dataSource={upstreams.data || []}
        loading={upstreams.isLoading}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '协议', render: (_, row) => upstreamProtocolTag(row) },
          { title: '上游地址', dataIndex: 'baseUrl' },
          { title: '上游密钥', render: (_, row) => <Tag color={row.apiKeySet ? 'green' : 'red'}>{row.apiKeySet ? '已设置' : '未设置'}</Tag> },
          { title: '代理', render: (_, row) => renderUpstreamProxyStatus(row) },
          { title: '能力', render: (_, row) => renderUpstreamCapabilityTags(row) },
          { title: '算力节点', render: (_, row) => computeServers.data?.find((server) => server.uuid === row.computeServerUuid)?.displayName || row.computeServerUuid || '-' },
          { title: '超时', dataIndex: 'timeoutSeconds' },
          { title: '状态', render: (_, row) => <Tag color={row.status === 'enabled' ? 'green' : 'grey'}>{row.status === 'enabled' ? '启用' : '停用'}</Tag> },
          {
            title: '操作',
            dataIndex: 'id',
            render: (id, row) => {
              const actionUpstream = resolveTableRecord<Upstream>(id, row, upstreams.data || []);
              if (!actionUpstream) return null;
              return (
                <ActionButtonGroup>
                  <ActionIconButton label="详情" icon={<IconEyeOpened />} onClick={() => setDetailUpstream(actionUpstream)} />
                  <ActionIconButton label="编辑" icon={<IconEdit />} onClick={() => openEdit(actionUpstream)} />
                  <ActionIconButton label="健康检查" icon={<IconSetting />} loading={health.isPending} onClick={() => health.mutate(actionUpstream.id)} />
                  <ActionIconButton
                    label="删除"
                    type="danger"
                    icon={<IconDelete />}
                    loading={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`确认删除上游服务“${actionUpstream.name}”？`)) remove.mutate(actionUpstream.id);
                    }}
                  />
                </ActionButtonGroup>
              );
            }
          }
        ]}
      />
      {detailUpstream && <UpstreamDetailModal upstream={detailUpstream} computeServers={computeServers.data || []} onClose={() => setDetailUpstream(null)} />}
      <div className="overview-section-title">
        <div>
          <Title heading={5}>上游集群</Title>
          <Text type="secondary">入口可以选择集群作为默认上游，转发时按策略选择成员服务。</Text>
        </div>
      </div>
      <Table
        rowKey="id"
        dataSource={clusters.data || []}
        loading={clusters.isLoading}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '策略', render: (_, row) => <Tag color="violet">{clusterPolicyLabel(row.policy)}</Tag> },
          {
            title: '成员',
            render: (_, row) => (
              <div className="tag-list">
                {normalizeClusterMembers(row.members || []).map((member) => (
                  <Tag key={member.upstreamId} color={member.status === 'enabled' ? 'blue' : 'grey'}>
                    {upstreams.data?.find((u) => u.id === member.upstreamId)?.name || member.upstreamId}
                  </Tag>
                ))}
                {(row.members || []).length === 0 && '-'}
              </div>
            )
          },
          { title: '状态', render: (_, row) => <Tag color={row.status === 'enabled' ? 'green' : 'grey'}>{row.status === 'enabled' ? '启用' : '停用'}</Tag> },
          {
            title: '操作',
            dataIndex: 'id',
            render: (id, row) => {
              const actionCluster = resolveTableRecord<UpstreamCluster>(id, row, clusters.data || []);
              if (!actionCluster) return null;
              return (
                <ActionButtonGroup>
                  <ActionIconButton label="编辑" icon={<IconEdit />} onClick={() => openEditCluster(actionCluster)} />
                  <ActionIconButton label="健康检查" icon={<IconSetting />} loading={clusterHealth.isPending} onClick={() => clusterHealth.mutate(actionCluster.id)} />
                  <ActionIconButton
                    label="删除"
                    type="danger"
                    icon={<IconDelete />}
                    loading={removeCluster.isPending}
                    onClick={() => {
                      if (window.confirm(`确认删除上游集群“${actionCluster.name}”？`)) removeCluster.mutate(actionCluster.id);
                    }}
                  />
                </ActionButtonGroup>
              );
            }
          }
        ]}
      />
      {clusterHealthResult && <ClusterHealthModal result={clusterHealthResult} onClose={() => setClusterHealthResult(null)} />}
    </section>
  );
}

function normalizeClusterMembers(members: UpstreamClusterMember[]): UpstreamClusterMember[] {
  return members.map((member, index) => ({ ...member, status: member.status || 'enabled', position: index }));
}

function normalizeModelOptions(models?: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  (models || []).forEach((raw) => {
    const model = raw.trim();
    if (!model || seen.has(model)) return;
    seen.add(model);
    out.push(model);
  });
  return out;
}

function intersectModelOptions(modelLists: string[][]): string[] {
  if (modelLists.length === 0) return [];
  const otherSets = modelLists.slice(1).map((models) => new Set(models));
  return modelLists[0].filter((model) => otherSets.every((set) => set.has(model)));
}

function clusterModelOptions(cluster: UpstreamCluster | undefined, upstreams: Upstream[]): string[] {
  if (!cluster || cluster.status !== 'enabled') return [];
  const upstreamById = new Map(upstreams.map((upstream) => [upstream.id, upstream]));
  const members = normalizeClusterMembers(cluster.members || []).filter((member) => member.status === 'enabled');
  const memberModelLists = members
    .map((member) => upstreamById.get(member.upstreamId))
    .filter((upstream): upstream is Upstream => Boolean(upstream) && upstream.status === 'enabled' && upstreamProtocol(upstream) === 'openai_http')
    .map((upstream) => normalizeModelOptions(upstream.models));
  return intersectModelOptions(memberModelLists);
}

function stressTargetModelOptions(
  targetType: StressTestTargetType,
  targetId: string,
  baseURLs: BaseURL[],
  upstreams: Upstream[],
  clusters: UpstreamCluster[]
): { models: string[]; scope: string } {
  if (targetType === 'upstream') {
    const upstream = upstreams.find((item) => item.id === targetId);
    if (upstream && upstreamProtocol(upstream) !== 'openai_http') {
      return {
        models: [],
        scope: `非 HTTP 上游不支持此压测：${upstream.name}`
      };
    }
    return {
      models: normalizeModelOptions(upstream?.models),
      scope: upstream ? `来自上游：${upstream.name}` : ''
    };
  }
  const base = baseURLs.find((item) => item.id === targetId);
  if (!base) return { models: [], scope: '' };
  if (baseTargetType(base) === 'upstream_cluster') {
    const cluster = clusters.find((item) => item.id === base.defaultUpstreamClusterId);
    return {
      models: clusterModelOptions(cluster, upstreams),
      scope: cluster ? `来自集群交集：${cluster.name}` : ''
    };
  }
  const upstream = upstreams.find((item) => item.id === base.defaultUpstreamId);
  if (upstream && upstreamProtocol(upstream) !== 'openai_http') {
    return {
      models: [],
      scope: `默认上游不是 OpenAI HTTP：${upstream.name}`
    };
  }
  return {
    models: normalizeModelOptions(upstream?.models),
    scope: upstream ? `来自默认上游：${upstream.name}` : ''
  };
}

function stressTranslationTargetUpstreams(
  targetType: StressTestTargetType,
  targetId: string,
  baseURLs: BaseURL[],
  upstreams: Upstream[],
  clusters: UpstreamCluster[]
): Upstream[] {
  if (targetType === 'upstream') {
    const upstream = upstreams.find((item) => item.id === targetId);
    return upstream && upstream.status === 'enabled' && upstreamProtocol(upstream) === 'openai_http' ? [upstream] : [];
  }
  const base = baseURLs.find((item) => item.id === targetId);
  return base ? entranceTestTargetUpstreams(base, upstreams, clusters) : [];
}

function StressTesting() {
  const queryClient = useQueryClient();
  const baseURLs = useQuery({ queryKey: ['baseURLs'], queryFn: api.baseURLs });
  const upstreams = useQuery({ queryKey: ['upstreams'], queryFn: api.upstreams });
  const upstreamClusters = useQuery({ queryKey: ['upstreamClusters'], queryFn: api.upstreamClusters });
  const runs = useQuery({ queryKey: ['stressTests'], queryFn: api.stressTests, refetchInterval: 1000 });
  const [targetType, setTargetType] = useState<StressTestTargetType>('base_url');
  const [targetId, setTargetId] = useState('');
  const [endpoint, setEndpoint] = useState<StressTestEndpoint>('chat_completions');
  const [model, setModel] = useState('');
  const [prompt, setPrompt] = useState('请用一句话介绍你自己。');
  const [translationMode, setTranslationMode] = useState(false);
  const [sourceLang, setSourceLang] = useState('auto');
  const [targetLang, setTargetLang] = useState('en');
  const [levelsText, setLevelsText] = useState('1,5,10');
  const [stopMode, setStopMode] = useState<'duration' | 'requests'>('duration');
  const [durationSeconds, setDurationSeconds] = useState(10);
  const [requestCount, setRequestCount] = useState(100);
  const [timeoutSeconds, setTimeoutSeconds] = useState(100);
  const [clientApiKey, setClientApiKey] = useState('');
  const [selectedRunId, setSelectedRunId] = useState('');
  const targets = targetType === 'base_url'
    ? (baseURLs.data || []).filter((item) => baseProtocol(item) === 'openai_http')
    : (upstreams.data || []).filter((item) => upstreamProtocol(item) === 'openai_http');
  const selectedBase = targetType === 'base_url' ? (baseURLs.data || []).find((item) => item.id === targetId) : undefined;
  const history = runs.data || [];
  const activeRun = history.find(isActiveStressRun);
  const selectedRun = history.find((item) => item.id === selectedRunId) || activeRun || history[0];
  const parsedConcurrencyLevels = useMemo(() => parseStressLevels(levelsText), [levelsText]);
  const requestCountConcurrencyWarning = stopMode === 'requests' && requestCount > 0 && parsedConcurrencyLevels.some((level) => level > requestCount);
  const activeLevel = activeRun && activeRun.currentLevelIndex !== undefined ? activeRun.levels[activeRun.currentLevelIndex] : undefined;
  const activeMetrics = activeLevel?.metrics;
  const modelSelection = useMemo(
    () => stressTargetModelOptions(targetType, targetId, baseURLs.data || [], upstreams.data || [], upstreamClusters.data || []),
    [targetType, targetId, baseURLs.data, upstreams.data, upstreamClusters.data]
  );
  const modelOptions = modelSelection.models;
  const translationTargetUpstreams = useMemo(
    () => stressTranslationTargetUpstreams(targetType, targetId, baseURLs.data || [], upstreams.data || [], upstreamClusters.data || []),
    [targetType, targetId, baseURLs.data, upstreams.data, upstreamClusters.data]
  );
  const translationMetadataReady = translationTargetUpstreams.length > 0
    && translationTargetUpstreams.every((upstream) => Boolean(upstream.capabilities?.translation))
    && modelOptions.includes('auto');
  const stressTranslationTargetKey = translationTargetUpstreams.map((upstream) => upstream.id).sort().join(',');
  const translationCapabilities = useQuery({
    queryKey: ['stressTranslationCapabilities', stressTranslationTargetKey],
    queryFn: () => Promise.all(translationTargetUpstreams.map((upstream) => api.translationCapabilities(upstream.id))),
    enabled: endpoint === 'chat_completions' && translationMetadataReady,
    retry: false
  });
  const mergedTranslationCapabilities = useMemo(
    () => mergeTranslationCapabilities(translationCapabilities.data || []),
    [translationCapabilities.data]
  );
  const translationCapabilitiesReady = Boolean(
    translationMetadataReady
    && !translationCapabilities.isError
    && mergedTranslationCapabilities
    && translationCapabilities.data?.length === translationTargetUpstreams.length
  );
  useEffect(() => {
    const first = targets[0];
    if (!targetId && first) {
      setTargetId(first.id);
      return;
    }
    if (targetId && !targets.some((item: any) => item.id === targetId)) {
      setTargetId(first?.id || '');
    }
  }, [targetId, targets]);
  useEffect(() => {
    if (endpoint !== 'chat_completions') return;
    if (translationMode) {
      if (model !== 'auto') setModel('auto');
      return;
    }
    if (modelOptions.length === 0) {
      if (model) setModel('');
      return;
    }
    if (!modelOptions.includes(model)) setModel(modelOptions[0]);
  }, [endpoint, model, modelOptions, translationMode]);
  useEffect(() => {
    if (!mergedTranslationCapabilities) return;
    const sourceCodes = mergedTranslationCapabilities.sourceLanguages.map((language) => language.code);
    const targetCodes = mergedTranslationCapabilities.languages.map((language) => language.code);
    if (!sourceCodes.includes(sourceLang)) setSourceLang(sourceCodes.includes('auto') ? 'auto' : sourceCodes[0] || '');
    if (!targetCodes.includes(targetLang)) setTargetLang(targetCodes.includes('en') ? 'en' : targetCodes[0] || '');
  }, [mergedTranslationCapabilities, sourceLang, targetLang]);
  useEffect(() => {
    if (translationMode && !translationMetadataReady) setTranslationMode(false);
  }, [translationMetadataReady, translationMode]);
  useEffect(() => {
    if (translationMode && translationCapabilities.isError) setTranslationMode(false);
  }, [translationCapabilities.isError, translationMode]);
  const create = useMutation({
    mutationFn: () => {
      const concurrencyLevels = parseStressLevels(levelsText);
      if (!targetId) throw new Error('请选择测试目标');
      if (concurrencyLevels.length === 0) throw new Error('请填写并发档位');
      if (endpoint === 'chat_completions' && !translationMode && !model) throw new Error('请选择测试模型');
      if (endpoint === 'chat_completions' && translationMode && !translationCapabilitiesReady) {
        throw new Error('无法读取目标上游的 auto 翻译能力，不能启动翻译压测');
      }
      if (endpoint === 'chat_completions' && translationMode && (!sourceLang.trim() || !targetLang.trim())) {
        throw new Error('翻译压测需要填写源语言和目标语言');
      }
      return api.createStressTest({
        targetType,
        targetId,
        endpoint,
        model: endpoint === 'chat_completions' ? (translationMode ? 'auto' : model) : undefined,
        prompt: endpoint === 'chat_completions' ? prompt : undefined,
        translationOptions: endpoint === 'chat_completions' && translationMode
          ? { sourceLang: sourceLang.trim(), targetLang: targetLang.trim() }
          : undefined,
        durationSeconds: stopMode === 'duration' ? Number(durationSeconds) || 0 : undefined,
        requestCount: stopMode === 'requests' ? Number(requestCount) || 0 : undefined,
        timeoutSeconds: Number(timeoutSeconds) || 100,
        concurrencyLevels,
        clientApiKey: clientApiKey || undefined
      });
    },
    onSuccess: (run) => {
      setClientApiKey('');
      setSelectedRunId(run.id);
      queryClient.invalidateQueries({ queryKey: ['stressTests'] });
      Toast.success('压力测试已启动');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const cancel = useMutation({
    mutationFn: api.cancelStressTest,
    onSuccess: (run) => {
      setSelectedRunId(run.id);
      queryClient.invalidateQueries({ queryKey: ['stressTests'] });
      Toast.success('压力测试已取消');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const resultChart = useMemo(() => buildStressResultChart(selectedRun), [selectedRun]);
  return (
    <section>
      <PageTitle title="压力测试" subtitle="按入口或上游服务对比不同并发档位下的响应时间、吞吐和错误情况。" />
      <Banner type="warning" closeIcon={null} description="压力测试会向所选目标发送真实请求，请确认上游服务和额度可以承受本次负载。" />
      <Card className="panel stress-config-panel">
        <div className="stress-concurrency-note">
          <Text type="secondary">
            并发档位 N 表示最多 N 个异步 HTTP 请求同时进入目标；每档请求数是该档总请求数，不是每个上游的请求数。
          </Text>
        </div>
        {requestCountConcurrencyWarning && (
          <Banner type="warning" closeIcon={null} description="当前每档请求数小于某些并发档位，这些档位无法达到设定并发峰值。" />
        )}
        <div className="stress-form-grid">
          <label>
            <Text strong>测试对象</Text>
            <Select value={targetType} onChange={(value) => { setTargetType(value as StressTestTargetType); setTargetId(''); setTranslationMode(false); }} className="select">
              <Select.Option value="base_url">入口地址</Select.Option>
              <Select.Option value="upstream">上游服务</Select.Option>
            </Select>
          </label>
          <label>
            <Text strong>{targetType === 'base_url' ? '入口' : '上游'}</Text>
            <Select value={targetId} onChange={(value) => { setTargetId(String(value)); setTranslationMode(false); }} className="select" loading={baseURLs.isLoading || upstreams.isLoading}>
              {targets.map((item: any) => (
                <Select.Option value={item.id} key={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </label>
          <label>
            <Text strong>接口</Text>
            <Select value={endpoint} onChange={(value) => { setEndpoint(value as StressTestEndpoint); setTranslationMode(false); }} className="select">
              <Select.Option value="chat_completions">Chat Completions</Select.Option>
              <Select.Option value="models">Models</Select.Option>
            </Select>
          </label>
          <label>
            <Text strong>并发档位</Text>
            <Input value={levelsText} onChange={setLevelsText} placeholder="1,5,10,20" />
          </label>
          <label>
            <Text strong>停止条件</Text>
            <Select value={stopMode} onChange={(value) => setStopMode(value as 'duration' | 'requests')} className="select">
              <Select.Option value="duration">按时长</Select.Option>
              <Select.Option value="requests">按请求数</Select.Option>
            </Select>
          </label>
          {stopMode === 'duration' ? (
            <label>
              <Text strong>每档时长（秒）</Text>
              <Input value={String(durationSeconds)} onChange={(value) => setDurationSeconds(Number(value) || 0)} />
            </label>
          ) : (
            <label>
              <Text strong>每档请求数</Text>
              <Input value={String(requestCount)} onChange={(value) => setRequestCount(Number(value) || 0)} />
            </label>
          )}
          <label>
            <Text strong>超时（秒）</Text>
            <Input value={String(timeoutSeconds)} onChange={(value) => setTimeoutSeconds(Number(value) || 0)} />
          </label>
          {endpoint === 'chat_completions' && (
            <>
              <label>
                <div className="stress-field-label">
                  <Text strong>模型</Text>
                  {modelSelection.scope && <Text type="tertiary">{modelSelection.scope}</Text>}
                </div>
                {translationMode ? (
                  <Input value="auto" disabled />
                ) : (
                  <Select
                    value={model || undefined}
                    onChange={(value) => setModel(String(value))}
                    className="select"
                    placeholder={modelOptions.length > 0 ? '选择模型' : '当前目标没有可用模型'}
                    loading={upstreams.isLoading || upstreamClusters.isLoading}
                    disabled={modelOptions.length === 0}
                  >
                    {modelOptions.map((item) => (
                      <Select.Option value={item} key={item}>
                        {item}
                      </Select.Option>
                    ))}
                  </Select>
                )}
                {!translationMode && modelOptions.length === 0 && (
                  <Text type="tertiary" className="stress-field-help">
                    请先在上游服务配置模型列表；如果目标是集群，需要所有启用成员都支持同一个模型。
                  </Text>
                )}
              </label>
              <div className="stress-wide-field stress-translation-toggle">
                <Space>
                  <Text strong>翻译请求</Text>
                  <Switch
                    checked={translationMode}
                    disabled={!translationCapabilitiesReady}
                    onChange={(checked) => setTranslationMode(Boolean(checked))}
                  />
                </Space>
                <Text type="tertiary">
                  {!translationMetadataReady
                    ? '目标上游必须勾选“翻译服务”，且模型列表必须共同包含 auto。'
                    : translationCapabilities.isLoading
                      ? '正在读取 /v1/models/auto/capabilities…'
                      : translationCapabilities.isError
                        ? `能力读取失败：${(translationCapabilities.error as Error).message}`
                        : translationCapabilities.data && !mergedTranslationCapabilities
                          ? '目标上游没有共同的 auto 翻译语言。'
                        : '启用后固定发送 model=auto；实际 provider 由 Uni Trans 根据翻译方向决策。'}
                </Text>
              </div>
              {translationMode && mergedTranslationCapabilities && (
                <>
                  <label>
                    <Text strong>源语言</Text>
                    <Select value={sourceLang} onChange={(value) => setSourceLang(String(value))} className="select">
                      {mergedTranslationCapabilities.sourceLanguages.map((language) => (
                        <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>
                      ))}
                    </Select>
                  </label>
                  <label>
                    <Text strong>目标语言</Text>
                    <Select value={targetLang} onChange={(value) => setTargetLang(String(value))} className="select">
                      {mergedTranslationCapabilities.languages.map((language) => (
                        <Select.Option value={language.code} key={language.code}>{language.name} · {language.code}</Select.Option>
                      ))}
                    </Select>
                  </label>
                  {mergedTranslationCapabilities.directionRules.length > 0 && (
                    <Text type="tertiary" className="stress-wide-field translation-direction-rule">
                      方向规则：{mergedTranslationCapabilities.directionRules.join('；')}
                    </Text>
                  )}
                </>
              )}
              <label className="stress-prompt-field">
                <div className="stress-prompt-label">
                  <Text strong>{translationMode ? '待翻译文本' : '提示词'}</Text>
                  <Text type="tertiary">{prompt.length} 字符</Text>
                </div>
                <TextArea
                  value={prompt}
                  onChange={setPrompt}
                  autosize={false}
                  className="stress-prompt-textarea"
                  placeholder={translationMode ? '输入压测使用的待翻译文本。' : '输入压测请求使用的提示词，可粘贴较长文本。'}
                />
              </label>
            </>
          )}
          {selectedBase?.authMode === 'require_key' && (
            <label className="stress-wide-field">
              <Text strong>一次性客户端 Key</Text>
              <Input mode="password" value={clientApiKey} onChange={setClientApiKey} placeholder="sk-..." />
            </label>
          )}
        </div>
        <div className="stress-actions">
          <Button theme="solid" type="primary" icon={<IconActivity />} loading={create.isPending} onClick={() => create.mutate()}>
            启动测试
          </Button>
          {activeRun && (
            <Button type="danger" loading={cancel.isPending} onClick={() => cancel.mutate(activeRun.id)}>
              取消当前测试
            </Button>
          )}
        </div>
      </Card>
      {runs.isError && <Banner type="danger" closeIcon={null} description={(runs.error as Error).message} />}
      {activeRun && (
        <Card className="panel">
          <div className="chart-toolbar">
            <Space>
              <Text strong>当前运行</Text>
              <Tag color={stressStatusColor(activeRun.status)}>{stressStatusLabel(activeRun.status)}</Tag>
              <Text type="secondary">{stressTargetText(activeRun)}</Text>
            </Space>
            <Text type="secondary">
              并发 {activeRun.currentConcurrency || '-'} · 进行中 {activeMetrics?.inFlightCount || 0} · 采样已完成 {activeRun.completedRequests || 0}
            </Text>
          </div>
          <div className="metric-grid">
            <Metric label="当前档位" value={activeRun.currentLevelIndex !== undefined && activeRun.currentLevelIndex >= 0 ? `${activeRun.currentLevelIndex + 1}/${activeRun.levels.length}` : '-'} />
            <Metric label="完成档位" value={`${activeRun.completedLevels || 0}/${activeRun.levels.length}`} />
            <Metric label="工作协程" value={activeMetrics?.workerCount || activeRun.currentConcurrency || '-'} />
            <Metric label="已启动请求" value={activeMetrics?.startedCount || 0} />
            <Metric label="进行中请求" value={activeMetrics?.inFlightCount || 0} />
            <Metric label="峰值进行中" value={activeMetrics?.peakInFlightCount || 0} />
            <Metric label="采样已完成" value={activeRun.completedRequests || 0} />
            <Metric label="启动扩散" value={formatMilliseconds(activeMetrics?.startSpreadMs)} />
            <Metric label="运行状态" value={stressStatusLabel(activeRun.status)} />
          </div>
        </Card>
      )}
      <div className="stress-result-grid">
        <Card className="panel">
          <div className="chart-toolbar">
            <Text strong>最近记录</Text>
            <Text type="secondary">{history.length} 条</Text>
          </div>
          {runs.isLoading && <Text type="secondary">正在加载压力测试记录...</Text>}
          {!runs.isLoading && history.length === 0 && <Text type="secondary">暂无压力测试记录。</Text>}
          {history.length > 0 && (
            <Table
              rowKey="id"
              dataSource={history}
              pagination={false}
              onRow={(row) => ({ onClick: () => setSelectedRunId(row.id) })}
              columns={[
                { title: '目标', render: (_, row) => stressTargetText(row) },
                { title: '状态', render: (_, row) => <Tag color={stressStatusColor(row.status)}>{stressStatusLabel(row.status)}</Tag> },
                { title: '并发', render: (_, row) => row.settings.concurrencyLevels.join(',') },
                { title: '请求数', render: (_, row) => formatCompactNumber(stressTotalRequests(row)) },
                { title: '成功率', render: (_, row) => stressSuccessRate(row) },
                { title: '开始时间', render: (_, row) => formatDateTime(row.startedAt || row.createdAt) }
              ]}
            />
          )}
        </Card>
        <Card className="panel">
          <div className="chart-toolbar">
            <Text strong>结果对比</Text>
            {selectedRun && <Tag color={stressStatusColor(selectedRun.status)}>{stressStatusLabel(selectedRun.status)}</Tag>}
          </div>
          {!selectedRun && <Text type="secondary">暂无可展示的压力测试结果。</Text>}
          {selectedRun && (
            <>
              <div className="stress-selected-run">
                <Text type="secondary">{stressTargetText(selectedRun)} · {selectedRun.settings.endpoint} · {formatDateTime(selectedRun.createdAt)}</Text>
                {selectedRun.reason && <Text type="danger">{selectedRun.reason}</Text>}
              </div>
              <ReactECharts option={resultChart} style={{ height: 260 }} />
              <Table
                rowKey="concurrency"
                dataSource={selectedRun.levels}
                pagination={false}
                columns={[
                  { title: '并发', dataIndex: 'concurrency' },
                  { title: '状态', render: (_, row) => <Tag color={stressStatusColor(row.status)}>{stressStatusLabel(row.status)}</Tag> },
                  { title: '耗时', render: (_, row) => formatDuration(row.metrics.elapsedMs || 0) },
                  { title: 'RPS', render: (_, row) => formatRPS(row.metrics.requestsPerSecond) },
                  { title: '请求', render: (_, row) => row.metrics.requestCount },
                  { title: '已启动/完成', render: (_, row) => `${row.metrics.startedCount || row.metrics.requestCount}/${row.metrics.completedCount || row.metrics.requestCount}` },
                  { title: '峰值并发', render: (_, row) => `${row.metrics.peakInFlightCount || 0}/${row.concurrency}` },
                  { title: '成功率', render: (_, row) => stressLevelSuccessRate(row) },
                  { title: '错误/超时', render: (_, row) => `${row.metrics.errorCount}/${row.metrics.timeoutCount}` },
                  { title: 'Avg', render: (_, row) => formatLatency(row.metrics.avgLatencyMs) },
                  { title: 'P95', render: (_, row) => formatLatency(row.metrics.p95LatencyMs) },
                  { title: 'P99', render: (_, row) => formatLatency(row.metrics.p99LatencyMs) },
                  { title: '状态码', render: (_, row) => stressStatusCountsText(row.metrics.statusCounts) }
                ]}
              />
              <StressUpstreamStats run={selectedRun} />
              <StressErrorSamples run={selectedRun} />
            </>
          )}
        </Card>
      </div>
    </section>
  );
}

function StressUpstreamStats({ run }: { run: StressTestRun }) {
  const rows = run.levels.flatMap((level) => (level.upstreamStats || []).map((stat) => ({ ...stat, concurrency: level.concurrency })));
  if (rows.length === 0) return null;
  return (
    <div className="stress-upstream-stats">
      <Text strong>上游明细</Text>
      <Table
        rowKey={(row: any) => `${row.concurrency}-${row.upstreamId || row.upstreamName || 'unknown'}`}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: '并发', dataIndex: 'concurrency' },
          { title: '上游服务', render: (_, row) => row.upstreamName || shortUUID(row.upstreamId || '') || '-' },
          { title: '集群', render: (_, row) => row.clusterName || '-' },
          { title: '请求', dataIndex: 'requests' },
          { title: '成功率', render: (_, row) => (row.requests ? `${((row.successes * 100) / row.requests).toFixed(1)}%` : '-') },
          { title: '错误', dataIndex: 'errors' },
          { title: '超时', dataIndex: 'timeouts' },
          { title: 'Avg', render: (_, row) => formatLatency(row.avgLatencyMs) },
          { title: 'Max', render: (_, row) => formatLatency(row.maxLatencyMs) },
          { title: '状态码', render: (_, row) => stressStatusCountsText(row.statusCounts) }
        ]}
      />
    </div>
  );
}

function StressErrorSamples({ run }: { run: StressTestRun }) {
  const samples = run.levels.flatMap((level) => (level.errorSamples || []).map((sample) => ({ ...sample, concurrency: level.concurrency })));
  if (samples.length === 0) return null;
  return (
    <div className="stress-errors">
      <Text strong>错误样本</Text>
      <div className="stress-error-head">
        <span>并发</span>
        <span>上游服务</span>
        <span>集群</span>
        <span>状态</span>
        <span>延迟</span>
        <span>错误</span>
      </div>
      {samples.map((sample, index) => (
        <div className="stress-error-row" key={`${sample.at}-${index}`}>
          <Tag color="red">并发 {sample.concurrency}</Tag>
          <span>{sample.upstreamName || shortUUID(sample.upstreamId || '') || '-'}</span>
          <span>{sample.clusterName || '-'}</span>
          <Text type="secondary">{sample.status || '-'}</Text>
          <Text type="secondary">{formatLatency(sample.latencyMs)}</Text>
          <span>{sample.error}</span>
        </div>
      ))}
    </div>
  );
}

function ClusterHealthModal({ result, onClose }: { result: any; onClose: () => void }) {
  const rows = result.results || [];
  return (
    <Modal title={`集群健康检查：${result.clusterName || ''}`} visible onCancel={onClose} footer={<Button onClick={onClose}>关闭</Button>} width={920}>
      <div className="modal-form">
        <Space>
          <Tag color={result.ok ? 'green' : 'red'}>{result.ok ? '正常' : '异常'}</Tag>
          <Text type="secondary">
            成员 {result.memberCount || 0} 个，已检查 {result.checkedCount || 0} 个，策略 {clusterPolicyLabel(result.policy)}
          </Text>
        </Space>
        <Table
          rowKey="upstreamId"
          dataSource={rows}
          pagination={false}
          columns={[
            { title: '上游服务', render: (_, row) => row.upstreamName || row.upstreamId },
            { title: '协议', render: (_, row) => upstreamProtocolTag({ protocol: row.protocol }) },
            { title: '成员状态', render: (_, row) => <Tag color={row.memberStatus === 'enabled' ? 'green' : 'grey'}>{row.memberStatus === 'enabled' ? '启用' : '停用'}</Tag> },
            { title: '上游状态', render: (_, row) => <Tag color={row.upstreamStatus === 'enabled' ? 'green' : 'grey'}>{row.upstreamStatus || '-'}</Tag> },
            { title: '路由可用', render: (_, row) => <Space>{row.httpEligible && <Tag color="blue">HTTP</Tag>}{row.websocketEligible && <Tag color="cyan">WS</Tag>}{row.realtimeEligible && <Tag color="green">Realtime</Tag>}{!row.httpEligible && !row.websocketEligible && !row.realtimeEligible && '-'}</Space> },
            { title: '结果', render: (_, row) => <Tag color={row.ok ? 'green' : row.skipped ? 'grey' : 'red'}>{row.ok ? '正常' : row.skipped ? '跳过' : '异常'}</Tag> },
            { title: 'HTTP', render: (_, row) => row.status || '-' },
            { title: '代理', render: (_, row) => formatProxyUsage(row) },
            { title: '延迟', render: (_, row) => (typeof row.latencyMs === 'number' ? `${row.latencyMs} ms` : '-') },
            { title: '说明', render: (_, row) => row.error || row.target || '-' }
          ]}
        />
      </div>
    </Modal>
  );
}

function moveClusterMember(members: UpstreamClusterMember[], from: number, to: number): UpstreamClusterMember[] {
  const next = [...members];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return normalizeClusterMembers(next);
}

function validateUpstreamDraftForSave(draft: Partial<Upstream>) {
  const baseUrl = String(draft.baseUrl || '').trim();
  if (!baseUrl) throw new Error('请填写上游 Base URL');
  if (!/^(?:https?|wss?):\/\//i.test(baseUrl)) {
    throw new Error('上游地址必须使用 HTTP、HTTPS、WS 或 WSS 协议');
  }
  if (isRealtimeUpstream(draft)) {
    validateHTTPSURL(baseUrl, 'Realtime 上游 API 根地址');
    validateRealtimeCapabilities(draft.realtimeCapabilities);
    if (draft.realtimeCapabilities?.sip) {
      if (!String(draft.realtimeSipDomain || '').trim()) throw new Error('启用 SIP 时必须填写上游 SIP 域名');
      if (!String(draft.realtimeSipUser || '').trim()) throw new Error('启用 SIP 时必须填写 SIP Project / User');
      const port = Number(draft.realtimeSipPort || 5061);
      if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error('上游 SIP TLS 端口必须在 1 到 65535 之间');
      if (!String(draft.realtimeWebhookSigningSecret || '').trim() && !draft.realtimeWebhookSecretSet) throw new Error('启用 SIP 时必须填写上游 Webhook 验签密钥');
    }
  }
}

function draftWithUpstreamProtocol(draft: Partial<Upstream>, protocol?: Upstream['protocol']): Partial<Upstream> {
  const next = { ...draft, protocol: protocol || 'openai_http' };
  if (protocol === 'openai_realtime') {
    next.capabilities = {};
    next.realtimeCapabilities = hasRealtimeCapabilities(next.realtimeCapabilities) ? next.realtimeCapabilities : defaultRealtimeCapabilities();
  } else {
    next.realtimeCapabilities = {};
    next.realtimeSipDomain = '';
    next.realtimeSipPort = undefined;
    next.realtimeSipTransport = undefined;
    next.realtimeSipUser = '';
    next.realtimeSipSni = '';
    next.realtimeWebhookSigningSecret = '';
    if (protocol === 'websocket') next.capabilities = {};
  }
  return next;
}

function normalizeUpstreamDraftForSave(draft: Partial<Upstream>, editing: Upstream | null): Partial<Upstream> {
  const next: Partial<Upstream> = { ...draft, protocol: upstreamProtocol(draft) };
  next.baseUrl = String(next.baseUrl || '').trim();
  if (isWebSocketUpstream(next)) {
    next.capabilities = {};
  }
  if (isRealtimeUpstream(next)) {
    next.capabilities = {};
    next.realtimeCapabilities = hasRealtimeCapabilities(next.realtimeCapabilities) ? next.realtimeCapabilities : defaultRealtimeCapabilities();
    if (!next.realtimeCapabilities.sip) {
      next.realtimeSipDomain = '';
      next.realtimeSipPort = undefined;
      next.realtimeSipTransport = undefined;
      next.realtimeSipUser = '';
      next.realtimeSipSni = '';
      next.realtimeWebhookSigningSecret = '';
    } else {
      next.realtimeSipDomain = String(next.realtimeSipDomain || '').trim();
      next.realtimeSipPort = Number(next.realtimeSipPort || 5061);
      next.realtimeSipTransport = 'tls';
      next.realtimeSipUser = String(next.realtimeSipUser || '').trim();
      next.realtimeSipSni = String(next.realtimeSipSni || '').trim();
      next.realtimeWebhookSigningSecret = String(next.realtimeWebhookSigningSecret || '').trim();
    }
  } else {
    next.realtimeCapabilities = {};
    next.realtimeSipDomain = '';
    next.realtimeSipPort = undefined;
    next.realtimeSipTransport = undefined;
    next.realtimeSipUser = '';
    next.realtimeSipSni = '';
    next.realtimeWebhookSigningSecret = '';
  }
  if (!editing) {
    next.proxyUrl = String(next.proxyUrl || '').trim();
    next.proxyUrlClear = false;
    return next;
  }
  const proxyUrl = String(next.proxyUrl || '').trim();
  if (next.proxyUrlClear) {
    next.proxyUrl = '';
    next.proxyUrlClear = true;
    return next;
  }
  if (!proxyUrl || proxyUrl === (editing.proxyUrl || '')) {
    delete next.proxyUrl;
  } else {
    next.proxyUrl = proxyUrl;
  }
  delete next.proxyUrlClear;
  return next;
}

function normalizeClusterDraft(draft: Partial<UpstreamCluster>): Partial<UpstreamCluster> {
  return {
    ...draft,
    status: draft.status || 'enabled',
    policy: draft.policy || 'random',
    members: normalizeClusterMembers(draft.members || [])
  };
}

function clusterPolicyLabel(policy?: string) {
  if (policy === 'round_robin') return '轮流选择';
  if (policy === 'least_connections') return '最少连接数';
  return '随机选择';
}

function ComputeMonitoring() {
  const servers = useQuery({ queryKey: ['computeServers'], queryFn: api.computeServers, refetchInterval: 10000 });
  const [selectedUuid, setSelectedUuid] = useState('');
  const [range, setRange] = useState('1h');
  const selected = (servers.data || []).find((item) => item.uuid === selectedUuid) || servers.data?.[0];
  const to = new Date();
  const from = new Date(to.getTime() - rangeToMs(range));
  const history = useQuery({
    queryKey: ['computeHistory', selected?.uuid, range],
    queryFn: () => api.computeHistory(selected!.uuid, from.toISOString(), to.toISOString()),
    enabled: Boolean(selected?.uuid),
    refetchInterval: 10000
  });
  const chart = useMemo(() => buildComputeChart(history.data?.samples || []), [history.data]);

  return (
    <section>
      <PageTitle title="算力服务器监控" subtitle="查看各算力节点 CPU、GPU、温度和显存状态。" />
      {servers.isLoading && <Card className="panel">正在加载算力服务器...</Card>}
      {servers.isError && <Banner type="danger" closeIcon={null} description="无法加载算力服务器监控数据。" />}
      {!servers.isLoading && (servers.data || []).length === 0 && (
        <Card className="panel empty-state">
          <Title heading={5}>暂无上报节点</Title>
          <Text type="secondary">请在算力服务器上安装 Go 探针，并配置上报地址和 token。</Text>
        </Card>
      )}
      <div className="server-grid">
        {(servers.data || []).map((server) => (
          <ComputeServerCard key={server.uuid} server={server} selected={selected?.uuid === server.uuid} onSelect={() => setSelectedUuid(server.uuid)} />
        ))}
      </div>
      {selected && (
        <Card className="panel">
          <div className="chart-toolbar">
            <Space>
              <Text strong>历史曲线</Text>
              <Select value={selected.uuid} onChange={(value) => setSelectedUuid(String(value))} style={{ width: 220 }}>
                {(servers.data || []).map((server) => (
                  <Select.Option value={server.uuid} key={server.uuid}>
                    {server.displayName}
                  </Select.Option>
                ))}
              </Select>
              <Select value={range} onChange={(value) => setRange(String(value))} style={{ width: 120 }}>
                <Select.Option value="1h">最近 1 小时</Select.Option>
                <Select.Option value="6h">最近 6 小时</Select.Option>
                <Select.Option value="24h">最近 24 小时</Select.Option>
              </Select>
            </Space>
          </div>
          <ReactECharts option={chart} style={{ height: 360 }} />
        </Card>
      )}
    </section>
  );
}

function ComputeServerCard({ server, selected, onSelect }: { server: ComputeServer; selected: boolean; onSelect: () => void }) {
  const queryClient = useQueryClient();
  const hold = useMutation({
    mutationFn: () => api.holdComputeServer(server.uuid, { hold: !server.manualHold, reason: 'dashboard manual control' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['computeServers'] })
  });
  const restore = useMutation({
    mutationFn: () => api.restoreComputeServer(server.uuid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['computeServers'] })
  });
  const gpus = server.gpus || [];
  const caps = server.agent?.capabilities || {};
  return (
    <div className="server-card-click" onClick={onSelect}>
      <Card className={`server-card ${selected ? 'selected' : ''}`}>
        <div className="server-card-head">
          <div>
            <Text strong>{server.displayName}</Text>
            <div className="muted-line">{shortUUID(server.uuid)}</div>
          </div>
          <Tag color={server.online ? 'green' : 'red'}>{server.online ? '在线' : '离线'}</Tag>
        </div>
        <Space>
          <Tag color={nodeStateColor(server.lifecycleState)}>{nodeStateLabel(server.lifecycleState)}</Tag>
          {server.manualHold && <Tag color="orange">手动保持</Tag>}
          {caps.commandAck && <Tag color="green">agent</Tag>}
          {caps.dockerWorkload && <Tag color="blue">Docker</Tag>}
        </Space>
        <div className="server-metrics">
          <MiniMetric label="CPU" value={fmtPercent(server.cpu?.usagePercent)} />
          <MiniMetric label="CPU 温度" value={fmtTemp(server.cpu?.temperatureC)} />
          <MiniMetric label="GPU 数" value={gpus.length} />
          <MiniMetric label="最后上报" value={`${server.ageSeconds || 0}s 前`} />
        </div>
        <div className="gpu-list">
          {gpus.length === 0 && <Text type="secondary">未检测到 GPU 指标</Text>}
          {gpus.map((gpu) => (
            <div className="gpu-row" key={`${gpu.index}-${gpu.uuid || gpu.name}`}>
              <Text strong>GPU {gpu.index}</Text>
              <span>{fmtTemp(gpu.temperatureC)}</span>
              <span>{fmtPercent(gpu.utilizationPercent)}</span>
              <span>{fmtMemory(gpu.memoryUsedMb, gpu.memoryTotalMb)}</span>
            </div>
          ))}
        </div>
        <div className="card-actions">
          <Button size="small" loading={hold.isPending} onClick={(event) => { event.stopPropagation(); hold.mutate(); }}>
            {server.manualHold ? '取消保持' : '手动保持'}
          </Button>
          <Button size="small" loading={restore.isPending} onClick={(event) => { event.stopPropagation(); restore.mutate(); }}>
            恢复服务
          </Button>
        </div>
        {(server.imageCache || []).length > 0 && <div className="muted-line">镜像缓存：{(server.imageCache || []).length} 个</div>}
      </Card>
    </div>
  );
}

function nodeStateLabel(state?: string) {
  const labels: Record<string, string> = {
    serving: '对外服务',
    draining: '排空中',
    reserved: '已预留',
    running_workload: '运行容器',
    stopping_workload: '停止容器',
    restoring: '恢复中',
    error: '异常',
    manual_hold: '手动保持'
  };
  return labels[state || 'serving'] || state || '对外服务';
}

function nodeStateColor(state?: string) {
  if (!state || state === 'serving') return 'green';
  if (state === 'error') return 'red';
  if (state === 'running_workload' || state === 'reserved') return 'violet';
  return 'orange';
}

function OffPeakPolicies() {
  const queryClient = useQueryClient();
  const policies = useQuery({ queryKey: ['offPeakPolicies'], queryFn: api.offPeakPolicies });
  const workloads = useQuery({ queryKey: ['workloads'], queryFn: api.workloads });
  const clusters = useQuery({ queryKey: ['upstreamClusters'], queryFn: api.upstreamClusters });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OffPeakPolicy | null>(null);
  const [draft, setDraft] = useState<any>(defaultPolicyDraft());
  const save = useMutation({
    mutationFn: () => (editing ? api.updateOffPeakPolicy(editing.id, policyPayload(draft)) : api.createOffPeakPolicy(policyPayload(draft))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offPeakPolicies'] });
      setOpen(false);
      setEditing(null);
      setDraft(defaultPolicyDraft());
      Toast.success('错峰策略已保存');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const remove = useMutation({
    mutationFn: api.deleteOffPeakPolicy,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['offPeakPolicies'] })
  });
  const toggle = useMutation({
    mutationFn: (policy: OffPeakPolicy) => (policy.status === 'enabled' ? api.disableOffPeakPolicy(policy.id) : api.enableOffPeakPolicy(policy.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['offPeakPolicies'] })
  });
  const openEdit = (policy: OffPeakPolicy) => {
    setEditing(policy);
    setDraft(policyDraft(policy));
    setOpen(true);
  };
  return (
    <section>
      <PageTitle title="错峰编排" subtitle="配置低谷时段、流量阈值、冷却时间和可被预留的算力节点。" />
      <div className="toolbar">
        <Button theme="solid" type="primary" icon={<IconPlus />} onClick={() => { setEditing(null); setDraft(defaultPolicyDraft()); setOpen(true); }}>
          新建策略
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={policies.data || []}
        loading={policies.isLoading}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '状态', render: (_, row) => <Tag color={row.status === 'enabled' ? 'green' : 'grey'}>{row.status === 'enabled' ? '启用' : '停用'}</Tag> },
          { title: '窗口', render: (_, row) => `${row.timezone} ${row.windows?.[0]?.startMinute ?? 0}-${row.windows?.[0]?.endMinute ?? 0}` },
          { title: '低阈值', render: (_, row) => `${row.thresholds?.low?.requestRatePerMinute || 0} req/min` },
          { title: '默认容器', render: (_, row) => workloads.data?.find((item) => item.id === row.defaultWorkloadId)?.name || '-' },
          { title: '节点', render: (_, row) => (row.eligibleNodeUuids || []).length },
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                <Button size="small" onClick={() => toggle.mutate(row)}>{row.status === 'enabled' ? '停用' : '启用'}</Button>
                <Button size="small" icon={<IconEdit />} onClick={() => openEdit(row)}>编辑</Button>
                <Button size="small" type="danger" icon={<IconDelete />} onClick={() => remove.mutate(row.id)}>删除</Button>
              </Space>
            )
          }
        ]}
      />
      <Modal title={editing ? '编辑错峰策略' : '新建错峰策略'} visible={open} width={760} onCancel={() => setOpen(false)} footer={<Space><Button onClick={() => setOpen(false)}>取消</Button><Button theme="solid" type="primary" loading={save.isPending} onClick={() => save.mutate()}>保存</Button></Space>}>
        <div className="modal-form">
          <Input prefix="名称" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <div className="two-column-form">
            <Input prefix="时区" value={draft.timezone} onChange={(timezone) => setDraft({ ...draft, timezone })} />
            <Select value={draft.status} onChange={(status) => setDraft({ ...draft, status: String(status) })} className="select">
              <Select.Option value="enabled">启用</Select.Option>
              <Select.Option value="disabled">停用</Select.Option>
            </Select>
          </div>
          <div className="two-column-form">
            <Input prefix="开始分钟" value={String(draft.startMinute)} onChange={(v) => setDraft({ ...draft, startMinute: Number(v) || 0 })} />
            <Input prefix="结束分钟" value={String(draft.endMinute)} onChange={(v) => setDraft({ ...draft, endMinute: Number(v) || 0 })} />
          </div>
          <Input prefix="星期" value={draft.daysOfWeek} onChange={(daysOfWeek) => setDraft({ ...draft, daysOfWeek })} />
          <div className="two-column-form">
            <Input prefix="低阈值 req/min" value={String(draft.lowReq)} onChange={(v) => setDraft({ ...draft, lowReq: Number(v) || 0 })} />
            <Input prefix="高阈值 req/min" value={String(draft.highReq)} onChange={(v) => setDraft({ ...draft, highReq: Number(v) || 0 })} />
          </div>
          <div className="two-column-form">
            <Input prefix="静默秒" value={String(draft.quietPeriodSeconds)} onChange={(v) => setDraft({ ...draft, quietPeriodSeconds: Number(v) || 0 })} />
            <Input prefix="冷却秒" value={String(draft.cooldownSeconds)} onChange={(v) => setDraft({ ...draft, cooldownSeconds: Number(v) || 0 })} />
          </div>
          <Select value={draft.defaultWorkloadId} onChange={(defaultWorkloadId) => setDraft({ ...draft, defaultWorkloadId: String(defaultWorkloadId) })} className="select" placeholder="默认算力容器">
            {(workloads.data || []).map((workload) => <Select.Option value={workload.id} key={workload.id}>{workload.name}</Select.Option>)}
          </Select>
          <Input prefix="节点 UUID" value={draft.eligibleNodeUuids} onChange={(eligibleNodeUuids) => setDraft({ ...draft, eligibleNodeUuids })} />
          <Input prefix="集群 ID" value={draft.eligibleClusterIds} onChange={(eligibleClusterIds) => setDraft({ ...draft, eligibleClusterIds })} placeholder={(clusters.data || []).map((item) => item.name).join(', ')} />
          <Select value={draft.dryRun ? 'true' : 'false'} onChange={(dryRun) => setDraft({ ...draft, dryRun: dryRun === 'true' })} className="select">
            <Select.Option value="false">实际执行</Select.Option>
            <Select.Option value="true">只记录不执行</Select.Option>
          </Select>
        </div>
      </Modal>
    </section>
  );
}

function WorkloadImages() {
  const queryClient = useQueryClient();
  const images = useQuery({ queryKey: ['workloadImages'], queryFn: api.workloadImages });
  const [draft, setDraft] = useState({ name: '', version: 'latest', expectedSizeBytes: 0, expectedSha256: '' });
  const [progress, setProgress] = useState('');
  const create = useMutation({
    mutationFn: () => api.createWorkloadImage({ ...draft, archiveType: 'docker_archive' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workloadImages'] }),
    onError: (error: Error) => Toast.error(error.message)
  });
  const remove = useMutation({ mutationFn: api.deleteWorkloadImage, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workloadImages'] }) });
  async function upload(file: File) {
    const result = await create.mutateAsync();
    const session = result.uploadSession;
    const chunkSize = session.chunkSizeBytes || 64 * 1024 * 1024;
    const total = Math.ceil(file.size / chunkSize);
    for (let i = 0; i < total; i++) {
      const chunk = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
      await api.uploadImageChunk(session.id, i, chunk);
      setProgress(`${i + 1}/${total}`);
    }
    await api.completeImageUpload(session.id);
    queryClient.invalidateQueries({ queryKey: ['workloadImages'] });
    Toast.success('镜像上传完成');
  }
  return (
    <section>
      <PageTitle title="算力镜像" subtitle="管理错峰算力容器使用的 Docker/OCI 镜像归档。" />
      <Card className="panel">
        <div className="inline-form">
          <Input placeholder="镜像名" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <Input placeholder="版本" value={draft.version} onChange={(version) => setDraft({ ...draft, version })} />
          <Input placeholder="预期 SHA256，可选" value={draft.expectedSha256} onChange={(expectedSha256) => setDraft({ ...draft, expectedSha256 })} />
          <input type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setDraft((old) => ({ ...old, expectedSizeBytes: file.size })); upload(file).catch((error) => Toast.error(error.message)); } }} />
        </div>
        {progress && <Text type="secondary">上传进度：{progress}</Text>}
      </Card>
      <Table rowKey="id" dataSource={images.data || []} loading={images.isLoading} columns={[
        { title: '名称', render: (_, row) => `${row.name}:${row.version}` },
        { title: '状态', render: (_, row) => <Tag color={row.status === 'available' ? 'green' : row.status === 'failed' ? 'red' : 'orange'}>{row.status}</Tag> },
        { title: '大小', render: (_, row) => formatBytes(row.sizeBytes || row.expectedSizeBytes || 0) },
        { title: 'SHA256', render: (_, row) => row.sha256 ? shortHash(row.sha256) : '-' },
        { title: '操作', render: (_, row) => <Button size="small" type="danger" icon={<IconDelete />} onClick={() => remove.mutate(row.id)}>删除</Button> }
      ]} />
    </section>
  );
}

function Workloads() {
  const queryClient = useQueryClient();
  const workloads = useQuery({ queryKey: ['workloads'], queryFn: api.workloads });
  const images = useQuery({ queryKey: ['workloadImages'], queryFn: api.workloadImages });
  const servers = useQuery({ queryKey: ['computeServers'], queryFn: api.computeServers });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Workload | null>(null);
  const [draft, setDraft] = useState<any>(defaultWorkloadDraft());
  const save = useMutation({
    mutationFn: () => (editing ? api.updateWorkload(editing.id, workloadPayload(draft)) : api.createWorkload(workloadPayload(draft))),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['workloads'] }); setOpen(false); setEditing(null); setDraft(defaultWorkloadDraft()); },
    onError: (error: Error) => Toast.error(error.message)
  });
  const remove = useMutation({ mutationFn: api.deleteWorkload, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workloads'] }) });
  const start = useMutation({
    mutationFn: (body: { workloadId: string; nodeUuids: string[] }) => api.startWorkloadRun(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['workloadRuns'] }); Toast.success('已下发启动命令'); }
  });
  const openEdit = (workload: Workload) => { setEditing(workload); setDraft(workloadDraft(workload)); setOpen(true); };
  return (
    <section>
      <PageTitle title="算力容器" subtitle="定义错峰期间在单个算力节点上运行的容器。" />
      <div className="toolbar"><Button theme="solid" type="primary" icon={<IconPlus />} onClick={() => { setEditing(null); setDraft(defaultWorkloadDraft()); setOpen(true); }}>新建容器</Button></div>
      <Table rowKey="id" dataSource={workloads.data || []} loading={workloads.isLoading} columns={[
        { title: '名称', dataIndex: 'name' },
        { title: '镜像', render: (_, row) => images.data?.find((image) => image.id === row.imageId)?.name || row.imageId },
        { title: 'GPU', dataIndex: 'gpuCount' },
        { title: '管理端口', dataIndex: 'managementPort' },
        { title: '状态', render: (_, row) => <Tag color={row.status === 'enabled' ? 'green' : 'grey'}>{row.status === 'enabled' ? '启用' : '停用'}</Tag> },
        { title: '操作', render: (_, row) => <Space><Button size="small" onClick={() => openEdit(row)}>编辑</Button><Button size="small" onClick={() => { const raw = window.prompt('输入节点 UUID，多个用英文逗号分隔', (servers.data || []).map((s) => s.uuid).join(',')); if (raw) start.mutate({ workloadId: row.id, nodeUuids: parseCSV(raw) }); }}>启动</Button><Button size="small" type="danger" onClick={() => remove.mutate(row.id)}>删除</Button></Space> }
      ]} />
      <Modal title={editing ? '编辑算力容器' : '新建算力容器'} visible={open} width={760} onCancel={() => setOpen(false)} footer={<Space><Button onClick={() => setOpen(false)}>取消</Button><Button theme="solid" type="primary" loading={save.isPending} onClick={() => save.mutate()}>保存</Button></Space>}>
        <div className="modal-form">
          <Input prefix="名称" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <Select value={draft.imageId} onChange={(imageId) => setDraft({ ...draft, imageId: String(imageId) })} className="select" placeholder="选择镜像">
            {(images.data || []).filter((image) => image.status === 'available').map((image) => <Select.Option value={image.id} key={image.id}>{image.name}:{image.version}</Select.Option>)}
          </Select>
          <div className="two-column-form"><Input prefix="GPU 数" value={String(draft.gpuCount)} onChange={(v) => setDraft({ ...draft, gpuCount: Number(v) || 0 })} /><Input prefix="管理端口" value={String(draft.managementPort || 0)} onChange={(v) => setDraft({ ...draft, managementPort: Number(v) || 0 })} /></div>
          <Input prefix="环境变量" value={draft.envText} onChange={(envText) => setDraft({ ...draft, envText })} placeholder="A=1;B=2" />
          <Input prefix="目录挂载" value={draft.mountText} onChange={(mountText) => setDraft({ ...draft, mountText })} placeholder="/data/a:/data/a:ro;/data/b:/data/b" />
          <Input prefix="命令" value={draft.commandText} onChange={(commandText) => setDraft({ ...draft, commandText })} />
          <Input prefix="参数" value={draft.argsText} onChange={(argsText) => setDraft({ ...draft, argsText })} />
          <Input prefix="可用节点" value={draft.eligibleNodeUuids} onChange={(eligibleNodeUuids) => setDraft({ ...draft, eligibleNodeUuids })} />
        </div>
      </Modal>
    </section>
  );
}

function WorkloadLogs() {
  const queryClient = useQueryClient();
  const runs = useQuery({ queryKey: ['workloadRuns'], queryFn: api.workloadRuns, refetchInterval: 10000 });
  const logs = useQuery({ queryKey: ['workloadLogs'], queryFn: () => api.workloadLogs(''), refetchInterval: 10000 });
  const stop = useMutation({ mutationFn: api.stopWorkloadRun, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workloadRuns'] }) });
  const restore = useMutation({ mutationFn: api.restoreWorkloadRun, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workloadRuns'] }) });
  return (
    <section>
      <PageTitle title="容器日志" subtitle="查看错峰容器运行状态、运行记录和 agent 上报日志。" />
      <Card className="panel">
        <Title heading={5}>运行记录</Title>
        <Table rowKey="id" dataSource={runs.data || []} loading={runs.isLoading} pagination={false} columns={[
          { title: '节点', dataIndex: 'nodeUuid' },
          { title: '状态', render: (_, row) => <Tag color={runStateColor(row.status)}>{row.status}</Tag> },
          { title: '来源', dataIndex: 'source' },
          { title: '原因', dataIndex: 'reason' },
          { title: '时间', dataIndex: 'createdAt' },
          { title: '操作', render: (_, row) => <Space><Button size="small" onClick={() => window.open(`/api/admin/workload-runs/${row.id}/manage/`, '_blank')}>管理页</Button><Button size="small" onClick={() => stop.mutate(row.id)}>停止</Button><Button size="small" onClick={() => restore.mutate(row.id)}>恢复</Button></Space> }
        ]} />
      </Card>
      <Table rowKey="id" dataSource={logs.data || []} loading={logs.isLoading} columns={[
        { title: '时间', dataIndex: 'createdAt' },
        { title: '节点', dataIndex: 'nodeUuid' },
        { title: '级别', dataIndex: 'severity' },
        { title: '流', dataIndex: 'stream' },
        { title: '消息', dataIndex: 'message' }
      ]} />
    </section>
  );
}

function MiniMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="mini-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function defaultThreshold(): any {
  return { requestRatePerMinute: 0, inFlightRequests: 0, errorRatePercent: 0, fallbackRatePerMinute: 0, queueDepth: 0 };
}

function defaultPolicyDraft() {
  return { name: '', status: 'disabled', timezone: 'Asia/Shanghai', startMinute: 0, endMinute: 360, daysOfWeek: '0,1,2,3,4,5,6', lowReq: 0, highReq: 10, quietPeriodSeconds: 300, cooldownSeconds: 900, eligibleNodeUuids: '', eligibleClusterIds: '', defaultWorkloadId: '', dryRun: true };
}

function policyDraft(policy: OffPeakPolicy) {
  const window = policy.windows?.[0] || { startMinute: 0, endMinute: 360, daysOfWeek: [0, 1, 2, 3, 4, 5, 6] };
  return {
    name: policy.name,
    status: policy.status,
    timezone: policy.timezone,
    startMinute: window.startMinute,
    endMinute: window.endMinute,
    daysOfWeek: (window.daysOfWeek || []).join(','),
    lowReq: policy.thresholds?.low?.requestRatePerMinute || 0,
    highReq: policy.thresholds?.high?.requestRatePerMinute || 0,
    quietPeriodSeconds: policy.quietPeriodSeconds,
    cooldownSeconds: policy.cooldownSeconds,
    eligibleNodeUuids: (policy.eligibleNodeUuids || []).join(','),
    eligibleClusterIds: (policy.eligibleClusterIds || []).join(','),
    defaultWorkloadId: policy.defaultWorkloadId || '',
    dryRun: policy.dryRun
  };
}

function policyPayload(draft: any): Partial<OffPeakPolicy> {
  const low = { ...defaultThreshold(), requestRatePerMinute: Number(draft.lowReq) || 0 };
  const high = { ...defaultThreshold(), requestRatePerMinute: Number(draft.highReq) || 0 };
  return {
    name: draft.name,
    status: draft.status,
    timezone: draft.timezone,
    windows: [{ daysOfWeek: parseCSV(draft.daysOfWeek).map(Number).filter((n) => !Number.isNaN(n)), startMinute: Number(draft.startMinute) || 0, endMinute: Number(draft.endMinute) || 360 }],
    thresholds: { low, high },
    quietPeriodSeconds: Number(draft.quietPeriodSeconds) || 0,
    cooldownSeconds: Number(draft.cooldownSeconds) || 0,
    eligibleNodeUuids: parseCSV(draft.eligibleNodeUuids),
    eligibleClusterIds: parseCSV(draft.eligibleClusterIds),
    defaultWorkloadId: draft.defaultWorkloadId || undefined,
    dryRun: Boolean(draft.dryRun)
  };
}

function defaultWorkloadDraft() {
  return { name: '', status: 'disabled', imageId: '', gpuCount: 0, managementPort: 0, managementScheme: 'http', envText: '', mountText: '', commandText: '', argsText: '', eligibleNodeUuids: '', timeoutSeconds: 86400, nodeCount: 1 };
}

function workloadDraft(workload: Workload) {
  return {
    ...workload,
    envText: Object.entries(workload.env || {}).map(([key, value]) => `${key}=${value}`).join(';'),
    mountText: (workload.mounts || []).map((m) => `${m.hostPath}:${m.containerPath}${m.readOnly ? ':ro' : ''}`).join(';'),
    commandText: (workload.command || []).join(' '),
    argsText: (workload.args || []).join(' '),
    eligibleNodeUuids: (workload.eligibleNodeUuids || []).join(',')
  };
}

function workloadPayload(draft: any): Partial<Workload> {
  return {
    name: draft.name,
    status: draft.status || 'disabled',
    imageId: draft.imageId,
    gpuCount: Number(draft.gpuCount) || 0,
    managementPort: Number(draft.managementPort) || 0,
    managementScheme: draft.managementScheme || 'http',
    env: parseEnv(draft.envText),
    mounts: parseMounts(draft.mountText),
    command: parseWords(draft.commandText),
    args: parseWords(draft.argsText),
    eligibleNodeUuids: parseCSV(draft.eligibleNodeUuids),
    timeoutSeconds: Number(draft.timeoutSeconds) || 86400,
    nodeCount: 1
  };
}

function parseCSV(raw: string) {
  return String(raw || '').split(',').map((item) => item.trim()).filter(Boolean);
}

function parseWords(raw: string) {
  return String(raw || '').split(/\s+/).map((item) => item.trim()).filter(Boolean);
}

function parseEnv(raw: string) {
  const out: Record<string, string> = {};
  String(raw || '').split(';').forEach((part) => {
    const idx = part.indexOf('=');
    if (idx > 0) out[part.slice(0, idx).trim()] = part.slice(idx + 1).trim();
  });
  return out;
}

function parseMounts(raw: string) {
  return String(raw || '').split(';').map((item) => item.trim()).filter(Boolean).map((item) => {
    const parts = item.split(':');
    return { hostPath: parts[0] || '', containerPath: parts[1] || '', readOnly: parts[2] === 'ro' };
  }).filter((item) => item.hostPath && item.containerPath);
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index++;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function shortHash(value: string) {
  return value.length > 14 ? `${value.slice(0, 10)}...` : value;
}

function runStateColor(state?: string) {
  if (state === 'running') return 'green';
  if (state === 'failed' || state === 'interrupted') return 'red';
  if (state === 'completed') return 'grey';
  return 'orange';
}

function appBossDeductColor(status?: string) {
  if (status === 'succeeded') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'delegated') return 'cyan';
  if (status === 'skipped') return 'orange';
  return 'grey';
}

function formatAppBossDeductStatus(event: UsageEvent) {
  if (event.appbossDeductStatus === 'succeeded') return '已扣费';
  if (event.appbossDeductStatus === 'failed') return event.appbossDeductHttpStatus ? `失败 ${event.appbossDeductHttpStatus}` : '扣费失败';
  if (event.appbossDeductStatus === 'delegated') return '上游扣费';
  if (event.appbossDeductStatus === 'skipped') return '跳过';
  return event.appbossDeductStatus || '-';
}

function Usage() {
  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.usage('') });
  const [detailEvent, setDetailEvent] = useState<UsageEvent | null>(null);
  const rows = usage.data?.events || [];
  return (
    <section>
      <PageTitle title="用量统计" subtitle="查看 BaseURL、匿名访问和客户端 key 维度的调用计量。" />
      <div className="metric-grid">
        <Metric label="请求数" value={usage.data?.summary?.requests || 0} />
        <Metric label="WS 连接" value={usage.data?.summary?.websocketConnections || 0} />
        <Metric label="Token 消耗" value={usage.data?.summary?.totalTokens || 0} />
        <Metric label="错误数" value={usage.data?.summary?.errors || 0} />
      </div>
      <Table
        rowKey="id"
        dataSource={rows}
        loading={usage.isLoading}
        columns={[
          { title: '时间', dataIndex: 'createdAt' },
          { title: '入口', dataIndex: 'baseSlug' },
          { title: '主体', dataIndex: 'principalType' },
          { title: '密钥前缀', dataIndex: 'keyPrefix' },
          { title: '协议', render: (_, row) => <Tag color={usageProtocolColor(row.protocol)}>{usageProtocolLabel(row.protocol)}</Tag> },
          { title: '接口', dataIndex: 'endpoint' },
          { title: '模型', dataIndex: 'model' },
          { title: '状态码', dataIndex: 'status' },
          { title: 'Token', dataIndex: 'totalTokens' },
          { title: '扣费方', render: (_, row) => (row.appbossBillingEnabled ? appBossBillingModeTag(row) : '-') },
          { title: '服务', render: (_, row) => (row.appbossBillingEnabled ? row.appbossServiceId || '-' : '-') },
          { title: '用量', render: (_, row) => (row.appbossBillingEnabled ? row.appbossConsumptionQuantity || 0 : '-') },
          { title: '点数', render: (_, row) => (row.appbossBillingEnabled ? row.appbossPoints || 0 : '-') },
          { title: '扣费', render: (_, row) => (row.appbossBillingEnabled ? <Tag color={appBossDeductColor(row.appbossDeductStatus)}>{formatAppBossDeductStatus(row)}</Tag> : '-') },
          { title: '连接时长', render: (_, row) => (isRealtimeSessionProtocol(row.protocol) ? formatDuration(row.durationMs || 0) : '-') },
          { title: '计量来源', dataIndex: 'exactness' },
          {
            title: '详情',
            render: (_, row) => (
              <Button size="small" onClick={() => setDetailEvent(row)}>
                详情
              </Button>
            )
          }
        ]}
      />
      {detailEvent && <UsageDetailModal event={detailEvent} onClose={() => setDetailEvent(null)} />}
    </section>
  );
}

function UsageDetailModal({ event, onClose }: { event: UsageEvent; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['usageDetail', event.id],
    queryFn: () => api.usageDetail(event.id)
  });
  const data = detail.data;
  const retained = data?.detail;
  return (
    <Modal title={`请求详情：${event.requestId}`} visible onCancel={onClose} footer={null} width={980}>
      <div className="usage-detail">
        {detail.isLoading && <Text type="secondary">正在加载详情...</Text>}
        {detail.isError && <Banner type="danger" closeIcon={null} description={(detail.error as Error).message} />}
        {data && !data.detailAvailable && <Banner type="warning" closeIcon={null} description={data.missingReason || '详情已过期或未捕获。'} />}
        {retained && (
          <>
            <div className="usage-detail-meta">
              <MiniMetric label="入口" value={retained.baseSlug || '-'} />
              <MiniMetric label="接口" value={retained.endpoint || '-'} />
              <MiniMetric label="模型" value={retained.model || '-'} />
              <MiniMetric label="协议" value={usageProtocolLabel(retained.protocol)} />
              <MiniMetric label="状态" value={String(retained.status || event.status || '-')} />
              <MiniMetric label="耗时" value={formatDuration(retained.latencyMs || event.latencyMs || 0)} />
              {isRealtimeSessionProtocol(event.protocol) && <MiniMetric label="连接" value={formatDuration(event.durationMs || 0)} />}
              {event.closeReason && <MiniMetric label="关闭原因" value={event.closeReason} />}
              {event.valueComplianceApplied && <MiniMetric label="价值观合规" value={`已注入 · ${event.valueCompliancePolicyVersion || '当前策略'}`} />}
              {event.appbossBillingEnabled && (
                <>
                  <MiniMetric label="扣费方式" value={appBossBillingModeText(event)} />
                  <MiniMetric label="服务类型" value={event.appbossServiceId || '-'} />
                  <MiniMetric label="扣费用量" value={String(event.appbossConsumptionQuantity || 0)} />
                  <MiniMetric label="扣费单元" value={event.appbossBillingUnit || '-'} />
                  <MiniMetric label="单元点数" value={String(event.appbossPointsPerUnit || 0)} />
                  <MiniMetric label="APPBOSS 点数" value={String(event.appbossPoints || 0)} />
                  <MiniMetric label="扣费 ID" value={event.appbossDeductId || '-'} />
                  <MiniMetric label="Ledger" value={event.appbossLedgerEntryId || '-'} />
                  <MiniMetric label="余额检查" value={event.appbossBalanceStatus || '-'} />
                  <MiniMetric label="扣费状态" value={formatAppBossDeductStatus(event)} />
                </>
              )}
              <MiniMetric label="过期" value={formatDateTime(retained.expiresAt)} />
            </div>
            <div className="test-message-grid">
              <UsageDetailBodyBlock title="请求消息" body={retained.request} />
              <UsageDetailBodyBlock title="回应消息" body={retained.response} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function UsageDetailBodyBlock({ title, body }: { title: string; body: UsageDetailBody }) {
  return (
    <div className="test-message-block">
      <Space align="center">
        <Text strong>{title}</Text>
        {body.contentType && <Tag color="blue">{body.contentType}</Tag>}
        <Text type="secondary">{formatBytes(body.bytes || 0)}</Text>
      </Space>
      <pre>{body.bodyAvailable ? formatTestMessage(body.body || '') : body.omissionMessage || body.omissionReason || '无可展示正文'}</pre>
    </div>
  );
}

function Audit() {
  const audits = useQuery({ queryKey: ['audits'], queryFn: api.audits });
  return (
    <section>
      <PageTitle title="审计日志" subtitle="查看管理员登录、配置变更和路由相关操作记录。" />
      <Table
        rowKey="id"
        dataSource={audits.data || []}
        loading={audits.isLoading}
        columns={[
          { title: '时间', dataIndex: 'createdAt' },
          { title: '操作人', dataIndex: 'actor' },
          { title: '动作', dataIndex: 'action' },
          { title: '资源', dataIndex: 'resource' },
          { title: '资源 ID', dataIndex: 'resourceId' }
        ]}
      />
    </section>
  );
}

function Settings() {
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const clearPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };
  const changePassword = useMutation({
    mutationFn: () => {
      if (!currentPassword || !newPassword || !confirmPassword) throw new Error('请填写当前密码、新密码和确认密码');
      if (newPassword !== confirmPassword) throw new Error('两次输入的新密码不一致');
      if (newPassword.length < 8) throw new Error('新密码至少需要 8 个字符');
      return api.changeAdminPassword({ currentPassword, newPassword, confirmPassword });
    },
    onSuccess: () => {
      clearPasswordForm();
      queryClient.invalidateQueries({ queryKey: ['audits'] });
      Toast.success('管理员密码已修改');
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const resetCounters = useMutation({
    mutationFn: api.resetCounters,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      queryClient.invalidateQueries({ queryKey: ['audits'] });
      const result = data?.result || {};
      Toast.success(`计数器已重置，清理 ${result.usageEventsDeleted || 0} 条用量记录`);
    },
    onError: (error: Error) => Toast.error(error.message)
  });
  const resetLogs = useMutation({
    mutationFn: api.resetLogs,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['audits'] });
      Toast.success(`日志已重置，清理 ${data?.result?.auditLogsDeleted || 0} 条审计日志`);
    },
    onError: (error: Error) => Toast.error(error.message)
  });

  return (
    <section>
      <PageTitle title="设置" subtitle="管理后台运行状态和维护操作。" />
      <div className="settings-grid">
        <Card className="panel">
          <div className="settings-card-head">
            <div>
              <Title heading={5}>修改管理员密码</Title>
              <Text type="secondary">修改当前登录管理员的密码，当前会话会保持登录。</Text>
            </div>
          </div>
          <div className="settings-password-form">
            <label>
              <Text strong>当前密码</Text>
              <Input mode="password" value={currentPassword} onChange={setCurrentPassword} />
            </label>
            <label>
              <Text strong>新密码</Text>
              <Input mode="password" value={newPassword} onChange={setNewPassword} />
            </label>
            <label>
              <Text strong>确认新密码</Text>
              <Input mode="password" value={confirmPassword} onChange={setConfirmPassword} />
            </label>
            <div className="settings-password-actions">
              <Button theme="solid" type="primary" icon={<IconLock />} loading={changePassword.isPending} onClick={() => changePassword.mutate()}>
                保存新密码
              </Button>
              <Button onClick={clearPasswordForm}>清空</Button>
            </div>
          </div>
        </Card>
        <Card className="panel danger-panel">
          <div className="settings-card-head">
            <div>
              <Title heading={5}>计数器重置</Title>
              <Text type="secondary">清空请求数、Token、错误数、用量明细、聚合计数和待写入计量队列。</Text>
            </div>
            <Button
              type="danger"
              icon={<IconDelete />}
              loading={resetCounters.isPending}
              onClick={() => {
                if (window.confirm('确认重置所有用量计数？请求数、Token、错误数和用量明细会被清空。')) resetCounters.mutate();
              }}
            >
              重置计数器
            </Button>
          </div>
        </Card>
        <Card className="panel danger-panel">
          <div className="settings-card-head">
            <div>
              <Title heading={5}>日志重置</Title>
              <Text type="secondary">清空审计日志，并保留一条本次重置记录。</Text>
            </div>
            <Button
              type="danger"
              icon={<IconDelete />}
              loading={resetLogs.isPending}
              onClick={() => {
                if (window.confirm('确认重置审计日志？历史登录和配置变更记录会被清空。')) resetLogs.mutate();
              }}
            >
              重置日志
            </Button>
          </div>
        </Card>
      </div>
    </section>
  );
}

function buildComputeChart(samples: any[]) {
  const times = samples.map((item) => new Date(item.reportedAt).toLocaleTimeString());
  const gpuIndexes = Array.from(new Set(samples.flatMap((item) => (item.gpus || []).map((gpu: any) => gpu.index)))).sort();
  const series: any[] = [
    { name: 'CPU 使用率', type: 'line', smooth: true, data: samples.map((item) => item.cpu?.usagePercent ?? null) },
    { name: 'CPU 温度', type: 'line', smooth: true, data: samples.map((item) => item.cpu?.temperatureC ?? null) }
  ];
  for (const index of gpuIndexes) {
    series.push({
      name: `GPU ${index} 使用率`,
      type: 'line',
      smooth: true,
      data: samples.map((item) => (item.gpus || []).find((gpu: any) => gpu.index === index)?.utilizationPercent ?? null)
    });
    series.push({
      name: `GPU ${index} 温度`,
      type: 'line',
      smooth: true,
      data: samples.map((item) => (item.gpus || []).find((gpu: any) => gpu.index === index)?.temperatureC ?? null)
    });
    series.push({
      name: `GPU ${index} 显存`,
      type: 'line',
      smooth: true,
      data: samples.map((item) => {
        const gpu = (item.gpus || []).find((g: any) => g.index === index);
        if (typeof gpu?.memoryUsedMb !== 'number' || typeof gpu?.memoryTotalMb !== 'number' || gpu.memoryTotalMb <= 0) return null;
        return Math.round((gpu.memoryUsedMb / gpu.memoryTotalMb) * 100);
      })
    });
  }
  return {
    tooltip: { trigger: 'axis' },
    legend: { type: 'scroll', top: 0 },
    grid: { left: 44, right: 24, top: 56, bottom: 36 },
    xAxis: { type: 'category', data: times },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}%' } },
    series
  };
}

function buildOverviewTrafficChart(items: BaseURLMonitoringItem[]) {
  const history = aggregateOverviewHistory(items);
  return {
    tooltip: { trigger: 'axis' },
    legend: { top: 0 },
    grid: { left: 44, right: 18, top: 48, bottom: 34 },
    xAxis: { type: 'category', data: history.map((bucket) => shortTime(bucket.windowStart)) },
    yAxis: { type: 'value', name: '次数' },
    series: [
      {
        name: '请求数',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: history.map((bucket) => bucket.requests),
        lineStyle: { color: '#2563eb', width: 2 },
        areaStyle: { color: 'rgba(37, 99, 235, 0.1)' }
      },
      {
        name: '错误数',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: history.map((bucket) => bucket.errors),
        lineStyle: { color: '#dc2626', width: 2 }
      }
    ]
  };
}

function buildOverviewTokenChart(items: BaseURLMonitoringItem[]) {
  const history = aggregateOverviewHistory(items);
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 54, right: 18, top: 30, bottom: 34 },
    xAxis: { type: 'category', data: history.map((bucket) => shortTime(bucket.windowStart)) },
    yAxis: { type: 'value', name: 'Token' },
    series: [
      {
        name: 'Token',
        type: 'bar',
        data: history.map((bucket) => bucket.totalTokens),
        itemStyle: { color: '#16a34a' }
      }
    ]
  };
}

function aggregateOverviewHistory(items: BaseURLMonitoringItem[]) {
  const buckets = new Map<string, { windowStart: string; requests: number; totalTokens: number; errors: number }>();
  for (const item of items) {
    for (const bucket of item.history || []) {
      const current = buckets.get(bucket.windowStart) || { windowStart: bucket.windowStart, requests: 0, totalTokens: 0, errors: 0 };
      current.requests += bucket.requests || 0;
      current.totalTokens += bucket.totalTokens || 0;
      current.errors += bucket.errors || 0;
      buckets.set(bucket.windowStart, current);
    }
  }
  return Array.from(buckets.values()).sort((a, b) => new Date(a.windowStart).getTime() - new Date(b.windowStart).getTime());
}

function buildEntranceMiniChart(item: BaseURLMonitoringItem) {
  const history = item.history || [];
  const times = history.map((bucket) => shortTime(bucket.windowStart));
  return {
    tooltip: { trigger: 'axis' },
    axisPointer: { link: [{ xAxisIndex: [0, 1] }] },
    title: [
      { text: '请求/错误', left: 0, top: 0, textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 500 } },
      { text: 'Token', left: 0, top: 82, textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 500 } }
    ],
    grid: [
      { left: 30, right: 8, top: 20, height: 48 },
      { left: 30, right: 8, top: 104, height: 36 }
    ],
    xAxis: [
      { type: 'category', gridIndex: 0, data: times, axisLabel: { show: false }, axisTick: { show: false } },
      { type: 'category', gridIndex: 1, data: times, axisLabel: { show: false }, axisTick: { show: false } }
    ],
    yAxis: [
      { type: 'value', gridIndex: 0, axisLabel: { show: false }, splitLine: { show: true } },
      { type: 'value', gridIndex: 1, axisLabel: { show: false }, splitLine: { show: true } }
    ],
    series: [
      {
        name: '请求数',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: true,
        showSymbol: false,
        data: history.map((bucket) => bucket.requests || 0),
        lineStyle: { color: '#2563eb', width: 2 },
        areaStyle: { color: 'rgba(37, 99, 235, 0.12)' }
      },
      {
        name: '错误数',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: true,
        showSymbol: false,
        data: history.map((bucket) => bucket.errors || 0),
        lineStyle: { color: '#dc2626', width: 1.5 }
      },
      {
        name: 'Token',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: history.map((bucket) => bucket.totalTokens || 0),
        itemStyle: { color: '#16a34a' }
      }
    ]
  };
}

function buildEntranceDetailChart(item?: BaseURLMonitoringItem) {
  const history = item?.history || [];
  const times = history.map((bucket) => shortTime(bucket.windowStart));
  return {
    tooltip: { trigger: 'axis' },
    axisPointer: { link: [{ xAxisIndex: [0, 1] }] },
    legend: { top: 0 },
    title: [
      { text: '请求/错误趋势', left: 0, top: 38, textStyle: { color: '#374151', fontSize: 12, fontWeight: 600 } },
      { text: 'Token 消耗趋势', left: 0, top: 228, textStyle: { color: '#374151', fontSize: 12, fontWeight: 600 } }
    ],
    grid: [
      { left: 54, right: 24, top: 72, height: 126 },
      { left: 54, right: 24, top: 262, height: 96 }
    ],
    xAxis: [
      { type: 'category', gridIndex: 0, data: times, axisLabel: { show: false }, axisTick: { show: false } },
      { type: 'category', gridIndex: 1, data: times }
    ],
    yAxis: [
      { type: 'value', gridIndex: 0, name: '次数' },
      { type: 'value', gridIndex: 1, name: 'Token' }
    ],
    series: [
      {
        name: '请求数',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: true,
        data: history.map((bucket) => bucket.requests || 0),
        lineStyle: { color: '#2563eb' }
      },
      {
        name: '错误数',
        type: 'line',
        xAxisIndex: 0,
        yAxisIndex: 0,
        smooth: true,
        data: history.map((bucket) => bucket.errors || 0),
        lineStyle: { color: '#dc2626' }
      },
      {
        name: 'Token',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: history.map((bucket) => bucket.totalTokens || 0),
        itemStyle: { color: '#16a34a' }
      }
    ]
  };
}

function shortTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatCompactNumber(value?: number) {
  return new Intl.NumberFormat('zh-CN', { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);
}

function formatDuration(ms: number) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${restSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return `${hours}h ${restMinutes}m`;
}

function parseStressLevels(raw: string) {
  const seen = new Set<number>();
  return String(raw || '')
    .split(/[,\s]+/)
    .map((item) => Number(item.trim()))
    .filter((value) => {
      if (!Number.isFinite(value) || value <= 0 || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

function isActiveStressRun(run: StressTestRun) {
  return run.status === 'queued' || run.status === 'running';
}

function stressStatusColor(status?: string) {
  if (status === 'completed') return 'green';
  if (status === 'running') return 'blue';
  if (status === 'queued') return 'cyan';
  if (status === 'cancelled') return 'grey';
  if (status === 'failed') return 'red';
  return 'grey';
}

function stressStatusLabel(status?: string) {
  if (status === 'completed') return '完成';
  if (status === 'running') return '运行中';
  if (status === 'queued') return '排队中';
  if (status === 'cancelled') return '已取消';
  if (status === 'failed') return '失败';
  if (status === 'pending') return '待执行';
  return status || '-';
}

function stressTargetText(run: StressTestRun) {
  const prefix = run.target.type === 'base_url' ? '入口' : '上游';
  return `${prefix} · ${run.target.name || run.target.slug || run.target.id}`;
}

function stressTotalRequests(run: StressTestRun) {
  return run.levels.reduce((sum, level) => sum + (level.metrics?.requestCount || 0), 0);
}

function stressTotalSuccess(run: StressTestRun) {
  return run.levels.reduce((sum, level) => sum + (level.metrics?.successCount || 0), 0);
}

function stressSuccessRate(run: StressTestRun) {
  const total = stressTotalRequests(run);
  if (!total) return '-';
  return `${((stressTotalSuccess(run) * 100) / total).toFixed(1)}%`;
}

function stressLevelSuccessRate(level: { metrics: { requestCount: number; successCount: number } }) {
  if (!level.metrics.requestCount) return '-';
  return `${((level.metrics.successCount * 100) / level.metrics.requestCount).toFixed(1)}%`;
}

function stressStatusCountsText(counts?: Record<string, number>) {
  const entries = Object.entries(counts || {});
  if (entries.length === 0) return '-';
  return entries.sort(([a], [b]) => a.localeCompare(b)).map(([code, count]) => `${code}:${count}`).join(' ');
}

function formatLatency(value?: number) {
  if (!value) return '0 ms';
  return `${Math.round(value)} ms`;
}

function formatMilliseconds(value?: number) {
  if (value === undefined || value === null) return '-';
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(2)}s`;
}

function formatRPS(value?: number) {
  if (!value) return '0.00';
  return value.toFixed(value >= 100 ? 1 : 2);
}

function formatDateTime(value?: string) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function buildStressResultChart(run?: StressTestRun) {
  const levels = run?.levels || [];
  return {
    tooltip: { trigger: 'axis' },
    legend: { top: 0 },
    grid: { left: 52, right: 48, top: 48, bottom: 36 },
    xAxis: { type: 'category', data: levels.map((level) => String(level.concurrency)) },
    yAxis: [
      { type: 'value', name: 'ms' },
      { type: 'value', name: 'RPS' }
    ],
    series: [
      { name: 'P95 延迟', type: 'line', smooth: true, data: levels.map((level) => level.metrics?.p95LatencyMs || 0), lineStyle: { color: '#dc2626' } },
      { name: '平均延迟', type: 'line', smooth: true, data: levels.map((level) => level.metrics?.avgLatencyMs || 0), lineStyle: { color: '#2563eb' } },
      { name: '吞吐 RPS', type: 'bar', yAxisIndex: 1, data: levels.map((level) => level.metrics?.requestsPerSecond || 0), itemStyle: { color: '#16a34a' } }
    ]
  };
}

function rangeToMs(range: string) {
  if (range === '24h') return 24 * 60 * 60 * 1000;
  if (range === '6h') return 6 * 60 * 60 * 1000;
  return 60 * 60 * 1000;
}

function shortUUID(uuid: string) {
  return uuid.length > 12 ? `${uuid.slice(0, 8)}...${uuid.slice(-4)}` : uuid;
}

function fmtPercent(v?: number) {
  return typeof v === 'number' ? `${v.toFixed(1)}%` : '-';
}

function fmtTemp(v?: number) {
  return typeof v === 'number' ? `${v.toFixed(1)}℃` : '-';
}

function fmtMemory(used?: number, total?: number) {
  if (typeof used !== 'number' || typeof total !== 'number' || total <= 0) return '-';
  return `${Math.round(used)}/${Math.round(total)} MB`;
}

function PageTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="page-title">
      <Title heading={3}>{title}</Title>
      <Text type="secondary">{subtitle}</Text>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="metric">
      <Text type="secondary">{label}</Text>
      <div className="metric-value">{value}</div>
    </Card>
  );
}
